// src/pages/POS.js

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
} from '@mui/material';

import {
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  PendingActions as PendingActionsIcon,
  PointOfSale as PointOfSaleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  Storage as StorageIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';

import api from '../api/api';
import './css/POS.css';

import ImportDocToPOSModal from './modals/ImportDocToPOSModal';
import NavbarPos from './components/NavbarPos';
import PosRightSidebar from './components/PosRightSidebar';
import ProductSearchModal from './modals/ProductSearchModal';
import CustomerModal from './modals/CustomerModal';
import PaymentModal from './modals/PaymentModal';
import CajaModal from './modals/CajaModal';
import ModalConsultorPrecios from './components/ModalConsultorPrecios';
import ModalMovimientosCaja from './modals/ModalMovimientosCaja';
import PinConfirmModal from './modals/PinConfirmModal';
import POS_VentasPendientes from './modals/POS_VentasPendientes';
import POS_VentasContingencia from './modals/POS_VentasContingencia';

/* ======================================================================
 * BACKEND / POS - MÉTODOS DE PAGO
 * ==================================================================== */
const PMT = Object.freeze({
  EFECTIVO: 'EFECTIVO',
  TARJETA_DEBITO: 'TARJETA_DEBITO',
  TARJETA_CREDITO: 'TARJETA_CREDITO',
  CREDITO: 'CREDITO',
});

function normalizePosPaymentMethodKey(raw) {
  return String(raw || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function metodoPagoTexto(tipoMetodoPago) {
  const k = normalizePosPaymentMethodKey(tipoMetodoPago);

  if (k === PMT.EFECTIVO) return 'Efectivo';
  if (k === PMT.TARJETA_DEBITO) return 'Tarjeta Débito';
  if (k === PMT.TARJETA_CREDITO) return 'Tarjeta Crédito';
  if (k === PMT.CREDITO) return 'Crédito';

  return null;
}

/**
 * Regla de redondeo para el pago final en efectivo en CLP.
 * 1..5 baja a la decena inferior; 6..9 sube a la superior.
 * No modifica el total tributario de la venta/DTE.
 */
function roundCashPaymentCLP(value) {
  const amount = Math.round(Number(value) || 0);
  const sign = amount < 0 ? -1 : 1;
  const abs = Math.abs(amount);
  const remainder = abs % 10;

  if (remainder === 0) return amount;

  const roundedAbs = remainder <= 5
    ? abs - remainder
    : abs + (10 - remainder);

  return sign * roundedAbs;
}

function getCashRoundingSummary(value) {
  const fiscalTotal = Math.round(Number(value) || 0);
  const cashTotal = roundCashPaymentCLP(fiscalTotal);

  return {
    fiscalTotal,
    cashTotal,
    rounding: cashTotal - fiscalTotal,
  };
}

function findCashPaymentMethod(methods = []) {
  return (
    methods.find((method) => isCashMethod(method)) ||
    methods.find(
      (method) =>
        normalizePosPaymentMethodKey(method?.key || method?.clave || method?.rawKey) === 'EFECTIVO'
    ) ||
    null
  );
}

function parseBoolish(value, defaultValue = false) {
  if (value === undefined || value === null || value === '') return defaultValue;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;

  const normalized = String(value).trim().toLowerCase();

  if (['true', '1', 'si', 'sí', 'yes', 'y', 'on'].includes(normalized)) return true;
  if (['false', '0', 'no', 'off', 'n'].includes(normalized)) return false;

  return defaultValue;
}

function hasValidNumericId(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0;
}

function getPaymentSelectionValue(method) {
  if (!method) return '';

  if (hasValidNumericId(method.relationId)) return String(Number(method.relationId));
  if (hasValidNumericId(method.id)) return String(Number(method.id));
  if (hasValidNumericId(method.methodId)) return String(Number(method.methodId));

  return String(method.key || '');
}

function normalizePaymentMethodRow(row, index = 0) {
  const key = normalizePosPaymentMethodKey(
    row?.key ??
      row?.metodopago_clave ??
      row?.metodo_pago_clave ??
      row?.clave ??
      row?.codigo ??
      row?.nombre ??
      row?.label ??
      row?.value
  );

  if (!key) return null;

  const relationId =
    row?.relationId ??
    row?.relation_id ??
    row?.relacion_id ??
    row?.payment_relation_id ??
    row?.payment_method_relation_id ??
    row?.configuracion_metodo_condicion_id ??
    row?.relacionId ??
    null;

  const methodId =
    row?.methodId ??
    row?.method_id ??
    row?.metodopago_id ??
    row?.metodo_pago_id ??
    row?.payment_method_id ??
    row?.metodoPagoId ??
    row?.id ??
    null;

  const conditionId =
    row?.conditionId ??
    row?.condition_id ??
    row?.condicionpago_id ??
    row?.condicion_pago_id ??
    row?.payment_condition_id ??
    null;

  const normalized = {
    id: relationId ?? methodId ?? row?.id ?? null,
    relationId: relationId ?? null,
    methodId: methodId ?? null,
    conditionId: conditionId ?? null,

    key,
    rawKey: key,
    clave:
      row?.metodopago_clave ??
      row?.metodo_pago_clave ??
      row?.clave ??
      key,

    label:
      row?.label ??
      row?.metodopago_nombre ??
      row?.metodo_pago_valor ??
      row?.metodo_pago_nombre ??
      row?.valor ??
      row?.nombre ??
      row?.clave ??
      metodoPagoTexto(key) ??
      key,

    description:
      row?.description ??
      row?.metodopago_descripcion ??
      row?.metodo_pago_descripcion ??
      row?.descripcion ??
      '',

    tipo:
      row?.metodopago_tipo ??
      row?.metodo_pago_tipo ??
      row?.tipo ??
      'OTRO',

    proveedor:
      row?.metodopago_proveedor ??
      row?.metodo_pago_proveedor ??
      row?.proveedor ??
      null,

    conditionKey:
      row?.conditionKey ??
      row?.condition_key ??
      row?.condicionpago_clave ??
      row?.condicion_pago_clave ??
      row?.payment_condition_key ??
      null,

    conditionLabel:
      row?.conditionLabel ??
      row?.condition_label ??
      row?.condicionpago_nombre ??
      row?.condicion_pago_valor ??
      row?.condicion_pago_nombre ??
      row?.payment_condition_label ??
      null,

    activo: parseBoolish(row?.activo, true),
    permitido: parseBoolish(row?.permitido, true),
    visiblePos: parseBoolish(row?.visiblePos ?? row?.visible_pos, false),
    configuracionCompleta: parseBoolish(
      row?.configuracionCompleta ?? row?.configuracion_completa,
      true
    ),

    requiereReferencia: parseBoolish(
      row?.requiereReferencia ?? row?.requiere_referencia,
      false
    ),

    requiereConfirmacion: parseBoolish(
      row?.requiereConfirmacion ?? row?.requiere_confirmacion,
      false
    ),

    requiereLiquidacion: parseBoolish(
      row?.requiereLiquidacion ?? row?.requiere_liquidacion,
      false
    ),

    permiteVuelto: parseBoolish(row?.permiteVuelto ?? row?.permite_vuelto, false),
    usaCuentaBancaria: parseBoolish(row?.usaCuentaBancaria ?? row?.usa_cuenta_bancaria, false),
    usaTerminal: parseBoolish(row?.usaTerminal ?? row?.usa_terminal, false),
    usaPasarela: parseBoolish(row?.usaPasarela ?? row?.usa_pasarela, false),

    cuentaId: row?.cuenta_id != null ? Number(row.cuenta_id) : null,
    cuentaNombre: row?.cuenta_nombre ?? null,
    cuentaTipo: row?.cuenta_tipo ?? null,
    cuentaMoneda: row?.cuenta_moneda ?? null,

    ordenPos: Number.isFinite(Number(row?.ordenPos ?? row?.orden_pos ?? row?.orden))
      ? Number(row.ordenPos ?? row.orden_pos ?? row.orden)
      : index + 1,

    raw: row,
    selectionValue: '',
  };

  normalized.selectionValue = getPaymentSelectionValue(normalized);

  return normalized;
}

function getAvailablePosPaymentMethods(rows = []) {
  const normalized = (Array.isArray(rows) ? rows : [])
    .map((row, index) => normalizePaymentMethodRow(row, index))
    .filter(Boolean)
    .filter((m) => m.activo && m.permitido && m.visiblePos && m.configuracionCompleta)
    .sort((a, b) => {
      if (a.ordenPos !== b.ordenPos) return a.ordenPos - b.ordenPos;

      const byLabel = String(a.label || '').localeCompare(String(b.label || ''), 'es');
      if (byLabel !== 0) return byLabel;

      return String(a.conditionLabel || '').localeCompare(String(b.conditionLabel || ''), 'es');
    });

  const seen = new Set();

  return normalized.filter((item) => {
    const dedupeKey =
      item.selectionValue ||
      String(item.relationId ?? item.id ?? item.methodId ?? item.key);

    if (seen.has(dedupeKey)) return false;

    seen.add(dedupeKey);
    return true;
  });
}

function findSelectedPaymentMethod(methods, selectedValue) {
  const selected = String(selectedValue || '').trim();

  if (!selected) return null;

  return (
    methods.find((m) => m.selectionValue === selected) ||
    methods.find((m) => String(m.relationId || '') === selected) ||
    methods.find((m) => String(m.id || '') === selected) ||
    methods.find((m) => String(m.methodId || '') === selected) ||
    methods.find((m) => m.key === normalizePosPaymentMethodKey(selected)) ||
    null
  );
}

function isCashMethod(methodOrKey) {
  if (!methodOrKey) return false;

  if (typeof methodOrKey === 'object') {
    if (methodOrKey.permiteVuelto) return true;
    return normalizePosPaymentMethodKey(methodOrKey.clave ?? methodOrKey.key) === PMT.EFECTIVO;
  }

  return normalizePosPaymentMethodKey(methodOrKey) === PMT.EFECTIVO;
}

function isCreditMethod(methodOrKey) {
  if (!methodOrKey) return false;

  if (typeof methodOrKey === 'object') {
    const k = normalizePosPaymentMethodKey(methodOrKey.clave ?? methodOrKey.key);
    return k === PMT.CREDITO;
  }

  return normalizePosPaymentMethodKey(methodOrKey) === PMT.CREDITO;
}

function requiresVoucherMethod(methodOrKey) {
  if (!methodOrKey) return false;

  if (typeof methodOrKey === 'object') {
    return !!methodOrKey.requiereReferencia;
  }

  return false;
}

function numberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function roundCLP(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

function roundDecimals(value, decimals = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;

  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

function getItemTaxPercentForPayload(item = {}) {
  if (Array.isArray(item.taxes) && item.taxes.length > 0) {
    const pct = Number(item.taxes[0]?.porcentaje ?? item.taxes[0]?.pct ?? 0);
    return Number.isFinite(pct) ? pct : 0;
  }

  const tax = Number(item.tax ?? item.iva ?? item.total_iva ?? 0);
  const net = Number(item.net ?? item.neto ?? item.total_neto ?? 0);

  if (Number.isFinite(tax) && Number.isFinite(net) && net > 0) {
    return roundDecimals((tax / net) * 100, 2);
  }

  return 0;
}

function getMainTaxPercentFromItems(items = []) {
  const rows = Array.isArray(items) ? items : [];

  const found = rows
    .map((item) => getItemTaxPercentForPayload(item))
    .find((pct) => Number.isFinite(Number(pct)) && Number(pct) > 0);

  return found != null ? Number(found) : 0;
}

function splitGrossCLP(grossAmount, taxPct = 19) {
  const gross = roundCLP(grossAmount);
  const pct = Number(taxPct || 0);

  if (!Number.isFinite(pct) || pct <= 0) {
    return {
      gross,
      net: gross,
      tax: 0,
      pct: 0,
    };
  }

  const net = roundCLP(gross / (1 + pct / 100));
  const tax = gross - net;

  return {
    gross,
    net,
    tax,
    pct,
  };
}

function resolveImportedOrderOfficialTotal(importSource = null, pagoOrigen = null, fallbackTotal = 0) {
  const src = importSource && typeof importSource === 'object' ? importSource : {};
  const pago = pagoOrigen && typeof pagoOrigen === 'object' ? pagoOrigen : {};

  const candidates = [
    pago.total_pagado,
    pago.totalPagado,
    pago.monto_pagado,
    pago.montoPagado,
    pago.monto_confirmado,
    pago.montoConfirmado,
    pago.monto,
    pago.total,

    src.totalPayable,
    src.total_pagable,
    src.total_bruto,
    src.monto_total,
    src.total,
    src?.totales?.total,
    src?.totales?.total_bruto,
    src?.pedido_raw?.total,
    src?.pedido_raw?.total_bruto,

    fallbackTotal,
  ];

  for (const value of candidates) {
    const amount = roundCLP(value);

    if (amount > 0) {
      return amount;
    }
  }

  return 0;
}

function addMapAmount(map, key, amount) {
  if (!key) return;
  map.set(key, Number(map.get(key) || 0) + Number(amount || 0));
}

function collectPayloadRuleAdjustments(rulesSnapshot = null) {
  const byProductId = new Map();
  const bySku = new Map();
  const byBarcode = new Map();
  let globalAdjustment = 0;

  const ajustes = Array.isArray(rulesSnapshot?.ajustes) ? rulesSnapshot.ajustes : [];

  ajustes.forEach((ajuste) => {
    const detalle = Array.isArray(ajuste?.detalle) ? ajuste.detalle : [];

    if (!detalle.length) {
      globalAdjustment += roundCLP(ajuste?.monto || 0);
      return;
    }

    detalle.forEach((det) => {
      let amount = Number(det?.ajuste);

      if (!Number.isFinite(amount) || amount === 0) {
        const descuentoBruto = Number(det?.descuento_bruto || 0);
        amount = descuentoBruto > 0 ? -Math.round(descuentoBruto) : 0;
      }

      if (!amount) return;

      const productId = Number(det?.producto_id ?? det?.id_producto);
      const sku = normalizeRuleKey(det?.sku || '');
      const barcode = normalizeRuleKey(det?.codigo_barras || det?.barcode || '');

      if (Number.isFinite(productId) && productId > 0) {
        addMapAmount(byProductId, String(productId), amount);
      } else if (sku) {
        addMapAmount(bySku, sku, amount);
      } else if (barcode) {
        addMapAmount(byBarcode, barcode, amount);
      } else {
        globalAdjustment += amount;
      }
    });
  });

  return {
    byProductId,
    bySku,
    byBarcode,
    globalAdjustment,
  };
}

function applyGrossLineToPayloadRow(row, grossLine) {
  const newGross = Math.max(0, roundCLP(grossLine));
  const split = splitGrossCLP(newGross, row.impuesto_pct);
  const safeQty = row.cantidad > 0 ? row.cantidad : 1;

  return {
    ...row,
    precio_neto: roundDecimals(split.net / safeQty, 8),
    precio_bruto: roundDecimals(split.gross / safeQty, 8),
    precio_unitario_bruto: roundDecimals(split.gross / safeQty, 8),
    impuesto_monto: Number(split.tax),
    total_iva: Number(split.tax),
    total_neto: Number(split.net),
    total_bruto: Number(split.gross),
  };
}
function resolveItemGrossLineForPayload(item = {}) {
  const qty = Number(item.quantity ?? item.cantidad ?? 0);

  const directLineCandidates = [
    item.lineTotal,
    item.line_total,
    item.total_bruto,
    item.monto_total_bruto,
    item.totalFinalBruto,
    item.total_final_bruto,
    item.total,
  ];

  for (const value of directLineCandidates) {
    const n = Number(value);

    if (Number.isFinite(n) && n > 0) {
      return roundCLP(n);
    }
  }

  if (item.isWeight) {
    const weightLine = Number(
      item.gross ??
        item.precio_bruto ??
        item.price ??
        item.precio ??
        0
    );

    return Number.isFinite(weightLine) ? roundCLP(weightLine) : 0;
  }

  const grossUnit = Number(
    item.gross ??
      item.precio_bruto ??
      item.price ??
      item.precio ??
      0
  );

  if (Number.isFinite(grossUnit) && grossUnit > 0 && Number.isFinite(qty) && qty > 0) {
    return roundCLP(grossUnit * qty);
  }

  const net = Number(item.net ?? item.precio_neto ?? 0);
  const pct = getItemTaxPercentForPayload(item);

  if (Number.isFinite(net) && net > 0 && pct > 0 && Number.isFinite(qty) && qty > 0) {
    return roundCLP(net * (1 + pct / 100) * qty);
  }

  return 0;
}

function positiveIntOrNull(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function getPedidoItemIdForPayload(item = {}) {
  if (!item || typeof item !== 'object') return null;

  const sourceItemType = String(item.source_item_type || '').trim().toLowerCase();

  const candidates = [
    item.pedido_item_id,
    item.pedidoItemId,
    item.id_pedido_item,
    item.id_item_pedido,
    item.linea_pedido_id,
    item.id_linea_pedido,

    sourceItemType === 'pedido_item' ? item.source_item_id : null,

    item.item_raw?.pedido_item_id,
    item.item_raw?.pedidoItemId,
    item.item_raw?.id_pedido_item,
    item.item_raw?.id_item_pedido,
    item.item_raw?.linea_pedido_id,
    item.item_raw?.id_linea_pedido,

    item.raw?.pedido_item_id,
    item.raw?.pedidoItemId,
    item.raw?.id_pedido_item,
    item.raw?.id_item_pedido,
    item.raw?.linea_pedido_id,
    item.raw?.id_linea_pedido,
  ];

  /*
   * Caso común en items_para_pos:
   * raw.id puede ser el ID de la línea del pedido.
   * Pero NO usamos item.id directo porque en el carrito item.id normalmente
   * es el ID del producto.
   */
  const rawLooksLikePedidoItem =
    item.raw &&
    typeof item.raw === 'object' &&
    (
      item.raw.pedido_id != null ||
      item.raw.id_pedido != null ||
      item.raw.cantidad_pendiente != null ||
      item.raw.cantidad_facturada != null ||
      item.raw.cantidad_original != null ||
      item.raw.estado_facturacion != null
    );

  if (rawLooksLikePedidoItem) {
    candidates.push(item.raw.id);
  }

  const itemRawLooksLikePedidoItem =
    item.item_raw &&
    typeof item.item_raw === 'object' &&
    (
      item.item_raw.pedido_id != null ||
      item.item_raw.id_pedido != null ||
      item.item_raw.cantidad_pendiente != null ||
      item.item_raw.cantidad_facturada != null ||
      item.item_raw.cantidad_original != null ||
      item.item_raw.estado_facturacion != null
    );

  if (itemRawLooksLikePedidoItem) {
    candidates.push(item.item_raw.id);
  }

  for (const value of candidates) {
    const id = positiveIntOrNull(value);
    if (id) return id;
  }

  return null;
}

function getAutomotrizItemIdForPayload(item = {}) {
  if (!item || typeof item !== 'object') return null;

  const candidates = [
    item.mantencion_item_id,
    item.automotriz_item_id,
    item.automotrizItemId,
    item.item_raw?.mantencion_item_id,
    item.item_raw?.automotriz_item_id,
    item.raw?.mantencion_item_id,
    item.raw?.automotriz_item_id,
  ];

  for (const value of candidates) {
    const parsed = positiveIntOrNull(value);
    if (parsed) return parsed;
  }

  return null;
}

function getAutomotrizBodegaIdForPayload(item = {}) {
  return positiveIntOrNull(
    item.bodega_id ??
      item.id_bodega ??
      item.item_raw?.bodega_id ??
      item.item_raw?.id_bodega ??
      item.raw?.bodega_id ??
      item.raw?.id_bodega
  );
}

function isAutomotrizMaintenanceCartItem(item = {}) {
  return Boolean(
    getAutomotrizItemIdForPayload(item) ||
    String(item.tipo_origen || item.source_type || '').toLowerCase() === 'automotriz_mantencion'
  );
}

function getPedidoIdForPayload(item = {}) {
  if (!item || typeof item !== 'object') return null;

  const candidates = [
    item.pedido_id,
    item.pedidoId,
    item.id_pedido,

    item.item_raw?.pedido_id,
    item.item_raw?.pedidoId,
    item.item_raw?.id_pedido,

    item.raw?.pedido_id,
    item.raw?.pedidoId,
    item.raw?.id_pedido,
  ];

  for (const value of candidates) {
    const id = positiveIntOrNull(value);
    if (id) return id;
  }

  return null;
}

function buildSaleItemsPayload(items = [], {
  forceTotalGross = null,
  forceTotalGrossTolerance = 1,
  rulesSnapshot = null,
} = {}) {
  let rows = (Array.isArray(items) ? items : [])
    .map((it) => {
      const qty = Number(it.quantity ?? it.cantidad ?? 0);
      const safeQty = qty > 0 ? qty : 1;

      const pct = getItemTaxPercentForPayload(it);
      const grossLine = resolveItemGrossLineForPayload(it);
      const split = splitGrossCLP(grossLine, pct);

      const netUnit = split.net / safeQty;
      const grossUnit = split.gross / safeQty;

      const pedidoItemId = getPedidoItemIdForPayload(it);
      const pedidoId = getPedidoIdForPayload(it);

      const resolvedProductId = positiveIntOrNull(
        it.product_id ??
          it.id_producto ??
          it.producto_id ??
          it.productId ??
          it.producto?.id ??
          it.producto_raw?.id
      );

      const mantencionItemId = getAutomotrizItemIdForPayload(it);
      const esItemMantencion = isAutomotrizMaintenanceCartItem(it);

      return {
        id_producto: resolvedProductId,

        cantidad: Number(qty),

        /*
         * Muy importante:
         * NO usar it.id como pedido_item_id.
         * En el carrito, it.id normalmente es id_producto.
         */
        pedido_item_id: pedidoItemId,
        source_item_id: pedidoItemId,
        source_item_type: pedidoItemId ? 'pedido_item' : null,

        pedido_id: pedidoId,

        mantencion_item_id: mantencionItemId,
        automotriz_item_id: mantencionItemId,
        afecta_inventario:
          esItemMantencion
            ? parseBoolish(it.afecta_inventario ?? it.item_raw?.afecta_inventario ?? it.raw?.afecta_inventario, false)
            : true,
        bodega_id: getAutomotrizBodegaIdForPayload(it),
        tipo_item:
          it.tipo_item ||
          it.item_raw?.tipo_item ||
          it.raw?.tipo_item ||
          null,
        reserva_id: positiveIntOrNull(
          it.reserva_id ?? it.item_raw?.reserva_id ?? it.raw?.reserva_id
        ),
        descripcion:
          it.descripcion_linea ||
          it.nombre_linea ||
          it.descripcion ||
          it.name ||
          it.nombre ||
          it.item_raw?.descripcion ||
          it.raw?.descripcion ||
          null,

        precio_neto: roundDecimals(netUnit, 8),
        precio_bruto: roundDecimals(grossUnit, 8),
        precio_unitario_bruto: roundDecimals(grossUnit, 8),

        permite_precio_variable: parseBoolish(
          it.permite_precio_variable ?? it.raw?.permite_precio_variable,
          false
        ),
        precio_variable_confirmado: parseBoolish(
          it.precio_variable_confirmado ?? it.variablePriceConfirmed,
          false
        ),
        precio_variable_bruto: Number(
          it.precio_variable_bruto ?? it.variablePriceGross ?? 0
        ) || null,
        precio_variable_nombre_base:
          it.precio_variable_nombre_base ?? it.variablePriceBaseName ?? null,
        precio_variable_orden: Number(
          it.precio_variable_orden ?? it.variablePriceOrder ?? 0
        ) || null,

        impuesto_pct: Number(pct),
        impuesto_monto: Number(split.tax),
        total_iva: Number(split.tax),
        total_neto: Number(split.net),
        total_bruto: Number(split.gross),

        sku: it.sku || '',
        codigo_barras:
          it.barcode ||
          it.codigo_barras ||
          it.codigo_barra ||
          '',

        item_raw: it.item_raw || it.raw || null,
      };
    })
    .filter(
      (row) =>
        row.cantidad > 0 &&
        (positiveIntOrNull(row.id_producto) || positiveIntOrNull(row.mantencion_item_id))
    );

  const collected = collectPayloadRuleAdjustments(rulesSnapshot);

  if (rows.length > 0) {
    rows = rows.map((row) => {
      const productKey = String(row.id_producto);
      const skuKey = normalizeRuleKey(row.sku || '');
      const barcodeKey = normalizeRuleKey(row.codigo_barras || '');

      const adjustment =
        Number(collected.byProductId.get(productKey) || 0) +
        Number(collected.bySku.get(skuKey) || 0) +
        Number(collected.byBarcode.get(barcodeKey) || 0);

      if (!adjustment) return row;

      return applyGrossLineToPayloadRow(
        row,
        Number(row.total_bruto || 0) + adjustment
      );
    });

    if (collected.globalAdjustment !== 0) {
      const index = rows.length - 1;

      rows[index] = applyGrossLineToPayloadRow(
        rows[index],
        Number(rows[index].total_bruto || 0) + collected.globalAdjustment
      );
    }
  }

  const forced = forceTotalGross != null ? roundCLP(forceTotalGross) : null;

  if (forced != null && rows.length > 0) {
    const current = rows.reduce((sum, row) => sum + roundCLP(row.total_bruto), 0);
    const diff = forced - current;

    /*
     * Seguridad crítica:
     * Solo permitimos ajustar diferencias mínimas de redondeo.
     * Nunca volver a meter una diferencia grande en la última línea.
     */
    const tolerance = Math.max(0, Number(forceTotalGrossTolerance ?? 1));

    if (diff !== 0 && Math.abs(diff) <= tolerance) {
      const index = rows.length - 1;

      rows[index] = applyGrossLineToPayloadRow(
        rows[index],
        Number(rows[index].total_bruto || 0) + diff
      );
    } else if (diff !== 0) {
      console.warn('[POS] forceTotalGross ignorado por diferencia peligrosa:', {
        forced,
        current,
        diff,
        tolerance,
      });
    }
  }

  return rows;
}

function sumSaleItemsPayloadTotals(itemsPayload = []) {
  return (Array.isArray(itemsPayload) ? itemsPayload : []).reduce(
    (acc, item) => {
      acc.net += roundCLP(item.total_neto);
      acc.tax += roundCLP(item.total_iva);
      acc.gross += roundCLP(item.total_bruto);
      return acc;
    },
    {
      net: 0,
      tax: 0,
      gross: 0,
    }
  );
}

function normalizePagoOrigen(rawPago = null, totalDocumento = 0) {
  const pago = rawPago && typeof rawPago === 'object' ? rawPago : {};
  const totalBase = numberOrZero(totalDocumento);

  const totalPagado = numberOrZero(
    pago.total_pagado ??
      pago.totalPagado ??
      pago.pagado ??
      pago.monto_pagado ??
      pago.montoPagado ??
      pago.monto_confirmado ??
      pago.montoConfirmado ??
      pago.monto ??
      pago.total ??
      0
  );

  const saldo = numberOrZero(
    pago.saldo ??
      pago.saldo_pendiente ??
      pago.saldoPendiente ??
      (totalBase > 0 ? Math.max(totalBase - totalPagado, 0) : 0)
  );

  const codigoPago = String(
    pago.codigo ||
      pago.estado_pago ||
      pago.estadoPago ||
      pago.estado ||
      ''
  )
    .trim()
    .toUpperCase();

  const pagada =
    pago.pagada === true ||
    pago.paid === true ||
    codigoPago === 'PAGADO' ||
    codigoPago === 'PAID' ||
    codigoPago === 'PAGADA' ||
    (totalBase > 0 && totalPagado >= totalBase - 0.5 && saldo <= 0.5);

  const existe =
    pago.existe === true ||
    pago.exists === true ||
    totalPagado > 0 ||
    codigoPago === 'PAGADO' ||
    codigoPago === 'PAID' ||
    codigoPago === 'PAGADA';

  const movimientos = Array.isArray(pago.movimientos)
    ? pago.movimientos
    : Array.isArray(pago.pagos)
      ? pago.pagos
      : [];

  const firstMovimiento = movimientos[0] || {};

  return {
    existe,
    pagada,
    total_pagado: totalPagado,
    saldo,
    fuente: pago.fuente || pago.source || pago.fuente_pago || 'pedido',
    origen_tipo: pago.origen_tipo || pago.tipo || 'pedido',
    origen_id: pago.origen_id ?? pago.id ?? null,
    metodo_pago:
      pago.metodo_pago_nombre ||
      pago.metodo_pago_valor ||
      pago.metodo_pago ||
      pago.forma_pago ||
      pago.payment_method ||
      firstMovimiento.metodo_pago_nombre_ref ||
      firstMovimiento.metodo_pago_valor ||
      firstMovimiento.metodo_pago ||
      firstMovimiento.forma_pago ||
      null,
    metodo_pago_clave:
      pago.metodo_pago_clave ||
      pago.metodo_pago_key ||
      pago.forma_pago_clave ||
      pago.payment_method_type ||
      pago.metodo_pago ||
      pago.forma_pago ||
      firstMovimiento.metodo_pago_clave_ref ||
      firstMovimiento.metodo_pago_clave ||
      firstMovimiento.forma_pago ||
      null,
    referencia:
      pago.referencia ||
      pago.referencia_externa ||
      pago.comprobante ||
      pago.voucher_number ||
      firstMovimiento.referencia ||
      firstMovimiento.comprobante ||
      null,
    movimientos,
    raw: pago,
  };
}

function extractPagoOrigenFromPedidoResponse(data, header = null, totalDocumento = 0) {
  const h = header && typeof header === 'object' ? header : {};
  const totalBase = numberOrZero(
    totalDocumento ||
      data?.totales?.total ||
      data?.pedido_raw?.total ||
      data?.total ||
      h?.total ||
      h?.total_bruto ||
      0
  );

  const direct =
    data?.pago_origen ||
    data?.pagoOrigen ||
    data?.pagos_asociados ||
    data?.pagosAsociados ||
    h?.pago_origen ||
    h?.pagoOrigen ||
    null;

  if (direct) {
    return normalizePagoOrigen(direct, totalBase);
  }

  const estadoPago =
    data?.estados?.pago ||
    data?.resumen_operacional?.pago ||
    h?.estados?.pago ||
    h?.resumen_operacional?.pago ||
    null;

  if (estadoPago) {
    const movimientos = Array.isArray(estadoPago.movimientos)
      ? estadoPago.movimientos
      : [];

    return normalizePagoOrigen(
      {
        ...estadoPago,
        existe:
          estadoPago.existe === true ||
          String(estadoPago.codigo || '').toUpperCase() === 'PAGADO' ||
          numberOrZero(estadoPago.pagado ?? estadoPago.total_pagado ?? estadoPago.total) > 0,
        pagada:
          estadoPago.pagada === true ||
          String(estadoPago.codigo || '').toUpperCase() === 'PAGADO',
        total_pagado:
          estadoPago.total_pagado ??
          estadoPago.pagado ??
          estadoPago.monto_pagado ??
          estadoPago.monto ??
          estadoPago.total ??
          0,
        saldo: estadoPago.saldo ?? 0,
        fuente: estadoPago.fuente || 'tesoreria',
        origen_tipo: 'pedido',
        origen_id: data?.id || h?.id || data?.pedido_raw?.id || null,
        metodo_pago:
          estadoPago.metodo_pago ||
          estadoPago.forma_pago ||
          movimientos[0]?.forma_pago ||
          data?.metodos?.pago ||
          data?.pedido_raw?.metodo_pago ||
          h?.metodo_pago ||
          null,
        metodo_pago_clave:
          estadoPago.metodo_pago_clave ||
          estadoPago.forma_pago ||
          movimientos[0]?.forma_pago ||
          data?.metodos?.pago ||
          data?.pedido_raw?.metodo_pago ||
          h?.metodo_pago ||
          null,
        referencia:
          estadoPago.referencia ||
          movimientos[0]?.referencia ||
          null,
        movimientos,
      },
      totalBase
    );
  }

  const pagos =
    data?.pagos_tesoreria ||
    h?.pagos_tesoreria ||
    data?.pagos ||
    h?.pagos ||
    data?.movimientos ||
    h?.movimientos ||
    [];

  if (Array.isArray(pagos) && pagos.length) {
    const pagosValidos = pagos.filter((p) => {
      const estado = String(p?.estado || p?.estado_confirmacion || '').toLowerCase();

      if (
        ['anulado', 'anulada', 'cancelado', 'cancelada', 'rechazado', 'rechazada'].includes(
          estado
        )
      ) {
        return false;
      }

      const tipo = String(p?.tipo || 'ingreso').toLowerCase();
      return !tipo || tipo === 'ingreso';
    });

    const totalPagado = pagosValidos.reduce(
      (sum, p) =>
        sum + numberOrZero(p?.monto ?? p?.monto_pagado ?? p?.monto_confirmado),
      0
    );

    return normalizePagoOrigen(
      {
        existe: totalPagado > 0,
        pagada: totalBase > 0 && totalPagado >= totalBase - 0.5,
        total_pagado: totalPagado,
        saldo: Math.max(totalBase - totalPagado, 0),
        fuente: 'tesoreria',
        origen_tipo: 'pedido',
        origen_id: data?.id || h?.id || data?.pedido_raw?.id || null,
        metodo_pago:
          pagosValidos[0]?.metodo_pago_nombre_ref ||
          pagosValidos[0]?.metodo_pago_valor ||
          pagosValidos[0]?.metodo_pago ||
          pagosValidos[0]?.forma_pago ||
          data?.metodos?.pago ||
          data?.pedido_raw?.metodo_pago ||
          h?.metodo_pago ||
          null,
        metodo_pago_clave:
          pagosValidos[0]?.metodo_pago_clave_ref ||
          pagosValidos[0]?.metodo_pago_clave ||
          pagosValidos[0]?.forma_pago ||
          data?.metodos?.pago ||
          data?.pedido_raw?.metodo_pago ||
          h?.metodo_pago ||
          null,
        referencia:
          pagosValidos[0]?.referencia ||
          pagosValidos[0]?.comprobante ||
          null,
        movimientos: pagosValidos,
      },
      totalBase
    );
  }

  const totalPagadoHeader = numberOrZero(
    h?.total_pagado ??
      h?.monto_pagado ??
      h?.monto_pagado_total ??
      data?.total_pagado ??
      data?.monto_pagado
  );

  const totalPedido = numberOrZero(
    h?.total_bruto ??
      h?.total ??
      h?.monto_total ??
      h?.totales?.total ??
      data?.total_bruto ??
      data?.total ??
      data?.monto_total ??
      data?.totales?.total ??
      data?.pedido_raw?.total ??
      totalBase
  );

  const saldoHeader =
    h?.saldo != null ||
    h?.saldo_pendiente != null ||
    data?.saldo != null ||
    data?.saldo_pendiente != null
      ? numberOrZero(h?.saldo ?? h?.saldo_pendiente ?? data?.saldo ?? data?.saldo_pendiente)
      : Math.max(totalPedido - totalPagadoHeader, 0);

  return normalizePagoOrigen(
    {
      existe: totalPagadoHeader > 0,
      pagada:
        h?.pagada === true ||
        data?.pagada === true ||
        (totalPedido > 0 && totalPagadoHeader >= totalPedido - 0.5 && saldoHeader <= 0.5),
      total_pagado: totalPagadoHeader,
      saldo: saldoHeader,
      fuente: 'pedido',
      metodo_pago:
        h?.metodo_pago ||
        data?.metodo_pago ||
        data?.metodos?.pago ||
        data?.pedido_raw?.metodo_pago ||
        null,
      metodo_pago_clave:
        h?.metodo_pago ||
        data?.metodo_pago ||
        data?.metodos?.pago ||
        data?.pedido_raw?.metodo_pago ||
        null,
    },
    totalPedido || totalBase
  );
}

function isImportSourcePedidoPendienteDte(importSource = null) {
  if (!importSource) return false;

  const type = String(importSource.type || '').trim().toLowerCase();
  const modo = String(importSource.modo_importacion || '').trim().toLowerCase();

  return (
    type === 'pedido_pendiente_dte' ||
    modo === 'pedido_pendiente_dte' ||
    importSource?.puede_emitir_pendientes === true ||
    importSource?.raw?.puede_emitir_pendientes === true ||
    importSource?.raw?.facturacion?.puede_emitir_pendientes === true ||
    importSource?.raw?.facturacion?.modo_importacion === 'pedido_pendiente_dte' ||
    importSource?.raw?.estado_facturacion === 'DTE_PARCIAL_PENDIENTE' ||
    importSource?.raw?.facturacion?.codigo === 'DTE_PARCIAL_PENDIENTE'
  );
}

function getSourceRefTypeForBackend(importSource = null) {
  if (!importSource) return null;

  const type = String(importSource.type || '').trim().toLowerCase();

  if (type === 'cotizacion') return 'cotizacion';
  if (type === 'automotriz_mantencion') return 'automotriz_mantencion';

  /*
   * pedido_pendiente_dte es un modo interno del frontend.
   * Para el backend, la fuente real sigue siendo ventas_pedidos.
   */
  if (
    type === 'pedido' ||
    type === 'pedido_pendiente_dte' ||
    isImportSourcePedidoPendienteDte(importSource)
  ) {
    return 'pedido';
  }

  return type || null;
}

function getPagoOrigenFromImportSource(importSource, totalPayable = 0) {
  if (!importSource) {
    return normalizePagoOrigen(null, totalPayable);
  }

  const raw =
    importSource.raw ||
    importSource.detalle ||
    importSource.pedido ||
    {};

  const rawPago =
    importSource.pago_origen ||
    importSource.pagoOrigen ||
    importSource.pago ||
    importSource.payment ||
    importSource.estado_pago ||
    importSource?.estados?.pago ||
    importSource?.resumen_operacional?.pago ||

    raw?.pago_origen ||
    raw?.pagoOrigen ||
    raw?.pago ||
    raw?.payment ||
    raw?.estado_pago ||
    raw?.estados?.pago ||
    raw?.resumen_operacional?.pago ||
    null;

  if (rawPago) {
    return normalizePagoOrigen(rawPago, totalPayable);
  }

  return normalizePagoOrigen(null, totalPayable);
}

function isImportedPaidOrder(importSource, totalPayable = 0) {
  if (!importSource) return false;

  const sourceRefType = getSourceRefTypeForBackend(importSource);

  if (sourceRefType !== 'pedido') return false;

  const total = numberOrZero(
    totalPayable ||
      importSource.totalPayable ||
      importSource.total_pagable ||
      importSource.total ||
      importSource.monto_total ||
      importSource?.totales?.total ||
      importSource?.pedido_raw?.total ||
      importSource?.raw?.totales?.total ||
      importSource?.raw?.pedido_raw?.total ||
      0
  );

  const pago = getPagoOrigenFromImportSource(importSource, total);

  if (!pago.existe) return false;
  if (pago.pagada === true) return true;

  const totalPagado = numberOrZero(pago.total_pagado);
  const saldo = numberOrZero(pago.saldo);

  return total > 0 && totalPagado >= total - 0.5 && saldo <= 0.5;
}

function getImportedPaymentLabel(importSource) {
  const total = numberOrZero(
    importSource?.totalPayable ||
      importSource?.total_pagable ||
      importSource?.total ||
      importSource?.monto_total ||
      importSource?.totales?.total ||
      importSource?.pedido_raw?.total ||
      0
  );

  const pago = getPagoOrigenFromImportSource(importSource, total);

  return pago.metodo_pago || pago.metodo_pago_clave || 'Pago heredado del pedido';
}


/* ======================================================================
 * FACTURA 33 - ENTREGA / TRASLADO (RES. EX. SII 144/2026)
 * ==================================================================== */
const INVOICE_DELIVERY_MODE = Object.freeze({
  NO_AMPARA: 'NO_AMPARA_TRASLADO',
  RETIRO_CLIENTE: 'RETIRO_CLIENTE',
  DESPACHO: 'DESPACHO',
});

const TRANSPORTER_TYPE = Object.freeze({
  COMPRADOR: 'COMPRADOR',
  EMISOR: 'EMISOR',
  TERCERO: 'TERCERO',
});

const DESTINATION_TYPE = Object.freeze({
  CLIENTE: 'DIRECCION_CLIENTE',
  OTRA: 'OTRA_DIRECCION',
});

function firstText(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

/**
 * Formatea un RUT chileno sólo para presentación/entrada del POS.
 * Acepta valores con o sin puntos/guion y conserva dígito verificador K.
 * Ej.: 780883294 -> 78.088.329-4
 */
function formatRutChile(value) {
  const clean = String(value ?? '')
    .toUpperCase()
    .replace(/[^0-9K]/g, '');

  if (!clean) return '';
  if (clean.length === 1) return clean;

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${formattedBody}-${dv}`;
}

function getLocalDateInputValue(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getLocalTimeInputValue(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function normalizeAddressSnapshot(address = null) {
  if (!address) {
    return { direccion: '', comuna: '', ciudad: '', fuente: '' };
  }

  if (typeof address === 'string') {
    return {
      direccion: firstText(address),
      comuna: '',
      ciudad: '',
      fuente: '',
    };
  }

  if (typeof address !== 'object') {
    return { direccion: '', comuna: '', ciudad: '', fuente: '' };
  }

  const calleNumero = [
    firstText(address.calle, address.street, address.via),
    firstText(address.numero, address.number, address.nro),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const complemento = firstText(
    address.complemento,
    address.depto,
    address.departamento,
    address.oficina,
    address.unit
  );

  const direccionBase = firstText(
    address.direccion,
    address.direccion_corta,
    address.address,
    calleNumero
  );

  return {
    direccion: [direccionBase, complemento].filter(Boolean).join(', '),
    comuna: firstText(
      address.comuna,
      address.comuna_nombre,
      address.nombre_comuna,
      address.municipality
    ),
    ciudad: firstText(
      address.ciudad,
      address.ciudad_nombre,
      address.nombre_ciudad,
      address.city
    ),
    fuente: firstText(address.tipo, address.type, address.fuente),
  };
}

function getCustomerInvoiceDestination(customer = null) {
  const row = customer && typeof customer === 'object' ? customer : {};
  const addresses = Array.isArray(row.direcciones) ? row.direcciones : [];

  const despachoFromList = addresses.find((address) => {
    const tipo = normalizeRuleText(address?.tipo || address?.type || '');
    return tipo.includes('DESPACH');
  });

  const facturacionFromList = addresses.find((address) => {
    const tipo = normalizeRuleText(address?.tipo || address?.type || '');
    return tipo.includes('FACTUR');
  });

  const candidates = [
    ['Dirección de despacho', row.direccion_despacho],
    ['Dirección de despacho', despachoFromList],
    ['Dirección de facturación', row.direccion_facturacion],
    ['Dirección de facturación', facturacionFromList],
    ['Dirección principal', row.direccion_principal],
    ['Dirección registrada', addresses[0]],
    ['Dirección del cliente', {
      direccion: firstText(row.direccion, row.direccion_comercial, row.address),
      calle: row.calle,
      numero: row.numero,
      complemento: row.complemento,
      comuna: firstText(row.comuna, row.comuna_nombre, row.nombre_comuna, row.municipality),
      ciudad: firstText(row.ciudad, row.ciudad_nombre, row.nombre_ciudad, row.city),
    }],
  ];

  for (const [sourceLabel, candidate] of candidates) {
    const normalized = normalizeAddressSnapshot(candidate);

    if (normalized.direccion || normalized.comuna || normalized.ciudad) {
      return {
        ...normalized,
        fuente: sourceLabel,
      };
    }
  }

  return {
    direccion: '',
    comuna: '',
    ciudad: '',
    fuente: '',
  };
}

function getInvoiceIssuerSnapshot(cajaData = null) {
  const row = cajaData && typeof cajaData === 'object' ? cajaData : {};
  const empresa =
    row.empresa ||
    row.emisor ||
    row.company ||
    row.caja?.empresa ||
    row.sucursal?.empresa ||
    {};

  return {
    rut: firstText(
      empresa.rut,
      empresa.rut_empresa,
      empresa.rut_emisor,
      row.rut_empresa,
      row.rut_emisor
    ),
    nombre: firstText(
      empresa.razon_social,
      empresa.nombre,
      empresa.nombre_fantasia,
      row.razon_social,
      row.empresa_nombre
    ),
  };
}

function getInvoiceOriginSnapshot(cajaData = null) {
  const row = cajaData && typeof cajaData === 'object' ? cajaData : {};
  const sucursal =
    row.sucursal ||
    row.branch ||
    row.caja?.sucursal ||
    row.punto_venta?.sucursal ||
    {};

  // Si el endpoint /pos_cajas/estado informa origen_efectivo,
  // tiene prioridad sobre la dirección administrativa de la sucursal.
  const origenEfectivo =
    row.origen_efectivo ||
    row.caja?.origen_efectivo ||
    row.data?.origen_efectivo ||
    null;

  const originAddressCandidates = [
    origenEfectivo,
    sucursal.direccion_objeto,
    sucursal.direccion_principal,
    sucursal.direccion,
    sucursal.address,
    row.sucursal_direccion_objeto,
    row.sucursal_direccion,
    row.direccion_sucursal,
    row.direccion,
  ];

  let normalizedAddress = { direccion: '', comuna: '', ciudad: '', fuente: '' };

  for (const candidate of originAddressCandidates) {
    const current = normalizeAddressSnapshot(candidate);
    if (current.direccion || current.comuna || current.ciudad) {
      normalizedAddress = current;
      break;
    }
  }

  return {
    nombre: firstText(
      typeof origenEfectivo === 'object' ? origenEfectivo?.nombre : '',
      sucursal.nombre,
      sucursal.nombre_sucursal,
      sucursal.descripcion,
      row.sucursal_nombre,
      row.nombre_sucursal,
      row.nombre_caja,
      row.nombre
    ),
    direccion: firstText(
      normalizedAddress.direccion,
      row.sucursal_direccion,
      row.direccion_sucursal,
      typeof row.direccion === 'string' ? row.direccion : ''
    ),
    comuna: firstText(
      normalizedAddress.comuna,
      sucursal.comuna,
      sucursal.comuna_nombre,
      row.sucursal_comuna,
      row.comuna_sucursal,
      row.comuna
    ),
    ciudad: firstText(
      normalizedAddress.ciudad,
      sucursal.ciudad,
      sucursal.ciudad_nombre,
      row.sucursal_ciudad,
      row.ciudad_sucursal,
      row.ciudad
    ),
  };
}

function createInvoiceTransferData({ cajaData = null, customer = null } = {}) {
  const origin = getInvoiceOriginSnapshot(cajaData);
  const destination = getCustomerInvoiceDestination(customer);

  return {
    origen_nombre: origin.nombre,
    origen_direccion: origin.direccion,
    origen_comuna: origin.comuna,

    destino_tipo: DESTINATION_TYPE.CLIENTE,
    destino_direccion: destination.direccion,
    destino_comuna: destination.comuna,
    destino_ciudad: destination.ciudad,
    destino_fuente: destination.fuente,

    transportista_tipo: '',
    transportista_rut: '',
    transportista_nombre: '',

    chofer_rut: '',
    chofer_nombre: '',

    patente_conocida: true,
    patente: '',
    patente_carro: '',

    fecha_salida: getLocalDateInputValue(),
    hora_salida: getLocalTimeInputValue(),

    traslado_prolongado: false,
    fecha_llegada_estimada: '',
    motivo_traslado_prolongado: '',
  };
}

function mergeInvoiceTransferDefaults(current = {}, defaults = {}) {
  const next = { ...defaults, ...(current || {}) };

  [
    'origen_nombre',
    'origen_direccion',
    'origen_comuna',
    'destino_direccion',
    'destino_comuna',
    'destino_ciudad',
    'destino_fuente',
    'fecha_salida',
    'hora_salida',
  ].forEach((key) => {
    if (!firstText(next[key])) {
      next[key] = defaults[key] || '';
    }
  });

  if (!next.destino_tipo) {
    next.destino_tipo = DESTINATION_TYPE.CLIENTE;
  }

  if (next.patente_conocida === undefined || next.patente_conocida === null) {
    next.patente_conocida = true;
  }

  return next;
}

function getInvoiceDeliveryModeLabel(mode) {
  if (mode === INVOICE_DELIVERY_MODE.NO_AMPARA) {
    return 'La factura no ampara el traslado';
  }
  if (mode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE) {
    return 'El cliente retira la mercadería';
  }
  if (mode === INVOICE_DELIVERY_MODE.DESPACHO) {
    return 'Despacharemos la mercadería';
  }
  return 'Pendiente de definir';
}

function validateInvoiceTransferData(mode, data = {}) {
  if (!mode) {
    return 'Debe indicar cómo se entregará la mercadería para emitir la Factura 33.';
  }

  // Res. Ex. SII 144/2026, resolutivo 15°: retiro directo desde el local.
  // La factura o boleta ampara el traslado sin exigir datos adicionales de transporte.
  if (
    mode === INVOICE_DELIVERY_MODE.NO_AMPARA ||
    mode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE
  ) {
    return null;
  }

  if (!firstText(data.origen_direccion) || !firstText(data.origen_comuna)) {
    return 'Complete la dirección y comuna de origen efectivo del traslado.';
  }

  if (!firstText(data.destino_direccion) || !firstText(data.destino_comuna)) {
    return 'Complete la dirección y comuna de destino efectivo del traslado.';
  }

  if (!firstText(data.transportista_rut)) {
    return 'Ingrese el RUT del transportista.';
  }

  if (!firstText(data.chofer_rut) || !firstText(data.chofer_nombre)) {
    return 'Ingrese el RUT y nombre del conductor.';
  }

  if (parseBoolish(data.patente_conocida, true) && !firstText(data.patente)) {
    return 'Ingrese la patente o marque que no es conocida al momento de emitir.';
  }

  if (!firstText(data.fecha_salida) || !firstText(data.hora_salida)) {
    return 'Ingrese la fecha y hora efectiva de salida.';
  }

  if (parseBoolish(data.traslado_prolongado, false)) {
    if (!firstText(data.fecha_llegada_estimada)) {
      return 'Ingrese la fecha estimada de llegada del traslado prolongado.';
    }

    if (!firstText(data.motivo_traslado_prolongado)) {
      return 'Indique el motivo del traslado prolongado.';
    }
  }

  return null;
}

function buildInvoiceTransferPayload(mode, data = {}) {
  const amparaTraslado =
    mode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE ||
    mode === INVOICE_DELIVERY_MODE.DESPACHO;

  if (!amparaTraslado) {
    return null;
  }

  if (mode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE) {
    // Retiro directo: conservar la modalidad sin añadir chofer, patente,
    // transportista, destino ni fechas de traslado (resolutivo 15°).
    // El origen es un dato operacional opcional, no una exigencia al comprador.
    return {
      tipo_entrega: mode,
      retiro_directo: true,
      fundamento_normativo: 'RES_EX_SII_144_2026_RESOLUTIVO_15',
      origen: {
        nombre: firstText(data.origen_nombre) || null,
        direccion: firstText(data.origen_direccion) || null,
        comuna: firstText(data.origen_comuna) || null,
      },
    };
  }

  const patenteConocida = parseBoolish(data.patente_conocida, true);
  const trasladoProlongado = parseBoolish(data.traslado_prolongado, false);

  return {
    tipo_entrega: mode,

    origen: {
      nombre: firstText(data.origen_nombre) || null,
      direccion: firstText(data.origen_direccion) || null,
      comuna: firstText(data.origen_comuna) || null,
    },

    destino: {
      tipo: data.destino_tipo || DESTINATION_TYPE.OTRA,
      direccion: firstText(data.destino_direccion) || null,
      comuna: firstText(data.destino_comuna) || null,
      ciudad: firstText(data.destino_ciudad) || null,
    },

    transportista: {
      tipo: data.transportista_tipo || null,
      rut: firstText(data.transportista_rut) || null,
      nombre: firstText(data.transportista_nombre) || null,
    },

    chofer: {
      rut: firstText(data.chofer_rut) || null,
      nombre: firstText(data.chofer_nombre) || null,
    },

    vehiculo: {
      patente_conocida: patenteConocida,
      patente_desconocida: !patenteConocida,
      patente: patenteConocida ? firstText(data.patente) || null : null,
      patente_carro: firstText(data.patente_carro) || null,
    },

    salida: {
      fecha: firstText(data.fecha_salida) || null,
      hora: firstText(data.hora_salida) || null,
    },

    traslado_prolongado: trasladoProlongado,
    fecha_llegada_estimada: trasladoProlongado
      ? firstText(data.fecha_llegada_estimada) || null
      : null,
    motivo_traslado_prolongado: trasladoProlongado
      ? firstText(data.motivo_traslado_prolongado) || null
      : null,
  };
}

/* ======================================================================
 * CONFIGURACIÓN
 * ==================================================================== */
const getClients = async (params = {}) => {
  const resp = await api.get('/ventas_clientes', {
    params,
    timeout: POS_FAST_API_TIMEOUT_MS,
  });
  return resp.data;
};

const formatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const GENERIC_RUT = '66.666.666-6';

const POS_DRAFT_KEY = 'integranube_pos_draft_v1';

function savePosDraft(data) {
  try {
    localStorage.setItem(POS_DRAFT_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('No se pudo guardar borrador POS:', err);
  }
}

function loadPosDraft() {
  try {
    const raw = localStorage.getItem(POS_DRAFT_KEY);

    if (!raw) return null;

    return JSON.parse(raw);
  } catch (err) {
    console.error('No se pudo leer borrador POS:', err);
    return null;
  }
}

function clearPosDraft() {
  try {
    localStorage.removeItem(POS_DRAFT_KEY);
  } catch (err) {
    console.error('No se pudo limpiar borrador POS:', err);
  }
}

const POS_VENTAS_PENDIENTES_KEY = 'integranube_pos_ventas_en_espera_v1';

function cargarVentasEnEspera() {
  try {
    const raw = localStorage.getItem(POS_VENTAS_PENDIENTES_KEY);

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return [];

    return parsed.filter((venta) => {
      return venta && venta.id && Array.isArray(venta.items);
    });
  } catch (err) {
    console.error('No se pudieron cargar ventas en espera:', err);
    return [];
  }
}

function guardarVentasEnEspera(ventas) {
  try {
    const rows = Array.isArray(ventas) ? ventas : [];
    localStorage.setItem(POS_VENTAS_PENDIENTES_KEY, JSON.stringify(rows));
  } catch (err) {
    console.error('No se pudieron guardar ventas en espera:', err);
  }
}

function crearFolioVentaEnEspera(fecha = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');

  const yyyy = fecha.getFullYear();
  const mm = pad(fecha.getMonth() + 1);
  const dd = pad(fecha.getDate());
  const hh = pad(fecha.getHours());
  const mi = pad(fecha.getMinutes());
  const ss = pad(fecha.getSeconds());

  return `ESP-${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
}

const getCustomerDisplayName = (cust) => {
  if (!cust) return '';

  if (cust.nombres && cust.apellidos) return `${cust.nombres} ${cust.apellidos}`;
  if (cust.razon_social) return cust.razon_social;

  return cust.fullname || cust.name || '';
};

const getProductBarcode = (product) => {
  return (
    product?.codigo_barra ||
    product?.codigo_barras ||
    product?.codigoBarra ||
    product?.codigoBarras ||
    product?.barcode ||
    product?.bar_code ||
    product?.ean ||
    product?.upc ||
    ''
  );
};

const WEIGHT_BARCODE_PATTERN = /^(220|230)\d{10}$/;

function normalizeDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/**
 * Formato de balanza EAN-13 usado por el POS:
 *   220/230 + PLU de 4 dígitos + peso de 5 dígitos + dígito verificador.
 *
 * Ejemplo conceptual:
 *   220 8101 00196 9  => SKU/PLU 8101, peso 0,196 kg.
 */
function parseWeightedBarcode(value) {
  const barcode = normalizeDigits(value);

  if (!WEIGHT_BARCODE_PATTERN.test(barcode)) return null;

  const plu = barcode.slice(3, 7);
  const weightRaw = barcode.slice(7, 12);
  const weightGrams = Number.parseInt(weightRaw, 10);
  const weightKg = weightGrams / 1000;

  if (!Number.isFinite(weightKg) || weightKg <= 0) return null;

  return {
    barcode,
    prefix: barcode.slice(0, 3),
    plu,
    weightRaw,
    weightGrams,
    weightKg,
    checkDigit: barcode.slice(12),
  };
}

function normalizePluComparable(value) {
  const text = String(value ?? '').trim().toUpperCase();
  const digits = normalizeDigits(text);

  return {
    text,
    digits,
    digitsWithoutLeadingZeros: digits.replace(/^0+(?=\d)/, ''),
  };
}

function productMatchesWeightedPlu(product = {}, plu = '') {
  const target = normalizePluComparable(plu);

  if (!target.text) return false;

  const candidates = [
    product.plu,
    product.codigo_plu,
    product.codigoPLU,
    product.sku,
    product.codigo_sku,
    product.codigo,
    getProductBarcode(product),
  ];

  return candidates.some((candidate) => {
    const current = normalizePluComparable(candidate);

    if (!current.text) return false;
    if (current.text === target.text) return true;

    return Boolean(
      current.digits &&
        target.digits &&
        current.digits === target.digits
    );
  });
}

/**
 * Devuelve los tipos de documento permitidos para el cliente
 */
const getAllowedDocTypes = (cust) => {
  if (!cust) return ['boleta'];

  const rut = (cust.rut || '').trim();

  if (rut === GENERIC_RUT) return ['boleta'];

  const tipoCode = cust.tipo_persona != null ? Number(cust.tipo_persona) : null;

  const hasGiroFlag = cust.tiene_giro === true || cust.tiene_giro === 1;
  const hasGiroText = !!cust.giro && String(cust.giro).trim() !== '';
  const hasGiro = hasGiroFlag || hasGiroText;

  if (tipoCode === 2) {
    return ['factura'];
  }

  if (tipoCode === 1) {
    return hasGiro ? ['boleta', 'factura'] : ['boleta'];
  }

  return ['boleta'];
};

/* ======================================================================
 * REGLAS POS - MOTOR LOCAL / CONTINGENCIA
 * ==================================================================== */
const POS_RULES_STORE_NAME = 'pos_rules';

function normalizeRuleText(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function normalizeRuleKey(value) {
  return normalizeRuleText(value)
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_:-]/g, '');
}

function getItemQuantity(item = {}) {
  const qty = Number(item.quantity ?? item.cantidad ?? 0);
  return Number.isFinite(qty) ? qty : 0;
}

function getItemProductId(item = {}) {
  return String(
    item.product_id ??
      item.id_producto ??
      item.productId ??
      item.id ??
      ''
  );
}

function getItemSku(item = {}) {
  return normalizeRuleKey(item.sku ?? item.codigo_sku ?? item.SKU ?? '');
}

function getItemBarcode(item = {}) {
  return normalizeRuleKey(getProductBarcode(item));
}

function getCategoryIdFromRow(cat) {
  if (cat === undefined || cat === null || cat === '') return '';

  if (typeof cat !== 'object') {
    return String(cat);
  }

  return String(
    cat.id ??
      cat.id_categoria ??
      cat.categoria_id ??
      cat.category_id ??
      cat.value ??
      ''
  );
}

function getItemCategoryIds(item = {}) {
  const values = [];

  [
    item.id_categoria,
    item.category_id,
    item.categoria_id,
    item.idCategory,
    item.categoryId,
    item.categoria?.id,
    item.categoria?.id_categoria,
    item.categoria?.categoria_id,
    item.category?.id,
    item.category?.category_id,
  ].forEach((value) => {
    if (value !== undefined && value !== null && value !== '') {
      values.push(String(value));
    }
  });

  if (Array.isArray(item.categorias)) {
    item.categorias.forEach((cat) => {
      const value = getCategoryIdFromRow(cat);

      if (value) values.push(value);
    });
  }

  if (Array.isArray(item.categories)) {
    item.categories.forEach((cat) => {
      const value = getCategoryIdFromRow(cat);

      if (value) values.push(value);
    });
  }

  if (Array.isArray(item.categorias_producto)) {
    item.categorias_producto.forEach((cat) => {
      const value = getCategoryIdFromRow(cat);

      if (value) values.push(value);
    });
  }

  return Array.from(new Set(values));
}

function getItemBrandId(item = {}) {
  return String(item.id_marca ?? item.marca_id ?? item.brand_id ?? item.brandId ?? '');
}

function getItemGrossUnit(item = {}) {
  const qty = Math.max(0, getItemQuantity(item));

  if (item.isWeight) {
    const directUnit = Number(
      item.unitGross ??
        item.grossUnit ??
        item.precio_unitario_bruto ??
        item.precio_bruto_unitario ??
        item.precio_bruto_kg ??
        item.gross_per_kg ??
        0
    );

    if (Number.isFinite(directUnit) && directUnit > 0) return directUnit;

    const lineTotal = Number(
      item.lineTotal ??
        item.line_total ??
        item.total ??
        item.total_bruto ??
        item.gross ??
        0
    );

    if (Number.isFinite(lineTotal) && lineTotal > 0 && qty > 0) {
      return lineTotal / qty;
    }

    return 0;
  }

  const gross = Number(item.gross ?? item.precio_bruto ?? item.price ?? item.precio ?? 0);

  if (Number.isFinite(gross) && gross > 0) return gross;

  const lineTotal = Number(item.lineTotal ?? item.total ?? item.total_bruto ?? 0);

  if (Number.isFinite(lineTotal) && lineTotal > 0 && qty > 0) return lineTotal / qty;

  return 0;
}

function getItemGrossLine(item = {}) {
  const qty = getItemQuantity(item);

  if (item.isWeight) {
    const line = Number(item.lineTotal ?? item.total ?? item.total_bruto ?? item.gross ?? 0);
    return Number.isFinite(line) ? line : 0;
  }

  return getItemGrossUnit(item) * qty;
}

/*
 * Productos pesables en el carrito
 * ---------------------------------
 * Contrato interno:
 *   - quantity / cantidad: peso total en kg.
 *   - unitNet / unitGross: precio por kg.
 *   - net / tax / gross / lineTotal: importes TOTALES de la línea.
 *
 * Esto es importante porque un producto pesable puede entrar tanto desde un
 * código de balanza (peso embebido) como desde su SKU/PLU normal. En este
 * último caso, cada marcación agrega 1 kg; nunca debemos aumentar quantity
 * dejando gross congelado, porque terminaríamos dividiendo el precio por kg.
 */
function getWeightedUnitGross(item = {}) {
  const candidates = [
    item.unitGross,
    item.grossUnit,
    item.precio_unitario_bruto,
    item.precio_bruto_unitario,
    item.precio_bruto_kg,
    item.precio_bruto,
    item.price,
    item.precio,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }

  const qty = Math.max(0, getItemQuantity(item));
  const line = Number(item.lineTotal ?? item.line_total ?? item.total_bruto ?? item.total ?? item.gross ?? 0);

  if (Number.isFinite(line) && line > 0 && qty > 0) return line / qty;
  return 0;
}

function getWeightedUnitNet(item = {}) {
  const candidates = [
    item.unitNet,
    item.precio_neto_unitario,
    item.precio_unitario_neto,
    item.precio_neto,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }

  const qty = Math.max(0, getItemQuantity(item));
  const line = Number(item.total_neto ?? item.net ?? 0);

  if (Number.isFinite(line) && line > 0 && qty > 0) return line / qty;

  const unitGross = getWeightedUnitGross(item);
  const pct = getItemTaxPercentForPayload(item);

  if (unitGross > 0 && Number.isFinite(pct) && pct > 0) {
    return unitGross / (1 + pct / 100);
  }

  return unitGross;
}

function buildWeightedCartLine(item = {}, quantity = null) {
  const qtyRaw = quantity ?? item.quantity ?? item.cantidad ?? 0;
  const qty = roundDecimals(Number(qtyRaw || 0), 3);

  if (!Number.isFinite(qty) || qty <= 0) {
    return {
      ...item,
      quantity: qty,
      cantidad: qty,
    };
  }

  const unitGross = getWeightedUnitGross(item);
  const unitNet = getWeightedUnitNet(item);

  const lineGross = roundDecimals(unitGross * qty, 2);
  let lineNet = roundDecimals(unitNet * qty, 2);

  if (!(Number.isFinite(lineNet) && lineNet >= 0)) {
    lineNet = lineGross;
  }

  const lineTax = Math.max(0, roundDecimals(lineGross - lineNet, 2));
  const unitTax = qty > 0 ? roundDecimals(lineTax / qty, 8) : 0;

  return {
    ...item,
    quantity: qty,
    cantidad: qty,

    net: lineNet,
    tax: lineTax,
    gross: lineGross,
    lineTotal: lineGross,
    line_total: lineGross,
    total: lineGross,
    total_neto: lineNet,
    total_iva: lineTax,
    total_bruto: lineGross,

    unitNet,
    unitTax,
    unitGross,
    grossUnit: unitGross,
    precio_neto_unitario: unitNet,
    precio_unitario_neto: unitNet,
    precio_unitario_bruto: unitGross,
    precio_bruto_unitario: unitGross,
    precio_bruto_kg: unitGross,

    isWeight: true,
    pesable: true,
    peso_kg: qty,
    peso_gramos: Math.round(qty * 1000),
  };
}

function isRuleActiveNow(rule = {}, now = new Date()) {
  const estado = normalizeRuleKey(rule.estado || 'ACTIVA');

  if (!['ACTIVA', 'ACTIVE'].includes(estado)) return false;

  const from = rule.vigencia_desde ? new Date(rule.vigencia_desde) : null;
  const to = rule.vigencia_hasta ? new Date(rule.vigencia_hasta) : null;
  const t = now instanceof Date ? now.getTime() : new Date(now).getTime();

  if (from && !Number.isNaN(from.getTime()) && t < from.getTime()) return false;
  if (to && !Number.isNaN(to.getTime()) && t > to.getTime()) return false;

  const limite = Number(rule.limite_usos_total || 0);
  const usados = Number(rule.usos_actuales || 0);

  if (limite > 0 && usados >= limite) return false;

  return true;
}

function getRulePaymentRows(rule = {}) {
  return Array.isArray(rule.metodos_pago)
    ? rule.metodos_pago
    : Array.isArray(rule.medios_pago)
      ? rule.medios_pago
      : Array.isArray(rule.metodospago)
        ? rule.metodospago
        : [];
}

function ruleMatchesPayment(rule = {}, paymentMethod = null) {
  const rows = getRulePaymentRows(rule);

  if (!rows.length) return true;

  const paymentIsObject = paymentMethod && typeof paymentMethod === 'object';

  const methodKey = normalizeRuleKey(
    paymentIsObject
      ? paymentMethod?.clave ??
          paymentMethod?.key ??
          paymentMethod?.rawKey ??
          paymentMethod?.payment_method_key ??
          paymentMethod?.payment_method_type ??
          paymentMethod?.metodo_pago_clave ??
          ''
      : paymentMethod
  );

  const methodTipo = normalizeRuleKey(
    paymentIsObject
      ? paymentMethod?.tipo ??
          paymentMethod?.metodo_pago_tipo ??
          paymentMethod?.methodType ??
          ''
      : ''
  );

  const methodId = String(
    paymentIsObject
      ? paymentMethod?.methodId ??
          paymentMethod?.metodo_pago_id ??
          paymentMethod?.payment_method_id ??
          paymentMethod?.id ??
          ''
      : paymentMethod ?? ''
  );

  const includes = rows.filter((row) => row.incluir !== false);
  const excludes = rows.filter((row) => row.incluir === false);

  const matchesRow = (row) => {
    const rowId = String(row.id_metodo_pago ?? row.metodo_pago_id ?? '');
    const rowClave = normalizeRuleKey(row.metodo_pago_clave ?? row.clave ?? '');
    const rowTipo = normalizeRuleKey(row.metodo_pago_tipo ?? row.tipo ?? '');

    if (rowId && methodId && rowId === methodId) return true;
    if (rowClave && methodKey && rowClave === methodKey) return true;
    if (rowTipo && methodTipo && rowTipo === methodTipo) return true;
    if (rowTipo === 'EFECTIVO' && methodKey === 'EFECTIVO') return true;

    return false;
  };

  if (excludes.some(matchesRow)) return false;
  if (!includes.length) return true;

  return includes.some(matchesRow);
}

function getRuleScopes(rule = {}) {
  return Array.isArray(rule.alcances) ? rule.alcances : [];
}

function itemMatchesScope(item = {}, scope = {}) {
  const tipo = normalizeRuleKey(scope.alcance_tipo || scope.tipo || 'GLOBAL');

  if (tipo === 'GLOBAL') return true;

  if (tipo === 'PRODUCTO') {
    /*
     * Un alcance PRODUCTO se guarda normalmente con id_producto y, además,
     * con SKU/código de barras de respaldo. El carrito puede venir desde API
     * o IndexedDB y no siempre conserva el mismo campo identificador primario.
     *
     * Primero intentamos el ID; si no coincide, usamos los identificadores
     * estables guardados en el alcance. Esto evita que una promoción válida
     * quede fuera cuando el producto escaneado conserva correctamente su
     * barcode/SKU pero su id local proviene de otra representación del catálogo.
     */
    const productId = String(scope.id_producto ?? scope.producto_id ?? '');
    const itemProductId = getItemProductId(item);

    if (productId && itemProductId && itemProductId === productId) return true;

    const scopeBarcode = normalizeRuleKey(
      scope.codigo_barras ?? scope.codigo_barra ?? scope.barcode ?? ''
    );
    const itemBarcode = getItemBarcode(item);

    if (scopeBarcode && itemBarcode && scopeBarcode === itemBarcode) return true;

    const scopeSku = normalizeRuleKey(scope.sku ?? scope.codigo_sku ?? '');
    const itemSku = getItemSku(item);

    if (scopeSku && itemSku && scopeSku === itemSku) return true;

    return false;
  }

  if (tipo === 'SKU') {
    const sku = normalizeRuleKey(scope.sku || '');
    return sku && getItemSku(item) === sku;
  }

  if (tipo === 'CODIGO_BARRAS') {
    const barcode = normalizeRuleKey(scope.codigo_barras || scope.barcode || '');
    return barcode && getItemBarcode(item) === barcode;
  }

  if (tipo === 'CATEGORIA') {
    const catId = String(scope.id_categoria ?? scope.categoria_id ?? '');
    return catId && getItemCategoryIds(item).includes(catId);
  }

  if (tipo === 'MARCA') {
    const brandId = String(scope.id_marca ?? scope.marca_id ?? '');
    return brandId && getItemBrandId(item) === brandId;
  }

  return false;
}

function getAffectedItemsForRule(items = [], rule = {}) {
  const scopes = getRuleScopes(rule);
  const includes = scopes.filter((s) => s.incluir !== false);
  const excludes = scopes.filter((s) => s.incluir === false);

  return (Array.isArray(items) ? items : []).filter((item) => {
    const included = includes.length
      ? includes.some((scope) => itemMatchesScope(item, scope))
      : true;

    if (!included) return false;
    if (excludes.some((scope) => itemMatchesScope(item, scope))) return false;

    return true;
  });
}

function componentMatchesItem(component = {}, item = {}) {
  return itemMatchesScope(item, {
    alcance_tipo: component.alcance_tipo,
    id_producto: component.id_producto,
    id_categoria: component.id_categoria,
    id_marca: component.id_marca,
    sku: component.sku,
    codigo_barras: component.codigo_barras,
    incluir: true,
  });
}

function getRuleSign(rule = {}) {
  const tipo = normalizeRuleKey(rule.tipo_ajuste || 'DESCUENTO');

  if (tipo === 'RECARGO') return 1;
  if (['DESCUENTO', 'DEVOLUCION', 'BONIFICACION'].includes(tipo)) return -1;

  return 0;
}

function buildAdjustmentRow({ rule, monto, detalle = [], descripcion = '' }) {
  return {
    regla_id: rule.id,
    codigo: rule.codigo,
    nombre: rule.nombre,
    glosa: rule.glosa_pos || rule.glosa_ticket || rule.nombre || rule.codigo,
    tipo_regla: rule.tipo_regla,
    tipo_ajuste: rule.tipo_ajuste,
    tipo_calculo: rule.tipo_calculo,
    monto: Math.round(Number(monto || 0)),
    detalle,
    descripcion,
  };
}

function readNullableNumber(value) {
  if (value === undefined || value === null || value === '') return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getPackGroupKey(item = {}, index = 0) {
  const productId = getItemProductId(item);
  const sku = getItemSku(item);
  const barcode = getItemBarcode(item);

  if (productId) return `P:${productId}`;
  if (sku) return `SKU:${sku}`;
  if (barcode) return `BC:${barcode}`;

  return `IDX:${index}`;
}

function resolvePackComponentFinalUnitPrice(component = {}, grossUnit = 0) {
  const precioUnitarioResultado = readNullableNumber(component.precio_unitario_resultado);

  if (precioUnitarioResultado !== null) {
    return Math.max(0, precioUnitarioResultado);
  }

  const descuentoUnitario = readNullableNumber(component.descuento_unitario);

  if (descuentoUnitario !== null) {
    return Math.max(0, grossUnit - descuentoUnitario);
  }

  const descuentoPorcentaje = readNullableNumber(component.descuento_porcentaje);

  if (descuentoPorcentaje !== null) {
    return Math.max(0, grossUnit * (1 - descuentoPorcentaje / 100));
  }

  return null;
}


function evaluatePackRule({ items = [], rule = {} }) {
  const componentes = Array.isArray(rule.componentes) ? rule.componentes : [];

  if (!componentes.length) return null;

  const tipoCalculo = normalizeRuleKey(rule.tipo_calculo || '');
  const precioPackTotal = readNullableNumber(
    rule.precio_pack_total ?? rule.resultado?.precio_pack_total
  );

  const missingRequired = [];
  const groups = new Map();

  componentes.forEach((component, componentIndex) => {
    const itemIndex = items.findIndex((row) => componentMatchesItem(component, row));
    const item = itemIndex >= 0 ? items[itemIndex] : null;
    const requiredQty = Math.max(1, Number(component.cantidad_requerida || 1));

    if (!item) {
      if (component.obligatorio !== false) {
        missingRequired.push(component);
      }
      return;
    }

    const key = getPackGroupKey(item, itemIndex);

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        item,
        itemIndex,
        requiredQty: 0,
        components: [],
      });
    }

    const group = groups.get(key);

    group.requiredQty += requiredQty;
    group.components.push({
      component,
      componentIndex,
      requiredQty,
    });
  });

  if (missingRequired.length > 0) return null;
  if (!groups.size) return null;

  const groupRows = Array.from(groups.values());

  const packTimes = Math.min(
    ...groupRows.map((group) => {
      const availableQty = getItemQuantity(group.item);
      return Math.floor(availableQty / group.requiredQty);
    })
  );

  if (!Number.isFinite(packTimes) || packTimes <= 0) return null;

  const normalPackGross = groupRows.reduce((sum, group) => {
    return sum + getItemGrossUnit(group.item) * group.requiredQty;
  }, 0);

  if (normalPackGross <= 0) return null;

  const detalle = [];
  let ajuste = 0;

  if (tipoCalculo === 'PRECIO_PACK' && precioPackTotal !== null && precioPackTotal >= 0) {
    const ajusteTotal = Math.round((precioPackTotal - normalPackGross) * packTimes);

    if (ajusteTotal === 0) return null;

    let distribuido = 0;

    groupRows.forEach((group, index) => {
      const grossUnit = getItemGrossUnit(group.item);
      const qty = group.requiredQty * packTimes;
      const normalLine = grossUnit * qty;

      const share = normalPackGross > 0
        ? (grossUnit * group.requiredQty) / normalPackGross
        : 1 / groupRows.length;

      const ajusteLinea =
        index === groupRows.length - 1
          ? ajusteTotal - distribuido
          : Math.round(ajusteTotal * share);

      distribuido += ajusteLinea;
      ajuste += ajusteLinea;

      const finalLine = Math.max(0, Math.round(normalLine + ajusteLinea));
      const precioFinal = qty > 0 ? finalLine / qty : grossUnit;

      detalle.push({
        producto_id: getItemProductId(group.item),
        sku: group.item.sku || '',
        nombre: group.item.name || group.item.nombre || '',
        cantidad: qty,
        precio_normal: Math.round(grossUnit),
        precio_final: Math.max(0, Math.round(precioFinal)),
        ajuste: ajusteLinea,
        grupo_codigo: group.components.map((c) => c.component.grupo_codigo || '').filter(Boolean).join(', '),
      });
    });

    return buildAdjustmentRow({
      rule,
      monto: ajuste,
      detalle,
      descripcion: `Pack aplicado ${packTimes} vez/veces`,
    });
  }

  groupRows.forEach((group) => {
    const grossUnit = getItemGrossUnit(group.item);

    group.components.forEach(({ component, requiredQty }) => {
      const qty = requiredQty * packTimes;
      const precioFinal = resolvePackComponentFinalUnitPrice(component, grossUnit);

      if (precioFinal === null) return;

      const ajusteLinea = Math.round((precioFinal - grossUnit) * qty);

      if (ajusteLinea === 0) return;

      ajuste += ajusteLinea;

      detalle.push({
        producto_id: getItemProductId(group.item),
        sku: group.item.sku || '',
        nombre: group.item.name || group.item.nombre || '',
        cantidad: qty,
        precio_normal: Math.round(grossUnit),
        precio_final: Math.max(0, Math.round(precioFinal)),
        ajuste: ajusteLinea,
        grupo_codigo: component.grupo_codigo || '',
      });
    });
  });

  if (ajuste === 0) return null;

  return buildAdjustmentRow({
    rule,
    monto: ajuste,
    detalle,
    descripcion: `Pack aplicado ${packTimes} vez/veces`,
  });
}


/**
 * Evalúa ofertas del tipo:
 *   OFERTA_CANTIDAD + PRECIO_PACK
 *
 * Contrato:
 *   - regla.alcances define qué ítems participan.
 *   - regla.cantidad_minima define cuántas unidades forman un pack.
 *   - regla.precio_pack_total define el precio FINAL de cada pack completo.
 *   - las unidades sobrantes conservan su precio normal.
 *   - la promoción se repite por cada pack completo.
 *
 * Ejemplo:
 *   precio normal $1.490, cantidad_minima=2, precio_pack_total=$2.790
 *   1 => 1.490
 *   2 => 2.790
 *   3 => 4.280
 *   4 => 5.580
 *
 * Importante:
 *   Esto NO usa rule.componentes. Los componentes quedan reservados para
 *   COMBO_PACK / PRECIOS_COMPONENTES.
 */
function evaluateQuantityPricePackRule({ items = [], rule = {} }) {
  const affected = getAffectedItemsForRule(items, rule);

  if (!affected.length) return null;

  const packQty = readNullableNumber(
    rule.cantidad_minima ?? rule.condiciones?.cantidad_minima
  );

  const precioPackTotal = readNullableNumber(
    rule.precio_pack_total ??
      rule.resultado?.precio_pack_total ??
      rule.resultado?.precio_pack
  );

  if (packQty === null || !Number.isFinite(packQty) || packQty <= 0) return null;
  if (
    precioPackTotal === null ||
    !Number.isFinite(precioPackTotal) ||
    precioPackTotal < 0
  ) {
    return null;
  }

  const totalAffectedQty = affected.reduce(
    (sum, item) => sum + Math.max(0, getItemQuantity(item)),
    0
  );

  if (totalAffectedQty <= 0) return null;

  /*
   * Si existe cantidad_maxima, se interpreta como el máximo de unidades
   * elegibles para esta regla. El excedente se mantiene a precio normal.
   * Si no existe, todos los packs completos pueden repetir la promoción.
   */
  const maxQty = readNullableNumber(
    rule.cantidad_maxima ?? rule.condiciones?.cantidad_maxima
  );

  const eligibleQty =
    maxQty !== null && Number.isFinite(maxQty) && maxQty > 0
      ? Math.min(totalAffectedQty, maxQty)
      : totalAffectedQty;

  // Epsilon pequeño para evitar que 1.999999999 impida formar un pack de 2.
  const packTimes = Math.floor((eligibleQty + 1e-9) / packQty);

  if (!Number.isFinite(packTimes) || packTimes <= 0) return null;

  const promoQtyTotal = packTimes * packQty;
  let promoQtyRemaining = promoQtyTotal;

  /*
   * Normalmente OFERTA_CANTIDAD se configura sobre un solo producto.
   * Aun así, soportamos más de una línea afectada distribuyendo las unidades
   * promocionadas en el orden del carrito y el ajuste proporcionalmente a su
   * valor bruto normal.
   */
  const promoRows = [];

  affected.forEach((item) => {
    if (promoQtyRemaining <= 1e-9) return;

    const availableQty = Math.max(0, getItemQuantity(item));
    if (availableQty <= 0) return;

    const promoQty = Math.min(availableQty, promoQtyRemaining);
    const grossUnit = getItemGrossUnit(item);
    const normalLine = grossUnit * promoQty;

    if (!Number.isFinite(grossUnit) || grossUnit <= 0 || normalLine <= 0) return;

    promoRows.push({
      item,
      promoQty,
      grossUnit,
      normalLine,
    });

    promoQtyRemaining -= promoQty;
  });

  if (!promoRows.length || promoQtyRemaining > 1e-6) return null;

  const normalPromoGross = promoRows.reduce(
    (sum, row) => sum + row.normalLine,
    0
  );

  if (!Number.isFinite(normalPromoGross) || normalPromoGross <= 0) return null;

  const promoFinalGross = precioPackTotal * packTimes;
  const ajusteTotal = Math.round(promoFinalGross - normalPromoGross);

  if (ajusteTotal === 0) return null;

  const detalle = [];
  let distribuido = 0;

  promoRows.forEach((row, index) => {
    const share =
      normalPromoGross > 0
        ? row.normalLine / normalPromoGross
        : 1 / promoRows.length;

    const ajusteLinea =
      index === promoRows.length - 1
        ? ajusteTotal - distribuido
        : Math.round(ajusteTotal * share);

    distribuido += ajusteLinea;

    const finalLine = Math.max(0, Math.round(row.normalLine + ajusteLinea));
    const precioFinal =
      row.promoQty > 0 ? finalLine / row.promoQty : row.grossUnit;

    detalle.push({
      producto_id: getItemProductId(row.item),
      sku: row.item.sku || '',
      codigo_barras: getProductBarcode(row.item) || '',
      nombre: row.item.name || row.item.nombre || '',
      cantidad: row.promoQty,
      precio_normal: Math.round(row.grossUnit),
      precio_final: Math.max(0, Math.round(precioFinal)),
      ajuste: ajusteLinea,
      cantidad_pack: packQty,
      packs_aplicados: packTimes,
      precio_pack_total: Math.round(precioPackTotal),
    });
  });

  return buildAdjustmentRow({
    rule,
    monto: ajusteTotal,
    detalle,
    descripcion:
      `${packTimes} pack${packTimes === 1 ? '' : 's'} de ` +
      `${packQty} por ${Math.round(precioPackTotal)}`,
  });
}

function evaluateSimpleRule({ items = [], rule = {} }) {
  const affected = getAffectedItemsForRule(items, rule);

  if (!affected.length) return null;

  const tipoCalculo = normalizeRuleKey(rule.tipo_calculo || '');
  const sign = getRuleSign(rule);
  const porcentaje = Number(rule.porcentaje || 0);
  const monto = Number(rule.monto || 0);
  const cantidadMinima = Number(rule.cantidad_minima || 0);
  const totalQty = affected.reduce((sum, item) => sum + getItemQuantity(item), 0);

  if (cantidadMinima > 0 && totalQty < cantidadMinima) return null;

  let ajuste = 0;
  const detalle = [];

  if (tipoCalculo === 'MONTO_FIJO_VENTA') {
    ajuste = Math.round(monto * sign);
  } else if (tipoCalculo === 'PORCENTAJE_TOTAL') {
    const base = items.reduce((sum, item) => sum + getItemGrossLine(item), 0);
    ajuste = Math.round(base * (porcentaje / 100) * sign);
  } else {
    affected.forEach((item) => {
      const qty = getItemQuantity(item);
      const grossUnit = getItemGrossUnit(item);
      const grossLine = getItemGrossLine(item);

      let ajusteLinea = 0;
      let precioFinal = grossUnit;

      if (tipoCalculo === 'MONTO_POR_UNIDAD') {
        ajusteLinea = Math.round(monto * qty * sign);
        precioFinal = grossUnit + monto * sign;
      } else if (tipoCalculo === 'PORCENTAJE_ITEMS') {
        ajusteLinea = Math.round(grossLine * (porcentaje / 100) * sign);
        precioFinal = grossUnit + grossUnit * (porcentaje / 100) * sign;
      } else if (tipoCalculo === 'PRECIO_FIJO_UNITARIO') {
        precioFinal = monto;
        ajusteLinea = Math.round((precioFinal - grossUnit) * qty);
      }

      ajuste += ajusteLinea;

      detalle.push({
        producto_id: getItemProductId(item),
        sku: item.sku || '',
        nombre: item.name || item.nombre || '',
        cantidad: qty,
        precio_normal: Math.round(grossUnit),
        precio_final: Math.max(0, Math.round(precioFinal)),
        ajuste: ajusteLinea,
      });
    });
  }

  if (ajuste === 0) return null;

  return buildAdjustmentRow({
    rule,
    monto: ajuste,
    detalle,
  });
}

function evaluatePosRules({
  items = [],
  rules = [],
  paymentMethod = null,
  authorizedRuleIds = {},
  now = new Date(),
}) {
  const sortedRules = (Array.isArray(rules) ? rules : [])
    .filter((rule) => isRuleActiveNow(rule, now))
    .filter((rule) => ruleMatchesPayment(rule, paymentMethod))
    .sort((a, b) => Number(a.prioridad || 100) - Number(b.prioridad || 100));

  const ajustes = [];
  const reglasAplicadas = [];
  const reglasPendientesPin = [];

  sortedRules.forEach((rule) => {
    let ajuste = null;

    const tipoCalculo = normalizeRuleKey(rule.tipo_calculo || '');
    const tipoRegla = normalizeRuleKey(rule.tipo_regla || '');

    /*
     * OFERTA_CANTIDAD + PRECIO_PACK es una promoción por cantidad sobre
     * alcances (por ejemplo: 2 leches por $2.790). No requiere componentes.
     *
     * COMBO_PACK y PRECIOS_COMPONENTES conservan el evaluador histórico
     * basado en rule.componentes.
     */
    if (tipoRegla === 'OFERTA_CANTIDAD' && tipoCalculo === 'PRECIO_PACK') {
      ajuste = evaluateQuantityPricePackRule({ items, rule });
    } else if (
      tipoRegla === 'COMBO_PACK' ||
      tipoCalculo === 'PRECIOS_COMPONENTES' ||
      tipoCalculo === 'PRECIO_PACK'
    ) {
      ajuste = evaluatePackRule({ items, rule });
    } else {
      ajuste = evaluateSimpleRule({ items, rule });
    }

    if (!ajuste) return;

    const requiresPin = parseBoolish(rule.requiere_pin, false);
    const authorized = Boolean(authorizedRuleIds[String(rule.id)]?.authorized);

    if (requiresPin && !authorized) {
      reglasPendientesPin.push({
        id: rule.id,
        codigo: rule.codigo,
        nombre: rule.nombre,
        monto_estimado: ajuste.monto,
        glosa: ajuste.glosa,
      });

      return;
    }

    ajustes.push(ajuste);

    reglasAplicadas.push({
      regla_id: rule.id,
      codigo: rule.codigo,
      nombre: rule.nombre,
      tipo_regla: rule.tipo_regla,
      tipo_ajuste: rule.tipo_ajuste,
      tipo_calculo: rule.tipo_calculo,
      monto: ajuste.monto,
      autorizada: requiresPin ? true : false,
    });
  });

  const totalAjusteBruto = ajustes.reduce((sum, row) => sum + Number(row.monto || 0), 0);

  return {
    reglasAplicadas,
    reglasPendientesPin,
    ajustes,
    totalAjusteBruto,
  };
}

/* ======================================================================
 * MODO CONTINGENCIA POS - IndexedDB
 * ==================================================================== */

const OFFLINE_DB_NAME = 'IntegraNubePOS';
const OFFLINE_DB_VERSION = 3;
const OFFLINE_STORE_NAME = 'offline_sales';
const POS_PRODUCTS_STORE_NAME = 'pos_products';
const POS_CUSTOMERS_STORE_NAME = 'pos_customers';
const POS_CONFIG_STORE_NAME = 'pos_config';

const POS_FAST_API_TIMEOUT_MS = 6000;
const POS_SYNC_API_TIMEOUT_MS = 15000;
const POS_SALE_API_TIMEOUT_MS = 35000;
const POS_API_PROBE_TIMEOUT_MS = 4000;
const POS_API_RECOVERY_INTERVAL_MS = 5000;
const POS_SALE_HEALTHCHECK_DELAY_MS = 3500;
const POS_PRODUCT_CACHE_FULL_TTL_MS = 24 * 60 * 60 * 1000;
const POS_PRODUCT_CACHE_DELTA_INTERVAL_MS = 5 * 60 * 1000;
const POS_OFFLINE_QUEUE_RETRY_INTERVAL_MS = 30 * 1000;

const hasIndexedDB =
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

function safeJsonParse(value, fallback = null) {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function normalizeScopeValue(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim().toLowerCase();
}

function getCurrentTenantIdFromStorage() {
  const direct =
    sessionStorage.getItem('tenantid') ||
    sessionStorage.getItem('tenantId') ||
    sessionStorage.getItem('tenant_id') ||
    localStorage.getItem('tenantid') ||
    localStorage.getItem('tenantId') ||
    localStorage.getItem('tenant_id');

  if (direct) return String(direct).trim();

  const tenantRaw = sessionStorage.getItem('tenant') || localStorage.getItem('tenant');
  const tenant = safeJsonParse(tenantRaw, null);

  if (tenant && typeof tenant === 'object') {
    return (
      tenant.id ||
      tenant.tenantid ||
      tenant.tenantId ||
      tenant.tenant_id ||
      tenant.uuid ||
      ''
    );
  }

  return tenantRaw || '';
}

function getCurrentUserFromStorage() {
  const userRaw = sessionStorage.getItem('user') || localStorage.getItem('user');
  const user = safeJsonParse(userRaw, null);

  if (user && typeof user === 'object') {
    return {
      userId:
        user.id ||
        user.id_usuario ||
        user.usuario_id ||
        user.account_id ||
        user.accountId ||
        user.sub ||
        null,

      userEmail:
        user.email ||
        user.correo ||
        user.username ||
        user.user_email ||
        null,

      userName:
        user.name ||
        user.nombre ||
        user.fullname ||
        user.fullName ||
        null,
    };
  }

  return {
    userId: null,
    userEmail: null,
    userName: null,
  };
}

function getCurrentPosScope() {
  const tenantId = getCurrentTenantIdFromStorage();
  const user = getCurrentUserFromStorage();

  return {
    tenantId: tenantId || null,
    userId: user.userId || null,
    userEmail: user.userEmail || null,
    userName: user.userName || null,
  };
}

function offlineSaleMatchesScope(record, scope = {}) {
  if (!record) return false;

  const tenantA = normalizeScopeValue(record.tenantId || record.tenant_id);
  const tenantB = normalizeScopeValue(scope.tenantId);

  if (tenantB && tenantA && tenantA !== tenantB) return false;

  const userA = normalizeScopeValue(record.userId || record.user_id);
  const userB = normalizeScopeValue(scope.userId);

  const emailA = normalizeScopeValue(record.userEmail || record.user_email);
  const emailB = normalizeScopeValue(scope.userEmail);

  if (userB && userA && userA !== userB) return false;
  if (!userB && emailB && emailA && emailA !== emailB) return false;

  return true;
}

function openOfflineDB() {
  if (!hasIndexedDB) {
    return Promise.reject(new Error('IndexedDB no está disponible en este navegador.'));
  }

  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = event.target.result;

      let offlineStore;

      if (!db.objectStoreNames.contains(OFFLINE_STORE_NAME)) {
        offlineStore = db.createObjectStore(OFFLINE_STORE_NAME, {
          keyPath: 'offlineId',
        });
      } else {
        offlineStore = event.target.transaction.objectStore(OFFLINE_STORE_NAME);
      }

      try {
        if (!offlineStore.indexNames.contains('by_createdAt')) {
          offlineStore.createIndex('by_createdAt', 'createdAt', { unique: false });
        }

        if (!offlineStore.indexNames.contains('by_synced')) {
          offlineStore.createIndex('by_synced', 'synced', { unique: false });
        }

        if (!offlineStore.indexNames.contains('by_tenantId')) {
          offlineStore.createIndex('by_tenantId', 'tenantId', { unique: false });
        }

        if (!offlineStore.indexNames.contains('by_userId')) {
          offlineStore.createIndex('by_userId', 'userId', { unique: false });
        }

        if (!offlineStore.indexNames.contains('by_scope')) {
          offlineStore.createIndex('by_scope', ['tenantId', 'userId'], { unique: false });
        }
      } catch (err) {
        console.warn('[POS IndexedDB] No se pudieron crear todos los índices offline:', err);
      }

      let rulesStore;

      if (!db.objectStoreNames.contains(POS_RULES_STORE_NAME)) {
        rulesStore = db.createObjectStore(POS_RULES_STORE_NAME, {
          keyPath: 'id',
        });
      } else {
        rulesStore = event.target.transaction.objectStore(POS_RULES_STORE_NAME);
      }

      try {
        if (!rulesStore.indexNames.contains('by_estado')) {
          rulesStore.createIndex('by_estado', 'estado', { unique: false });
        }

        if (!rulesStore.indexNames.contains('by_tipo_regla')) {
          rulesStore.createIndex('by_tipo_regla', 'tipo_regla', { unique: false });
        }

        if (!rulesStore.indexNames.contains('by_cachedAt')) {
          rulesStore.createIndex('by_cachedAt', 'cachedAt', { unique: false });
        }
      } catch (err) {
        console.warn('[POS IndexedDB] No se pudieron crear todos los índices reglas:', err);
      }

      let productsStore;

      if (!db.objectStoreNames.contains(POS_PRODUCTS_STORE_NAME)) {
        productsStore = db.createObjectStore(POS_PRODUCTS_STORE_NAME, {
          keyPath: 'cacheKey',
        });
      } else {
        productsStore = event.target.transaction.objectStore(POS_PRODUCTS_STORE_NAME);
      }

      try {
        if (!productsStore.indexNames.contains('by_tenant')) {
          productsStore.createIndex('by_tenant', 'tenantId', { unique: false });
        }
        if (!productsStore.indexNames.contains('by_tenant_id')) {
          productsStore.createIndex('by_tenant_id', ['tenantId', 'productId'], { unique: true });
        }
        if (!productsStore.indexNames.contains('by_tenant_sku')) {
          productsStore.createIndex('by_tenant_sku', ['tenantId', 'skuNorm'], { unique: false });
        }
        if (!productsStore.indexNames.contains('by_tenant_barcode')) {
          productsStore.createIndex('by_tenant_barcode', ['tenantId', 'barcodeNorm'], { unique: false });
        }
      } catch (err) {
        console.warn('[POS IndexedDB] No se pudieron crear índices de productos:', err);
      }

      let customersStore;

      if (!db.objectStoreNames.contains(POS_CUSTOMERS_STORE_NAME)) {
        customersStore = db.createObjectStore(POS_CUSTOMERS_STORE_NAME, {
          keyPath: 'cacheKey',
        });
      } else {
        customersStore = event.target.transaction.objectStore(POS_CUSTOMERS_STORE_NAME);
      }

      try {
        if (!customersStore.indexNames.contains('by_tenant')) {
          customersStore.createIndex('by_tenant', 'tenantId', { unique: false });
        }
        if (!customersStore.indexNames.contains('by_tenant_rut')) {
          customersStore.createIndex('by_tenant_rut', ['tenantId', 'rutNorm'], { unique: false });
        }
      } catch (err) {
        console.warn('[POS IndexedDB] No se pudieron crear índices de clientes:', err);
      }

      let configStore;

      if (!db.objectStoreNames.contains(POS_CONFIG_STORE_NAME)) {
        configStore = db.createObjectStore(POS_CONFIG_STORE_NAME, {
          keyPath: 'cacheKey',
        });
      } else {
        configStore = event.target.transaction.objectStore(POS_CONFIG_STORE_NAME);
      }

      try {
        if (!configStore.indexNames.contains('by_tenant')) {
          configStore.createIndex('by_tenant', 'tenantId', { unique: false });
        }
      } catch (err) {
        console.warn('[POS IndexedDB] No se pudieron crear índices de configuración:', err);
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('No se pudo abrir IndexedDB.'));
  });
}

async function saveOfflineSaleToDB(record) {
  if (!hasIndexedDB) {
    throw new Error('IndexedDB no está disponible.');
  }

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(OFFLINE_STORE_NAME);

    store.put(record);

    tx.oncomplete = () => {
      db.close();
      resolve(record);
    };

    tx.onerror = () => {
      const error = tx.error || new Error('No se pudo guardar la venta offline.');
      db.close();
      reject(error);
    };
  });
}

async function getOfflineSalesFromDB({ includeAllUsers = false } = {}) {
  if (!hasIndexedDB) return [];

  const scope = getCurrentPosScope();
  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readonly');
    const store = tx.objectStore(OFFLINE_STORE_NAME);
    const req = store.getAll();

    req.onsuccess = () => {
      db.close();

      const rows = Array.isArray(req.result) ? req.result : [];

      const scoped = includeAllUsers
        ? rows.filter((record) => {
            const tenantA = normalizeScopeValue(record.tenantId || record.tenant_id);
            const tenantB = normalizeScopeValue(scope.tenantId);

            if (!tenantB) return true;
            if (!tenantA) return true;

            return tenantA === tenantB;
          })
        : rows.filter((record) => offlineSaleMatchesScope(record, scope));

      scoped.sort((a, b) => {
        const da = new Date(a.createdAt || 0).getTime();
        const dbb = new Date(b.createdAt || 0).getTime();

        return dbb - da;
      });

      resolve(scoped);
    };

    req.onerror = () => {
      const error = req.error || new Error('No se pudieron leer ventas offline.');
      db.close();
      reject(error);
    };
  });
}

async function getOfflineSaleById(offlineId) {
  if (!hasIndexedDB || !offlineId) return null;

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readonly');
    const store = tx.objectStore(OFFLINE_STORE_NAME);
    const req = store.get(offlineId);

    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };

    req.onerror = () => {
      const error = req.error || new Error('No se pudo leer la venta offline.');
      db.close();
      reject(error);
    };
  });
}

async function updateOfflineSaleInDB(offlineId, patch = {}) {
  if (!hasIndexedDB || !offlineId) {
    throw new Error('No se indicó venta offline para actualizar.');
  }

  const current = await getOfflineSaleById(offlineId);

  if (!current) {
    throw new Error('No se encontró la venta offline.');
  }

  const updated = {
    ...current,
    ...patch,
    updatedAt: new Date().toISOString(),
  };

  await saveOfflineSaleToDB(updated);

  return updated;
}

async function deleteOfflineSaleFromDB(offlineId) {
  if (!hasIndexedDB || !offlineId) return false;

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(OFFLINE_STORE_NAME);

    store.delete(offlineId);

    tx.oncomplete = () => {
      db.close();
      resolve(true);
    };

    tx.onerror = () => {
      const error = tx.error || new Error('No se pudo eliminar la venta offline.');
      db.close();
      reject(error);
    };
  });
}

async function replacePosRulesCache(reglas = []) {
  if (!hasIndexedDB) return false;

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_RULES_STORE_NAME, 'readwrite');
    const store = tx.objectStore(POS_RULES_STORE_NAME);

    store.clear();

    const now = new Date().toISOString();

    reglas.forEach((regla) => {
      if (!regla?.id) return;

      store.put({
        ...regla,
        id: String(regla.id),
        cachedAt: now,
      });
    });

    tx.oncomplete = () => {
      db.close();
      resolve(true);
    };

    tx.onerror = () => {
      const error = tx.error || new Error('No se pudieron guardar reglas POS.');
      db.close();
      reject(error);
    };
  });
}

async function getPosRulesCache() {
  if (!hasIndexedDB) return [];

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_RULES_STORE_NAME, 'readonly');
    const store = tx.objectStore(POS_RULES_STORE_NAME);
    const req = store.getAll();

    req.onsuccess = () => {
      db.close();

      const rows = Array.isArray(req.result) ? req.result : [];

      resolve(
        rows
          .map((rule) => ({
            ...rule,
            id: String(rule.id),
          }))
          .sort((a, b) => Number(a.prioridad || 100) - Number(b.prioridad || 100))
      );
    };

    req.onerror = () => {
      const error = req.error || new Error('No se pudieron leer reglas POS cacheadas.');
      db.close();
      reject(error);
    };
  });
}

async function clearPosRulesCache() {
  if (!hasIndexedDB) return false;

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_RULES_STORE_NAME, 'readwrite');
    const store = tx.objectStore(POS_RULES_STORE_NAME);

    store.clear();

    tx.oncomplete = () => {
      db.close();
      resolve(true);
    };

    tx.onerror = () => {
      const error = tx.error || new Error('No se pudo limpiar cache de reglas POS.');
      db.close();
      reject(error);
    };
  });
}


function normalizeOfflineLookup(value) {
  return String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function normalizeOfflineSearchText(value) {
  return String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ');
}

function isRecoverablePosConnectivityError(error) {
  if (!error) return true;

  if (!error.response) return true;

  const status = Number(error.response?.status || 0);
  const code = String(error.code || '').toUpperCase();

  if (['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(code)) return true;

  return [
    408, 425, 429,
    500, 502, 503, 504,
    520, 521, 522, 523, 524, 525, 526, 527, 530,
  ].includes(status);
}

function getPosTenantCacheId() {
  return normalizeScopeValue(getCurrentTenantIdFromStorage() || 'sin_tenant');
}

function getPosUserCacheId() {
  const user = getCurrentUserFromStorage();
  return normalizeScopeValue(user.userId || user.userEmail || 'sin_usuario');
}

function makePosConfigCacheKey(name, { userScoped = false } = {}) {
  const tenantId = getPosTenantCacheId();
  const userPart = userScoped ? `:${getPosUserCacheId()}` : '';
  return `${tenantId}${userPart}:${String(name || '').trim().toLowerCase()}`;
}

async function putPosConfigCache(name, value, { userScoped = false } = {}) {
  if (!hasIndexedDB) return false;

  const db = await openOfflineDB();
  const tenantId = getPosTenantCacheId();
  const record = {
    cacheKey: makePosConfigCacheKey(name, { userScoped }),
    tenantId,
    userId: userScoped ? getPosUserCacheId() : null,
    name,
    value,
    cachedAt: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_CONFIG_STORE_NAME, 'readwrite');
    tx.objectStore(POS_CONFIG_STORE_NAME).put(record);
    tx.oncomplete = () => {
      db.close();
      resolve(true);
    };
    tx.onerror = () => {
      const error = tx.error || new Error('No se pudo guardar configuración POS local.');
      db.close();
      reject(error);
    };
  });
}

async function getPosConfigCache(name, { userScoped = false } = {}) {
  if (!hasIndexedDB) return null;

  const db = await openOfflineDB();
  const cacheKey = makePosConfigCacheKey(name, { userScoped });

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_CONFIG_STORE_NAME, 'readonly');
    const req = tx.objectStore(POS_CONFIG_STORE_NAME).get(cacheKey);

    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      const error = req.error || new Error('No se pudo leer configuración POS local.');
      db.close();
      reject(error);
    };
  });
}

const posOfflineProductSearchMemory = {
  tenantId: null,
  rows: null,
  loadingPromise: null,
};

function invalidatePosOfflineProductSearchMemory() {
  posOfflineProductSearchMemory.tenantId = null;
  posOfflineProductSearchMemory.rows = null;
  posOfflineProductSearchMemory.loadingPromise = null;
}

async function getCachedPosProductExact(term) {
  if (!hasIndexedDB) return null;

  const qLookup = normalizeOfflineLookup(term);
  if (!qLookup) return null;

  const db = await openOfflineDB();
  const tenantId = getPosTenantCacheId();
  const storeName = POS_PRODUCTS_STORE_NAME;

  return new Promise((resolve) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const barcodeReq = store.index('by_tenant_barcode').get([tenantId, qLookup]);

    const finish = (row) => {
      try { db.close(); } catch (_) {}
      resolve(row || null);
    };

    barcodeReq.onsuccess = () => {
      if (barcodeReq.result) {
        finish(barcodeReq.result);
        return;
      }

      const skuReq = store.index('by_tenant_sku').get([tenantId, qLookup]);
      skuReq.onsuccess = () => finish(skuReq.result || null);
      skuReq.onerror = () => finish(null);
    };

    barcodeReq.onerror = () => finish(null);
  });
}

async function getCachedPosProductsForOfflineSearch() {
  if (!hasIndexedDB) return [];

  const tenantId = getPosTenantCacheId();

  if (
    posOfflineProductSearchMemory.tenantId === tenantId &&
    Array.isArray(posOfflineProductSearchMemory.rows)
  ) {
    return posOfflineProductSearchMemory.rows;
  }

  if (
    posOfflineProductSearchMemory.tenantId === tenantId &&
    posOfflineProductSearchMemory.loadingPromise
  ) {
    return posOfflineProductSearchMemory.loadingPromise;
  }

  posOfflineProductSearchMemory.tenantId = tenantId;
  posOfflineProductSearchMemory.rows = null;

  const loadingPromise = (async () => {
    const db = await openOfflineDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(POS_PRODUCTS_STORE_NAME, 'readonly');
      const req = tx.objectStore(POS_PRODUCTS_STORE_NAME)
        .index('by_tenant')
        .getAll(window.IDBKeyRange.only(tenantId));

      req.onsuccess = () => {
        const rows = Array.isArray(req.result) ? req.result : [];
        try { db.close(); } catch (_) {}
        resolve(rows);
      };

      req.onerror = () => {
        const error = req.error || new Error('No se pudo preparar índice local de productos.');
        try { db.close(); } catch (_) {}
        reject(error);
      };
    });
  })();

  posOfflineProductSearchMemory.loadingPromise = loadingPromise;

  try {
    const rows = await loadingPromise;
    posOfflineProductSearchMemory.rows = rows;
    return rows;
  } finally {
    posOfflineProductSearchMemory.loadingPromise = null;
  }
}

function buildCachedProductRecord(product = {}, generation = null) {
  const tenantId = getPosTenantCacheId();
  const productId = Number(product.id ?? product.id_producto ?? product.product_id ?? 0);
  if (!Number.isInteger(productId) || productId <= 0) return null;

  const sku = String(product.sku || product.codigo_sku || '').trim();
  const barcode = String(getProductBarcode(product) || '').trim();
  const name = String(product.nombre || product.name || product.descripcion || '').trim();
  const categories = Array.isArray(product.categorias)
    ? product.categorias
    : Array.isArray(product.categories)
      ? product.categories
      : [];
  const equivalences = Array.isArray(product.equivalencias)
    ? product.equivalencias
    : Array.isArray(product.equivalences)
      ? product.equivalences
      : [];
  const equivalenceCodes = equivalences
    .flatMap((row) => [row?.codigo, row?.codigo_normalizado])
    .filter(Boolean);

  return {
    ...product,
    cacheKey: `${tenantId}:${productId}`,
    tenantId,
    productId,
    id: productId,
    sku,
    codigo_barras: product.codigo_barras || barcode || null,
    skuNorm: normalizeOfflineLookup(sku),
    barcodeNorm: normalizeOfflineLookup(barcode),
    searchText: normalizeOfflineSearchText(
      [sku, barcode, name, ...equivalenceCodes].filter(Boolean).join(' ')
    ),
    equivalencias: equivalences,
    equivalenceNorms: equivalenceCodes.map(normalizeOfflineLookup).filter(Boolean),
    categorias: categories,
    generation: generation || product.generation || null,
    cachedAt: new Date().toISOString(),
  };
}

async function cachePosProductsPage(products = [], { generation = null } = {}) {
  if (!hasIndexedDB) return 0;

  const rows = (Array.isArray(products) ? products : [])
    .map((product) => buildCachedProductRecord(product, generation))
    .filter(Boolean);

  if (!rows.length) return 0;

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_PRODUCTS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(POS_PRODUCTS_STORE_NAME);
    let changed = 0;

    rows.forEach((row) => {
      if (row.deleted === true) {
        store.delete(row.cacheKey);
      } else {
        store.put(row);
      }
      changed += 1;
    });

    tx.oncomplete = () => {
      db.close();
      invalidatePosOfflineProductSearchMemory();
      resolve(changed);
    };
    tx.onerror = () => {
      const error = tx.error || new Error('No se pudo actualizar catálogo POS local.');
      db.close();
      reject(error);
    };
  });
}

async function finalizePosProductsGeneration(generation) {
  if (!hasIndexedDB || !generation) return 0;

  const db = await openOfflineDB();
  const tenantId = getPosTenantCacheId();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_PRODUCTS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(POS_PRODUCTS_STORE_NAME);
    const index = store.index('by_tenant');
    const range = window.IDBKeyRange.only(tenantId);
    const req = index.openCursor(range);
    let deleted = 0;

    req.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;

      if (cursor.value?.generation !== generation) {
        cursor.delete();
        deleted += 1;
      }
      cursor.continue();
    };

    tx.oncomplete = () => {
      db.close();
      invalidatePosOfflineProductSearchMemory();
      resolve(deleted);
    };
    tx.onerror = () => {
      const error = tx.error || new Error('No se pudo finalizar catálogo POS local.');
      db.close();
      reject(error);
    };
  });
}

async function getCachedPosProductCount() {
  if (!hasIndexedDB) return 0;

  const db = await openOfflineDB();
  const tenantId = getPosTenantCacheId();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_PRODUCTS_STORE_NAME, 'readonly');
    const req = tx.objectStore(POS_PRODUCTS_STORE_NAME)
      .index('by_tenant')
      .count(window.IDBKeyRange.only(tenantId));

    req.onsuccess = () => {
      db.close();
      resolve(Number(req.result || 0));
    };
    req.onerror = () => {
      const error = req.error || new Error('No se pudo contar catálogo POS local.');
      db.close();
      reject(error);
    };
  });
}

async function getCachedPosProductById(productId) {
  if (!hasIndexedDB) return null;

  const id = Number(productId || 0);
  if (!Number.isInteger(id) || id <= 0) return null;

  const db = await openOfflineDB();
  const tenantId = getPosTenantCacheId();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_PRODUCTS_STORE_NAME, 'readonly');
    const req = tx.objectStore(POS_PRODUCTS_STORE_NAME)
      .index('by_tenant_id')
      .get([tenantId, id]);

    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      const error = req.error || new Error('No se pudo leer producto POS local.');
      db.close();
      reject(error);
    };
  });
}

async function searchCachedPosProducts(term, limit = 30) {
  if (!hasIndexedDB) return [];

  const qRaw = String(term || '').trim();
  const qLookup = normalizeOfflineLookup(qRaw);
  const qText = normalizeOfflineSearchText(qRaw);
  if (!qRaw) return [];

  // SKU / código de barras exacto: usa índices de IndexedDB, sin recorrer catálogo.
  const exact = await getCachedPosProductExact(qRaw);
  if (exact) return [exact];

  // Las búsquedas amplias solo llegan aquí en contingencia. La primera carga
  // trae el catálogo del tenant a memoria; las siguientes búsquedas filtran RAM
  // en lugar de abrir un cursor sobre ~80k productos por cada tecla.
  const rows = await getCachedPosProductsForOfflineSearch();
  const candidates = [];

  for (const row of rows) {
    const skuNorm = row?.skuNorm || '';
    const barcodeNorm = row?.barcodeNorm || '';
    const text = row?.searchText || '';
    const equivalenceNorms = Array.isArray(row?.equivalenceNorms) ? row.equivalenceNorms : [];

    let score = 999;
    if (qLookup && equivalenceNorms.includes(qLookup)) score = 2;
    else if (qLookup && skuNorm.startsWith(qLookup)) score = 3;
    else if (qLookup && barcodeNorm.startsWith(qLookup)) score = 4;
    else if (qLookup && equivalenceNorms.some((code) => code.startsWith(qLookup))) score = 5;
    else if (qText && text.includes(qText)) score = 6;
    else if (qLookup && (skuNorm.includes(qLookup) || barcodeNorm.includes(qLookup))) score = 7;

    if (score < 999) candidates.push({ row, score });
  }

  candidates.sort(
    (a, b) => a.score - b.score || Number(b.row?.productId || 0) - Number(a.row?.productId || 0)
  );

  return candidates
    .slice(0, Math.max(1, Number(limit || 30)))
    .map((entry) => entry.row);
}

async function cachePosCustomers(customers = []) {
  if (!hasIndexedDB) return 0;

  const tenantId = getPosTenantCacheId();
  const rows = (Array.isArray(customers) ? customers : [])
    .filter((customer) => customer && Number(customer.id || customer.id_cliente || 0) > 0)
    .map((customer) => {
      const id = Number(customer.id || customer.id_cliente);
      const rut = String(customer.rut || '').trim();
      const name = [
        customer.nombres,
        customer.apellidos,
        customer.razon_social,
        customer.email,
        customer.telefono,
      ].filter(Boolean).join(' ');

      return {
        ...customer,
        id,
        cacheKey: `${tenantId}:${id}`,
        tenantId,
        rutNorm: normalizeOfflineLookup(rut),
        searchText: normalizeOfflineSearchText(`${rut} ${name}`),
        cachedAt: new Date().toISOString(),
      };
    });

  if (!rows.length) return 0;

  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_CUSTOMERS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(POS_CUSTOMERS_STORE_NAME);
    rows.forEach((row) => store.put(row));
    tx.oncomplete = () => {
      db.close();
      resolve(rows.length);
    };
    tx.onerror = () => {
      const error = tx.error || new Error('No se pudieron guardar clientes POS locales.');
      db.close();
      reject(error);
    };
  });
}

async function searchCachedPosCustomers(term, limit = 30) {
  if (!hasIndexedDB) return [];

  const qRaw = String(term || '').trim();
  const qLookup = normalizeOfflineLookup(qRaw);
  const qText = normalizeOfflineSearchText(qRaw);
  if (!qRaw) return [];

  const db = await openOfflineDB();
  const tenantId = getPosTenantCacheId();

  const exactRut = await new Promise((resolve) => {
    if (!qLookup) return resolve(null);
    const tx = db.transaction(POS_CUSTOMERS_STORE_NAME, 'readonly');
    const req = tx.objectStore(POS_CUSTOMERS_STORE_NAME)
      .index('by_tenant_rut')
      .get([tenantId, qLookup]);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });

  if (exactRut) {
    db.close();
    return [exactRut];
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(POS_CUSTOMERS_STORE_NAME, 'readonly');
    const req = tx.objectStore(POS_CUSTOMERS_STORE_NAME)
      .index('by_tenant')
      .openCursor(window.IDBKeyRange.only(tenantId));
    const rows = [];

    req.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;
      const row = cursor.value || {};
      const text = row.searchText || '';
      const rutNorm = row.rutNorm || '';
      if (
        (qText && text.includes(qText)) ||
        (qLookup && rutNorm.includes(qLookup))
      ) {
        rows.push(row);
      }
      cursor.continue();
    };

    tx.oncomplete = () => {
      db.close();
      resolve(rows.slice(0, Math.max(1, Number(limit || 30))));
    };
    tx.onerror = () => {
      const error = tx.error || new Error('No se pudieron buscar clientes POS locales.');
      db.close();
      reject(error);
    };
  });
}

function createClientSaleId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback UUID v4 para navegadores antiguos.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

function createOfflineId() {
  const now = new Date();
  const rand =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `OFF-${now.toISOString().replace(/[-:.TZ]/g, '').slice(0, 14)}-${rand}`;
}

function buildOfflineSaleRecord({
  payload,
  itemsSnapshot,
  totals,
  payment,
  customer,
  documentType,
  sourceRef,
  rulesSnapshot,
  printerConfig,
}) {
  const scope = getCurrentPosScope();
  const offlineId = createOfflineId();
  const createdAt = new Date().toISOString();

  return {
    offlineId,
    createdAt,
    updatedAt: createdAt,

    tenantId: scope.tenantId,
    userId: scope.userId,
    userEmail: scope.userEmail,
    userName: scope.userName,

    synced: false,
    syncing: false,
    syncError: null,
    syncedAt: null,
    serverData: null,

    payload,
    itemsSnapshot: Array.isArray(itemsSnapshot) ? itemsSnapshot : [],
    totals: totals || {},
    payment: payment || {},
    customer: customer || null,
    documentType: documentType || payload?.document_type || payload?.tipo_documento || 'boleta',
    sourceRef: sourceRef || payload?.source_ref || null,
    rulesSnapshot: rulesSnapshot || null,
    printerConfig: printerConfig || null,

    metadata: {
      origin: 'POS_CONTINGENCY',
      app: 'IntegraNubePOS',
      version: 2,
    },
  };
}

function normalizeOfflineSaleForSync(record = {}) {
  const payload = record.payload && typeof record.payload === 'object' ? record.payload : {};

  return {
    ...payload,
    offline: true,
    offlineId: record.offlineId,
    offline_id: record.offlineId,
    offline_created_at: record.createdAt,
    offline_scope: {
      tenantId: record.tenantId || null,
      userId: record.userId || null,
      userEmail: record.userEmail || null,
      userName: record.userName || null,
    },
    contingencia: {
      offlineId: record.offlineId,
      createdAt: record.createdAt,
      rulesSnapshot: record.rulesSnapshot || null,
      totals: record.totals || null,
      payment: record.payment || null,
    },
  };
}

function getMovementVoucherValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }

  return '—';
}

function buildCashMovementVoucherHtml(movimiento = {}, caja = null) {
  const tipoRaw = String(
    movimiento.tipo ||
      movimiento.tipo_movimiento ||
      movimiento.movimiento_tipo ||
      ''
  ).toUpperCase();

  const tipoLabel =
    tipoRaw === 'EGRESO'
      ? 'EGRESO DE CAJA'
      : tipoRaw === 'INGRESO'
        ? 'INGRESO DE CAJA'
        : 'MOVIMIENTO DE CAJA';

  const monto = Number(
    movimiento.monto ||
      movimiento.amount ||
      movimiento.valor ||
      0
  );

  const fecha = movimiento.created_at ||
    movimiento.fecha ||
    movimiento.fecha_movimiento ||
    movimiento.createdAt ||
    new Date().toISOString();

  const cajeroNombre = getMovementVoucherValue(
    movimiento.cajero_nombre,
    movimiento.usuario_nombre,
    movimiento.user_name,
    movimiento.usuario?.nombre,
    movimiento.usuario?.name,
    getCurrentUserFromStorage()?.userName
  );

  const cajeroEmail = getMovementVoucherValue(
    movimiento.cajero_email,
    movimiento.usuario_email,
    movimiento.user_email,
    movimiento.usuario?.email,
    getCurrentUserFromStorage()?.userEmail
  );

  const folio = getMovementVoucherValue(
    movimiento.folio,
    movimiento.numero,
    movimiento.codigo,
    movimiento.id_movimiento,
    movimiento.id
  );

  const motivo = getMovementVoucherValue(
    movimiento.motivo,
    movimiento.concepto,
    movimiento.categoria,
    movimiento.glosa
  );

  const observacion = getMovementVoucherValue(
    movimiento.observacion,
    movimiento.observaciones,
    movimiento.descripcion,
    movimiento.detalle
  );

  const cajaNombre = getMovementVoucherValue(
    movimiento.caja_nombre,
    caja?.nombre,
    caja?.descripcion,
    caja?.codigo,
    caja?.id ? `Caja #${caja.id}` : null
  );

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${tipoLabel}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            margin: 0;
            padding: 12px;
            color: #111;
          }

          .ticket {
            width: 280px;
            margin: 0 auto;
          }

          .center {
            text-align: center;
          }

          .brand {
            font-weight: 900;
            font-size: 14px;
            letter-spacing: .5px;
          }

          .doc-title {
            margin-top: 4px;
            font-weight: 900;
            font-size: 13px;
          }

          .muted {
            color: #555;
            font-size: 10px;
          }

          .box {
            border: 1px dashed #111;
            padding: 8px;
            margin: 10px 0;
            text-align: center;
            font-weight: 900;
          }

          .section {
            border-top: 1px dashed #aaa;
            margin-top: 8px;
            padding-top: 8px;
          }

          .line {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            margin: 4px 0;
          }

          .line span {
            color: #444;
          }

          .line strong {
            text-align: right;
          }

          .amount {
            font-size: 18px;
            font-weight: 900;
          }

          .obs {
            margin-top: 6px;
            font-size: 11px;
            white-space: pre-wrap;
          }

          .signature {
            margin-top: 24px;
            border-top: 1px solid #111;
            text-align: center;
            padding-top: 4px;
            font-size: 10px;
          }

          @media print {
            body {
              padding: 0;
            }

            .ticket {
              width: 100%;
            }
          }
        </style>
      </head>

      <body>
        <div class="ticket">
          <div class="center">
            <div class="brand">INTEGRANUBE POS</div>
            <div class="doc-title">COMPROBANTE INTERNO DE CAJA</div>
            <div class="muted">${new Date(fecha).toLocaleString('es-CL')}</div>
          </div>

          <div class="box">
            ${tipoLabel}
          </div>

          <div class="section">
            <div class="line">
              <span>Folio</span>
              <strong>${folio}</strong>
            </div>
            <div class="line">
              <span>Caja</span>
              <strong>${cajaNombre}</strong>
            </div>
            <div class="line">
              <span>Cajero</span>
              <strong>${cajeroNombre}</strong>
            </div>
            <div class="line">
              <span>Usuario</span>
            </div>
          </div>

          <div class="section center">
            <div class="muted">Monto</div>
            <div class="amount">${formatter.format(monto)}</div>
          </div>

          <div class="section">
            <div class="line">
              <span>Motivo</span>
              <strong>${motivo}</strong>
            </div>
            <div class="obs">
              <strong>Observación:</strong><br/>
              ${observacion}
            </div>
          </div>

          <div class="section">
            <div class="signature">Firma cajero</div>
            <div class="signature">Firma supervisor / encargado</div>
          </div>

          <div class="section center">
            <div class="muted">
              Este comprobante es de uso interno.<br/>
              No reemplaza documento tributario.
            </div>
          </div>
        </div>

        <script>
          window.onload = function () {
            setTimeout(function () {
              window.print();
            }, 300);
          };
        </script>
      </body>
    </html>
  `;
}

function printCashMovementVoucher(movimiento = {}, caja = null) {
  const html = buildCashMovementVoucherHtml(movimiento, caja);
  const win = window.open('', '_blank', 'width=420,height=700');

  if (!win) {
    throw new Error('No se pudo abrir la ventana de impresión. Revise el bloqueo de popups.');
  }

  win.document.open();
  win.document.write(html);
  win.document.close();

  return true;
}

function getVentaIdFromServerData(serverData) {
  if (!serverData) return null;

  return (
    serverData.venta_id ||
    serverData.id_venta ||
    serverData.venta?.id ||
    serverData.venta?.id_venta ||
    serverData.data?.venta_id ||
    serverData.data?.id_venta ||
    serverData.data?.venta?.id ||
    null
  );
}

function getDteInfoFromServerData(serverData) {
  if (!serverData) {
    return {
      folio: null,
      track_id: null,
      estado_sii: null,
      tipo_dte: null,
    };
  }

  const dte =
    serverData.dte ||
    serverData.documento ||
    serverData.data?.dte ||
    serverData.data?.documento ||
    serverData.venta?.dte ||
    serverData.data?.venta?.dte ||
    {};

  return {
    folio:
      dte.folio ||
      serverData.folio ||
      serverData.data?.folio ||
      serverData.venta?.folio ||
      null,

    track_id:
      dte.track_id ||
      dte.trackId ||
      serverData.track_id ||
      serverData.trackId ||
      serverData.data?.track_id ||
      null,

    estado_sii:
      dte.estado_sii ||
      dte.estadoSii ||
      serverData.estado_sii ||
      serverData.data?.estado_sii ||
      null,

    tipo_dte:
      dte.tipo_dte ||
      dte.tipoDte ||
      serverData.tipo_dte ||
      serverData.data?.tipo_dte ||
      null,
  };
}

function getPrintableCustomerName(customer) {
  if (!customer) return 'Cliente genérico';

  return (
    customer.razon_social ||
    customer.fullname ||
    customer.nombre ||
    customer.name ||
    `${customer.nombres || ''} ${customer.apellidos || ''}`.trim() ||
    'Cliente'
  );
}

function getPrintableCustomerRut(customer) {
  if (!customer) return GENERIC_RUT;

  return customer.rut || customer.rut_cliente || customer.documento || GENERIC_RUT;
}

function getVariablePriceGroupKey(item = {}) {
  if (!parseBoolish(item?.permite_precio_variable ?? item?.raw?.permite_precio_variable, false)) {
    return null;
  }

  const productId =
    item.product_id ??
    item.id_producto ??
    item.producto_id ??
    item.productId ??
    item.id ??
    null;

  if (productId != null && String(productId).trim() !== '') {
    return `producto:${String(productId).trim()}`;
  }

  const sku = String(item.sku || '').trim();
  if (sku) return `sku:${sku}`;

  const barcode = String(item.barcode || item.codigo_barras || item.codigo_barra || '').trim();
  if (barcode) return `barcode:${barcode}`;

  return null;
}

function getVariablePriceBaseName(item = {}) {
  return (
    item.precio_variable_nombre_base ||
    item.variablePriceBaseName ||
    item.raw?.nombre ||
    item.raw?.name ||
    item.nombre ||
    item.name ||
    item.descripcion ||
    item.sku ||
    'PRODUCTO GENÉRICO'
  );
}

/**
 * Numera únicamente las líneas repetidas de un mismo producto de precio variable.
 *
 * 1 línea  -> PRODUCTO GENÉRICO
 * 2 líneas -> PRODUCTO GENÉRICO 1 / PRODUCTO GENÉRICO 2
 * 3 líneas -> PRODUCTO GENÉRICO 1 / 2 / 3
 *
 * El nombre maestro nunca se modifica: la numeración vive en descripcion_linea.
 * precio_variable_orden conserva el orden real de ingreso aunque el carrito muestre
 * las líneas nuevas primero. Si se elimina una línea, la numeración se compacta.
 */
function relabelVariablePriceCartItems(list = []) {
  const source = Array.isArray(list) ? list : [];
  const working = source.map((item) => ({ ...item }));
  const groups = new Map();

  working.forEach((item, index) => {
    const key = getVariablePriceGroupKey(item);
    if (!key) return;

    const bucket = groups.get(key) || [];
    bucket.push({ index, item });
    groups.set(key, bucket);
  });

  groups.forEach((bucket) => {
    // Para borradores antiguos sin orden explícito, la línea más antigua suele
    // estar más abajo porque las nuevas se insertan al inicio del carrito.
    let maxOrder = bucket.reduce((max, row) => {
      const n = Number(row.item.precio_variable_orden ?? row.item.variablePriceOrder ?? 0);
      return Number.isFinite(n) && n > max ? n : max;
    }, 0);

    const missing = bucket
      .filter((row) => {
        const n = Number(row.item.precio_variable_orden ?? row.item.variablePriceOrder ?? 0);
        return !(Number.isFinite(n) && n > 0);
      })
      .sort((a, b) => b.index - a.index);

    missing.forEach((row) => {
      maxOrder += 1;
      row.item.precio_variable_orden = maxOrder;
      row.item.variablePriceOrder = maxOrder;
    });

    const ordered = [...bucket].sort((a, b) => {
      const orderA = Number(a.item.precio_variable_orden ?? a.item.variablePriceOrder ?? 0);
      const orderB = Number(b.item.precio_variable_orden ?? b.item.variablePriceOrder ?? 0);
      if (orderA !== orderB) return orderA - orderB;
      return b.index - a.index;
    });

    ordered.forEach((row, rank) => {
      const current = working[row.index];
      const baseName = String(getVariablePriceBaseName(current)).trim() || 'PRODUCTO GENÉRICO';
      const displayName = ordered.length > 1 ? `${baseName} ${rank + 1}` : baseName;

      working[row.index] = {
        ...current,
        precio_variable_nombre_base: baseName,
        variablePriceBaseName: baseName,
        precio_variable_orden:
          Number(row.item.precio_variable_orden ?? row.item.variablePriceOrder ?? rank + 1) || rank + 1,
        variablePriceOrder:
          Number(row.item.precio_variable_orden ?? row.item.variablePriceOrder ?? rank + 1) || rank + 1,
        descripcion_linea: displayName,
        nombre_linea: displayName,
      };
    });
  });

  return working;
}

function getItemDisplayName(item = {}) {
  return (
    item.descripcion_linea ||
    item.nombre_linea ||
    item.name ||
    item.nombre ||
    item.descripcion ||
    item.sku ||
    'Producto'
  );
}

function getItemNameForTicket(item) {
  return getItemDisplayName(item);
}

function buildContingencyTicketHtml(record = {}) {
  const items = Array.isArray(record.itemsSnapshot) ? record.itemsSnapshot : [];
  const totals = record.totals || {};
  const rulesSnapshot = record.rulesSnapshot || {};
  const ajustes = Array.isArray(rulesSnapshot.ajustes) ? rulesSnapshot.ajustes : [];
  const customer = record.customer || {};
  const payment = record.payment || {};
  const paymentLines = Array.isArray(payment.payments)
    ? payment.payments
    : Array.isArray(payment.pagos)
      ? payment.pagos
      : [];

  const paymentLabel =
    payment.mixed || payment.esPagoMixto || paymentLines.length > 1
      ? 'Pago mixto'
      : payment.label || payment.method || payment.metodo_pago || paymentLines[0]?.label || '—';

  const paymentDetailsHtml = paymentLines.length
    ? `
      <div class="section">
        <div class="title">Detalle de pago</div>
        ${paymentLines
          .map((pago) => {
            const monto = Number(
              pago.amountPaid ??
              pago.amount_paid ??
              pago.montoBruto ??
              pago.monto ??
              0
            ) || 0;

            const ref =
              pago.voucherNumber ||
              pago.voucher_number ||
              pago.referencia ||
              pago.reference ||
              null;

            return `
              <div class="line">
                <span>${pago.label || pago.methodLabel || pago.methodKey || 'Pago'}</span>
                <strong>${formatter.format(monto)}</strong>
              </div>
              ${ref ? `<div class="muted">Ref: ${ref}</div>` : ''}
            `;
          })
          .join('')}
      </div>
    `
    : '';

  const totalOriginal =
    Number(totals.baseTotalGross ?? rulesSnapshot.resumen?.total_original_bruto ?? 0) || 0;

  const totalAjustes =
    Number(totals.totalRulesAdjustment ?? rulesSnapshot.resumen?.total_ajustes_bruto ?? 0) || 0;

  const totalFinal =
    Number(
      totals.totalPayable ??
        totals.totalGross ??
        rulesSnapshot.resumen?.total_final_bruto ??
        totalOriginal + totalAjustes
    ) || 0;

  const rowsHtml = items
    .map((item) => {
      const qty = Number(item.quantity ?? item.cantidad ?? 1);
      const unit = getItemGrossUnit(item);
      const line = getItemGrossLine(item);

      return `
        <tr>
          <td>
            <div class="name">${getItemNameForTicket(item)}</div>
            <div class="muted">SKU: ${item.sku || '—'}</div>
          </td>
          <td class="right">${qty}</td>
          <td class="right">${formatter.format(unit)}</td>
          <td class="right">${formatter.format(line)}</td>
        </tr>
      `;
    })
    .join('');

  const ajustesHtml = ajustes.length
    ? `
      <div class="section">
        <div class="title">Reglas / ajustes POS</div>
        ${ajustes
          .map(
            (ajuste) => `
              <div class="line">
                <span>${ajuste.glosa || ajuste.nombre || ajuste.codigo || 'Ajuste'}</span>
                <strong>${formatter.format(ajuste.monto || 0)}</strong>
              </div>
            `
          )
          .join('')}
      </div>
    `
    : '';

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Comprobante interno de contingencia</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            margin: 0;
            padding: 12px;
            color: #111;
          }

          .ticket {
            width: 280px;
            margin: 0 auto;
          }

          .center {
            text-align: center;
          }

          .title {
            font-weight: 800;
            font-size: 13px;
            margin-bottom: 4px;
          }

          .subtitle {
            font-size: 11px;
            color: #333;
          }

          .warning {
            border: 1px dashed #111;
            padding: 8px;
            margin: 10px 0;
            text-align: center;
            font-weight: 800;
          }

          .section {
            border-top: 1px dashed #aaa;
            margin-top: 8px;
            padding-top: 8px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th, td {
            padding: 3px 0;
            vertical-align: top;
          }

          th {
            border-bottom: 1px solid #ddd;
            font-size: 10px;
          }

          .right {
            text-align: right;
          }

          .name {
            font-weight: 700;
          }

          .muted {
            color: #666;
            font-size: 10px;
          }

          .line {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            margin: 3px 0;
          }

          .total {
            font-size: 14px;
            font-weight: 900;
          }

          @media print {
            body {
              padding: 0;
            }

            .ticket {
              width: 100%;
            }
          }
        </style>
      </head>

      <body>
        <div class="ticket">
          <div class="center">
            <div class="title">INTEGRANUBE POS</div>
            <div class="subtitle">Comprobante interno de contingencia</div>
            <div class="subtitle">${new Date(record.createdAt || Date.now()).toLocaleString('es-CL')}</div>
          </div>

          <div class="warning">
            DOCUMENTO INTERNO DE CONTINGENCIA<br/>
            El DTE será emitido al reconectar.
          </div>

          <div class="section">
            <div class="line">
              <span>Folio contingencia</span>
              <strong>${record.offlineId || '—'}</strong>
            </div>
            <div class="line">
              <span>Cliente</span>
              <strong>${getPrintableCustomerName(customer)}</strong>
            </div>
            <div class="line">
              <span>RUT</span>
              <strong>${getPrintableCustomerRut(customer)}</strong>
            </div>
            <div class="line">
              <span>Documento</span>
              <strong>${String(record.documentType || 'boleta').toUpperCase()}</strong>
            </div>
            <div class="line">
              <span>Pago</span>
              <strong>${paymentLabel}</strong>
            </div>
          </div>

          ${paymentDetailsHtml}

          <div class="section">
            <table>
              <thead>
                <tr>
                  <th align="left">Producto</th>
                  <th class="right">Cant.</th>
                  <th class="right">Unit.</th>
                  <th class="right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </div>

          ${ajustesHtml}

          <div class="section">
            <div class="line">
              <span>Total original</span>
              <strong>${formatter.format(totalOriginal)}</strong>
            </div>
            <div class="line">
              <span>Ajustes</span>
              <strong>${formatter.format(totalAjustes)}</strong>
            </div>
            <div class="line total">
              <span>Total pagado</span>
              <strong>${formatter.format(totalFinal)}</strong>
            </div>
          </div>

          <div class="section center">
            <div class="subtitle">
              Este comprobante no reemplaza el DTE oficial.<br/>
              Será regularizado automáticamente al volver la conexión.
            </div>
          </div>
        </div>

        <script>
          window.onload = function () {
            setTimeout(function () {
              window.print();
            }, 300);
          };
        </script>
      </body>
    </html>
  `;
}

function printContingencyTicket(record = {}) {
  const html = buildContingencyTicketHtml(record);
  const win = window.open('', '_blank', 'width=420,height=700');

  if (!win) {
    throw new Error('No se pudo abrir la ventana de impresión. Revisa el bloqueo de popups.');
  }

  win.document.open();
  win.document.write(html);
  win.document.close();

  return true;
}

async function syncOfflineSaleRecord(record) {
  if (!record?.offlineId) {
    throw new Error('Venta offline inválida.');
  }

  const payload = normalizeOfflineSaleForSync(record);

  const { data } = await api.post('/ventas_pos/offline', payload, {
    timeout: POS_SALE_API_TIMEOUT_MS,
  });

  const result = Array.isArray(data?.results) && data.results.length === 1
    ? data.results[0]
    : data;

  if (result?.ok === false) {
    const error = new Error(result.message || 'La API rechazó la venta offline.');
    error.code = result.error || 'OFFLINE_SYNC_REJECTED';
    error.response = { status: 409, data: result };
    throw error;
  }

  const dteInfo = getDteInfoFromServerData(result);
  const ventaId = getVentaIdFromServerData(result);

  const updated = await updateOfflineSaleInDB(record.offlineId, {
    synced: true,
    syncing: false,
    syncError: null,
    syncErrorCode: null,
    syncErrorStatus: null,
    syncAutoRetryEligible: null,
    syncRequiresReview: false,
    syncedAt: new Date().toISOString(),
    serverData: result,
    ventaId,
    dteInfo,
  });

  return updated;
}

function getOfflineSyncErrorPolicy(error) {
  const rawStatus = Number(error?.response?.status || 0);
  const status = Number.isFinite(rawStatus) && rawStatus > 0 ? rawStatus : null;
  const code =
    error?.code ||
    error?.response?.data?.error ||
    error?.response?.data?.code ||
    'OFFLINE_SYNC_ERROR';

  const retryable =
    isRecoverablePosConnectivityError(error) ||
    !error?.response ||
    status === 408 ||
    status === 425 ||
    status === 429 ||
    (status != null && status >= 500 && status <= 599);

  return {
    code: String(code || 'OFFLINE_SYNC_ERROR'),
    status,
    retryable,
    requiresReview: !retryable,
  };
}

function isOfflineSaleAutoRetryEligible(record) {
  if (!record || record.synced === true) return false;

  if (record.syncRequiresReview === true) return false;
  if (record.syncAutoRetryEligible === false) return false;
  if (record.syncAutoRetryEligible === true) return true;

  // Registros antiguos con error no tienen clasificación. No los reintentamos
  // automáticamente para evitar loops sobre errores funcionales históricos.
  // Las ventas nuevas sin error sí son elegibles inmediatamente.
  return !record.syncError;
}

async function markOfflineSaleSyncError(record, error) {
  if (!record?.offlineId) return null;

  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'No se pudo sincronizar la venta offline.';

  const policy = getOfflineSyncErrorPolicy(error);

  return updateOfflineSaleInDB(record.offlineId, {
    synced: false,
    syncing: false,
    syncError: message,
    syncErrorCode: policy.code,
    syncErrorStatus: policy.status,
    syncAutoRetryEligible: policy.retryable,
    syncRequiresReview: policy.requiresReview,
    lastSyncAttemptAt: new Date().toISOString(),
  });
}

function getRulesSourceLabel({ isOnline, posRulesLastSync }) {
  if (isOnline && posRulesLastSync) return 'POS_CACHE_BACKEND';
  if (!isOnline && posRulesLastSync) return 'INDEXEDDB_CACHE';
  if (!isOnline) return 'INDEXEDDB_CACHE';

  return 'POS_CACHE';
}

function buildRulesSnapshot({
  isOnline,
  posRules,
  posRulesLastSync,
  posRulesEvaluation,
  authorizedRuleIds,
  baseTotalGross,
  totalRulesAdjustment,
  totalPayable,
}) {
  return {
    source: getRulesSourceLabel({ isOnline, posRulesLastSync }),
    rules_last_sync: posRulesLastSync || null,
    reglas_cache_count: Array.isArray(posRules) ? posRules.length : 0,
    reglas_aplicadas: posRulesEvaluation?.reglasAplicadas || [],
    reglas_pendientes_pin: posRulesEvaluation?.reglasPendientesPin || [],
    ajustes: posRulesEvaluation?.ajustes || [],
    autorizaciones: authorizedRuleIds || {},
    resumen: {
      total_original_bruto: Math.round(Number(baseTotalGross || 0)),
      total_ajustes_bruto: Math.round(Number(totalRulesAdjustment || 0)),
      total_final_bruto: Math.round(Number(totalPayable || 0)),
    },
  };
}

function buildPaymentSnapshot({
  selectedPaymentMethodConfig,
  selectedPaymentMethod,
  amountReceived,
  voucherNumber,
  change,
  importedPaymentLabel,
  payments = null,
}) {
  const pagos = Array.isArray(payments)
    ? payments.filter((pago) => pago && typeof pago === 'object')
    : [];

  if (pagos.length) {
    const normalizedPayments = pagos.map((pago, index) => ({
      id:
        pago.payment_method_id ||
        pago.metodo_pago_id ||
        pago.payment_relation_id ||
        index + 1,
      relationId: pago.payment_relation_id || pago.relationId || null,
      methodId: pago.payment_method_id || pago.methodId || null,
      key:
        pago.payment_method_key ||
        pago.metodo_pago_clave ||
        pago.key ||
        null,
      clave:
        pago.payment_method_key ||
        pago.metodo_pago_clave ||
        pago.key ||
        null,
      label:
        pago.payment_method_name ||
        pago.metodo_pago_nombre ||
        pago.label ||
        pago.payment_method_key ||
        `Pago ${index + 1}`,
      amountApplied: Number(pago.amount_applied ?? pago.monto_aplicado ?? 0),
      amountPaid: Number(pago.amount_paid ?? pago.monto_pagado ?? 0),
      amountReceived: Number(pago.amount_received ?? pago.monto_recibido ?? 0),
      voucherNumber:
        pago.voucher_number ||
        pago.reference ||
        pago.referencia ||
        null,
      change: Number(pago.change_given ?? pago.vuelto ?? 0),
      cashRounding: Number(pago.cash_rounding ?? 0),
    }));

    return {
      mixed: normalizedPayments.length > 1,
      esPagoMixto: normalizedPayments.length > 1,
      label: normalizedPayments.length > 1
        ? 'Pago mixto'
        : normalizedPayments[0]?.label || 'Pago',
      payments: normalizedPayments,
      pagos: normalizedPayments,
      amountReceived: normalizedPayments.reduce(
        (sum, pago) => sum + Number(pago.amountReceived || 0),
        0
      ),
      change: normalizedPayments.reduce(
        (sum, pago) => sum + Number(pago.change || 0),
        0
      ),
    };
  }

  return {
    id:
      selectedPaymentMethodConfig?.methodId ||
      selectedPaymentMethodConfig?.id ||
      selectedPaymentMethodConfig?.relationId ||
      null,

    relationId: selectedPaymentMethodConfig?.relationId || null,
    methodId: selectedPaymentMethodConfig?.methodId || null,
    key:
      selectedPaymentMethodConfig?.key ||
      selectedPaymentMethodConfig?.clave ||
      selectedPaymentMethod ||
      null,

    clave:
      selectedPaymentMethodConfig?.clave ||
      selectedPaymentMethodConfig?.key ||
      selectedPaymentMethod ||
      null,

    label:
      selectedPaymentMethodConfig?.label ||
      importedPaymentLabel ||
      selectedPaymentMethod ||
      null,

    tipo: selectedPaymentMethodConfig?.tipo || null,
    permiteVuelto: Boolean(selectedPaymentMethodConfig?.permiteVuelto),
    requiereReferencia: Boolean(selectedPaymentMethodConfig?.requiereReferencia),
    amountReceived: Number(amountReceived || 0),
    voucherNumber: voucherNumber || null,
    change: Number(change || 0),
  };
}


function normalizeMixedPaymentsForPayload(payments = [], fiscalTotal = 0) {
  const source = Array.isArray(payments) ? payments : [];

  const normalized = source
    .map((raw, index) => {
      const method = raw?.method || raw?.methodConfig || raw?.config || raw || {};

      const relationId = Number(
        raw?.payment_relation_id ??
        raw?.relationId ??
        method?.relationId ??
        method?.id ??
        0
      );

      const methodId = Number(
        raw?.payment_method_id ??
        raw?.methodId ??
        method?.methodId ??
        0
      );

      const conditionId = Number(
        raw?.payment_condition_id ??
        raw?.conditionId ??
        method?.conditionId ??
        0
      );

      const methodKey =
        raw?.payment_method_key ||
        raw?.key ||
        method?.key ||
        method?.clave ||
        '';

      const methodName =
        raw?.payment_method_name ||
        raw?.label ||
        method?.label ||
        method?.nombre ||
        methodKey ||
        `Pago ${index + 1}`;

      const conditionKey =
        raw?.payment_condition_key ||
        raw?.conditionKey ||
        method?.conditionKey ||
        '';

      const amountApplied = Math.max(
        0,
        Math.round(
          Number(
            raw?.amount_applied ??
            raw?.amountApplied ??
            raw?.monto_aplicado ??
            raw?.amount ??
            0
          ) || 0
        )
      );

      const cash = isCashMethod(method) ||
        normalizePosPaymentMethodKey(methodKey) === PMT.EFECTIVO;

      const cashSummary = getCashRoundingSummary(amountApplied);
      const amountPaid = cash ? cashSummary.cashTotal : amountApplied;

      const requestedReceived = Number(
        raw?.amount_received ??
        raw?.amountReceived ??
        raw?.cashReceived ??
        amountPaid
      );

      const amountReceived = cash
        ? Math.max(0, Math.round(Number.isFinite(requestedReceived) ? requestedReceived : amountPaid))
        : amountPaid;

      const changeGiven = cash
        ? Math.max(amountReceived - amountPaid, 0)
        : 0;

      const reference =
        raw?.voucher_number ||
        raw?.voucherNumber ||
        raw?.reference ||
        raw?.referencia ||
        '';

      return {
        payment_relation_id:
          Number.isInteger(relationId) && relationId > 0 ? relationId : null,
        payment_method_id:
          Number.isInteger(methodId) && methodId > 0 ? methodId : null,
        payment_method_key: methodKey || null,
        payment_method_name: methodName || null,
        payment_condition_id:
          Number.isInteger(conditionId) && conditionId > 0 ? conditionId : null,
        payment_condition_key: conditionKey || null,

        amount_applied: amountApplied,
        amount_paid: amountPaid,
        amount_received: amountReceived,
        change_given: changeGiven,
        cash_rounding: cash ? cashSummary.rounding : 0,

        ...(reference
          ? {
              voucher_number: String(reference).trim(),
              reference: String(reference).trim(),
              referencia: String(reference).trim(),
            }
          : {}),

        __isCash: cash,
        __requiresVoucher: Boolean(
          raw?.requiereReferencia ??
          method?.requiereReferencia ??
          method?.requiere_referencia
        ),
      };
    })
    .filter((pago) => pago.amount_applied > 0);

  const totalApplied = normalized.reduce(
    (sum, pago) => sum + Number(pago.amount_applied || 0),
    0
  );

  const amountPaid = normalized.reduce(
    (sum, pago) => sum + Number(pago.amount_paid || 0),
    0
  );

  const amountReceived = normalized.reduce(
    (sum, pago) => sum + Number(pago.amount_received || 0),
    0
  );

  const changeGiven = normalized.reduce(
    (sum, pago) => sum + Number(pago.change_given || 0),
    0
  );

  const cashRounding = normalized.reduce(
    (sum, pago) => sum + Number(pago.cash_rounding || 0),
    0
  );

  const remainingBalance = Math.max(
    0,
    Math.round(Number(fiscalTotal || 0)) - totalApplied
  );

  return {
    payments: normalized.map(({ __isCash, __requiresVoucher, ...pago }) => pago),
    validation: normalized,
    totalApplied,
    amountPaid,
    amountReceived,
    changeGiven,
    cashRounding,
    remainingBalance,
    status: remainingBalance > 0 ? 'pending' : 'completed',
  };
}

const POS_STARTUP_STEPS = Object.freeze([
  { id: 'storage', label: 'Preparando almacenamiento local' },
  { id: 'caja', label: 'Verificando estado de caja' },
  { id: 'payments', label: 'Cargando métodos de pago' },
  { id: 'customer', label: 'Preparando cliente genérico' },
  { id: 'rules', label: 'Sincronizando reglas del POS' },
  { id: 'products', label: 'Sincronizando catálogo de productos' },
  { id: 'contingency', label: 'Revisando ventas de contingencia' },
]);

function createPosStartupSteps() {
  return POS_STARTUP_STEPS.reduce((acc, step) => {
    acc[step.id] = { ...step, status: 'pending', detail: '' };
    return acc;
  }, {});
}

/* ======================================================================
 * COMPONENTE PRINCIPAL POS
 * ==================================================================== */
const POS = () => {
  /* ----------------------------- STATE ---------------------------- */
  const [isOnline, setIsOnline] = useState(true);
  const isOnlineRef = useRef(true);
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const [syncingOffline, setSyncingOffline] = useState(false);
  const [apiRecoveryChecking, setApiRecoveryChecking] = useState(false);
  const apiRecoveryInFlightRef = useRef(false);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  /* --------------------- ARRANQUE / SINCRONIZACIÓN ------------------- */
  const [startupModalOpen, setStartupModalOpen] = useState(true);
  const [startupSteps, setStartupSteps] = useState(() => createPosStartupSteps());
  const [startupSummary, setStartupSummary] = useState('Preparando el POS…');
  const startupRanRef = useRef(false);

  const updateStartupStep = useCallback((id, patch = {}) => {
    setStartupSteps((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  }, []);

  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [items, setItems] = useState([]);

  const [printUrl, setPrintUrl] = useState(null);
  const [shouldAutoPrint, setShouldAutoPrint] = useState(false);
  const printIframeRef = useRef(null);

  const [lastVenta, setLastVenta] = useState(null);
  const [reprintPinOpen, setReprintPinOpen] = useState(false);
  const [reprintLoading, setReprintLoading] = useState(false);

  const [lineActionPinOpen, setLineActionPinOpen] = useState(false);
  const [pendingLineAction, setPendingLineAction] = useState(null);

  const [editQtyModalOpen, setEditQtyModalOpen] = useState(false);
  const [editQtyPinOpen, setEditQtyPinOpen] = useState(false);
  const [itemToEditQty, setItemToEditQty] = useState(null);
  const [editQtyValue, setEditQtyValue] = useState('');

  const [variablePriceModalOpen, setVariablePriceModalOpen] = useState(false);
  const [variablePriceProduct, setVariablePriceProduct] = useState(null);
  const [variablePriceValue, setVariablePriceValue] = useState('');

  const [modalMode, setModalMode] = useState('search');
  const [productSearchModalOpen, setProductSearchModalOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [totalProductos, setTotalProductos] = useState(0);

  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [totalClientes, setTotalClientes] = useState(0);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);

  const [loading, setLoading] = useState(false);

  // Si una operación interactiva estaba esperando la API y el POS ya detectó
  // contingencia, no dejamos el overlay global "Procesando..." bloqueando la caja.
  // La operación que corresponda continuará con su fallback local / catch.
  useEffect(() => {
    if (!isOnline && loading) {
      setLoading(false);
    }
  }, [isOnline, loading]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [selectedIndex, setSelectedIndex] = useState(-1);

  /* ------------------------- MODAL PAGO -------------------------- */
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState(0);
  const [paymentMethodsConfig, setPaymentMethodsConfig] = useState([]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [cashGivenState, setCashGivenStateInternal] = useState('');
  const [change, setChange] = useState(0);
  const [voucherNumber, setVoucherNumber] = useState('');

  // El vuelto de una venta recién pagada debe sobrevivir a clearSale().
  // Se mantiene visible sin límite de tiempo y solo desaparece cuando el
  // cajero agrega correctamente el primer producto de la venta siguiente.
  const [postSaleChangeNotice, setPostSaleChangeNotice] = useState(null);

  /* ------------------------- MODAL CAJA -------------------------- */
  const [cajaModalOpen, setCajaModalOpen] = useState(false);
  const [cajaEstadoLoading, setCajaEstadoLoading] = useState(true);
  const [isCajaAbierta, setIsCajaAbierta] = useState(false);
  const [cajaData, setCajaData] = useState(null);
  const isCajaAbiertaRef = useRef(false);
  const cajaDataRef = useRef(null);

  useEffect(() => {
    isCajaAbiertaRef.current = isCajaAbierta;
    cajaDataRef.current = cajaData;
  }, [isCajaAbierta, cajaData]);

  /* ---------------- MODAL CONSULTOR DE PRECIOS ---------------------- */
  const [consultorPreciosOpen, setConsultorPreciosOpen] = useState(false);

  /* ---------------- MODAL EGRESOS / INGRESOS ---------------------- */
  const [movCajaModalOpen, setMovCajaModalOpen] = useState(false);

  /* ---------------- VENTAS EN ESPERA ---------------------- */
  const [ventasPendientesOpen, setVentasPendientesOpen] = useState(false);
  const [ventasEnEspera, setVentasEnEspera] = useState(() => cargarVentasEnEspera());
  const [ventaEnEsperaDelete, setVentaEnEsperaDelete] = useState(null);
  const [ventaEnEsperaDeletePinOpen, setVentaEnEsperaDeletePinOpen] = useState(false);

  /* ---------------- CONTINGENCIA / OFFLINE ---------------------- */
  const [ventasContingenciaOpen, setVentasContingenciaOpen] = useState(false);
  const [ventasContingencia, setVentasContingencia] = useState([]);
  const [ventasContingenciaLoading, setVentasContingenciaLoading] = useState(false);
  const [offlineSaleSyncingId, setOfflineSaleSyncingId] = useState(null);
  const [posProductsCacheCount, setPosProductsCacheCount] = useState(0);
  const [posProductsLastSync, setPosProductsLastSync] = useState(null);
  const [posProductsSyncing, setPosProductsSyncing] = useState(false);
  const [cajaFromCache, setCajaFromCache] = useState(false);

  /* ---------------- REGLAS POS ---------------------- */
  const [posRules, setPosRules] = useState([]);
  const [posRulesLoading, setPosRulesLoading] = useState(false);
  const [posRulesLastSync, setPosRulesLastSync] = useState(null);
  const [authorizedRuleIds, setAuthorizedRuleIds] = useState({});
  const [pendingRuleAuthorization, setPendingRuleAuthorization] = useState(null);

  /* ----------------------------- REFS ---------------------------- */
  const productSearchRef = useRef(null);
  const productSearchRequestSeqRef = useRef(0);
  const customerSearchRef = useRef(null);
  const editQtyInputRef = useRef(null);
  const variablePriceInputRef = useRef(null);
  const clientSaleIdRef = useRef(null);
  const syncingProductsRef = useRef(false);

  const getOrCreateClientSaleId = useCallback(() => {
    if (!clientSaleIdRef.current) {
      clientSaleIdRef.current = createClientSaleId();
    }
    return clientSaleIdRef.current;
  }, []);

  /* --------------------- CLIENTE POR DEFECTO --------------------- */
  const [defaultCustomerId, setDefaultCustomerId] = useState(null);
  const [defaultCustomer, setDefaultCustomer] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [pendingDocumentTypeAfterCustomer, setPendingDocumentTypeAfterCustomer] = useState(null);

  /* ---------------------- TIPO DOCUMENTO ------------------------- */
  const [documentType, setDocumentType] = useState('boleta');

  /* -------- FACTURA 33 - ENTREGA / TRASLADO DE MERCADERÍA ------- */
  const [invoiceDeliveryMode, setInvoiceDeliveryMode] = useState('');
  const [invoiceTransferData, setInvoiceTransferData] = useState(() =>
    createInvoiceTransferData()
  );
  const [invoiceTransferDialogOpen, setInvoiceTransferDialogOpen] = useState(false);
  const [invoiceOriginManualEdit, setInvoiceOriginManualEdit] = useState(false);

  /* ------------------ MODAL IMPORTA DOCUMENTO -------------------- */
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importSource, setImportSource] = useState(null);

  /* ------------------ PAGO ORIGEN IMPORTADO -------------------- */
  const [pagoOrigenModalOpen, setPagoOrigenModalOpen] = useState(false);
  const [pagoOrigenModalMode, setPagoOrigenModalMode] = useState('imported');

  /* ====================================================================
   * Métodos de pago disponibles en POS
   * ==================================================================== */
  const availablePaymentMethods = useMemo(() => {
    return getAvailablePosPaymentMethods(paymentMethodsConfig);
  }, [paymentMethodsConfig]);

  const selectedPaymentMethodConfig = useMemo(() => {
    return findSelectedPaymentMethod(availablePaymentMethods, selectedPaymentMethod);
  }, [availablePaymentMethods, selectedPaymentMethod]);

  const selectedPaymentIsCash = useMemo(() => {
    return isCashMethod(selectedPaymentMethodConfig || selectedPaymentMethod);
  }, [selectedPaymentMethodConfig, selectedPaymentMethod]);

  const selectedPaymentIsCredit = useMemo(() => {
    return isCreditMethod(selectedPaymentMethodConfig || selectedPaymentMethod);
  }, [selectedPaymentMethodConfig, selectedPaymentMethod]);

  const selectedPaymentRequiresVoucher = useMemo(() => {
    return requiresVoucherMethod(selectedPaymentMethodConfig || selectedPaymentMethod);
  }, [selectedPaymentMethodConfig, selectedPaymentMethod]);

  const fetchPaymentMethodsConfig = useCallback(async () => {
    const endpoints = [
      '/tesoreria/metodos-contabilidad',
      '/tesoreria_metodospago/pos',
      '/tesoreria_metodospago',
    ];

    let lastError = null;

    for (const endpoint of endpoints) {
      try {
        const { data } = await api.get(endpoint, {
          params: {
            visible_pos: true,
            activo: true,
          },
          timeout: POS_FAST_API_TIMEOUT_MS,
        });

        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.metodos)
              ? data.metodos
              : Array.isArray(data?.metodos_pago)
                ? data.metodos_pago
                : Array.isArray(data?.data)
                  ? data.data
                  : [];

        const methods = getAvailablePosPaymentMethods(rows);

        setPaymentMethodsConfig(rows);
        isOnlineRef.current = true;
        setIsOnline(true);

        try {
          await putPosConfigCache('payment_methods', rows);
        } catch (cacheError) {
          console.warn('[POS] No se pudieron cachear métodos de pago:', cacheError);
        }

        return methods;
      } catch (error) {
        lastError = error;

        if (isRecoverablePosConnectivityError(error)) {
          console.warn('API no disponible al obtener métodos de pago POS:', error);
          isOnlineRef.current = false;
          setIsOnline(false);
          break;
        }

        if (error.response?.status === 404) {
          continue;
        }

        console.error('Error al obtener métodos de pago POS:', error);
        break;
      }
    }

    try {
      const cached = await getPosConfigCache('payment_methods');
      const rows = Array.isArray(cached?.value) ? cached.value : [];
      const methods = getAvailablePosPaymentMethods(rows);

      if (methods.length) {
        setPaymentMethodsConfig(rows);
        return methods;
      }
    } catch (cacheError) {
      console.error('No se pudieron leer métodos de pago locales:', cacheError);
    }

    setPaymentMethodsConfig([]);

    setSnackbar({
      open: true,
      message:
        'No hay métodos de pago disponibles en servidor ni en el cache local del POS.',
      severity: 'error',
    });

    if (lastError && isRecoverablePosConnectivityError(lastError)) {
      isOnlineRef.current = false;
      setIsOnline(false);
    }

    return [];
  }, []);

  /* ====================================================================
   * Catálogo local de productos POS
   * ==================================================================== */
  const refreshPosProductsCache = useCallback(
    async ({ forceFull = false, silent = true } = {}) => {
      if (syncingProductsRef.current || !hasIndexedDB) return null;
      if (!navigator.onLine) return null;

      syncingProductsRef.current = true;
      setPosProductsSyncing(true);

      try {
        const currentMetaRecord = await getPosConfigCache('products_sync_meta');
        const currentMeta = currentMetaRecord?.value || {};
        const lastFullMs = currentMeta.lastFullSync
          ? new Date(currentMeta.lastFullSync).getTime()
          : 0;

        const mustFull =
          forceFull ||
          !lastFullMs ||
          Date.now() - lastFullMs >= POS_PRODUCT_CACHE_FULL_TTL_MS;

        const updatedSince = mustFull ? null : currentMeta.lastDeltaSync || currentMeta.lastFullSync || null;
        const generation = mustFull ? `g-${Date.now()}-${Math.random().toString(36).slice(2)}` : null;

        let afterId = 0;
        let totalChanged = 0;
        let firstServerTime = null;

        do {
          const { data } = await api.get('/inventario_productos/pos-cache', {
            params: {
              limit: 2000,
              after_id: afterId,
              ...(updatedSince ? { updated_since: updatedSince } : {}),
            },
            timeout: POS_SYNC_API_TIMEOUT_MS,
          });

          const rows = Array.isArray(data?.productos) ? data.productos : [];
          if (!firstServerTime) {
            firstServerTime = data?.server_time || new Date().toISOString();
          }

          totalChanged += await cachePosProductsPage(rows, { generation });

          afterId = Number(data?.next_cursor || 0);
          if (!data?.has_more || !afterId) break;
        } while (afterId > 0);

        if (mustFull) {
          await finalizePosProductsGeneration(generation);
        }

        const serverTime = firstServerTime || new Date().toISOString();
        // Solape de 5 segundos: evita perder una actualización que ocurra justo
        // mientras se recorre una sincronización paginada.
        const deltaAnchor = new Date(new Date(serverTime).getTime() - 5000).toISOString();

        const nextMeta = {
          ...currentMeta,
          lastFullSync: mustFull ? serverTime : currentMeta.lastFullSync || null,
          lastDeltaSync: deltaAnchor,
          lastMode: mustFull ? 'full' : 'delta',
          lastChanged: totalChanged,
        };

        await putPosConfigCache('products_sync_meta', nextMeta);

        const count = await getCachedPosProductCount();
        setPosProductsCacheCount(count);
        setPosProductsLastSync(serverTime);
        isOnlineRef.current = true;
        setIsOnline(true);

        if (!silent) {
          setSnackbar({
            open: true,
            severity: 'success',
            message: `Catálogo local actualizado: ${count} productos.`,
          });
        }

        return { count, changed: totalChanged, mode: nextMeta.lastMode };
      } catch (error) {
        console.error('[POS] Error sincronizando catálogo local:', error);

        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);
        }

        if (!silent) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'No se pudo actualizar el catálogo local. Se conservará la última copia disponible.',
          });
        }

        return null;
      } finally {
        syncingProductsRef.current = false;
        setPosProductsSyncing(false);
      }
    },
    []
  );

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const [count, metaRecord] = await Promise.all([
          getCachedPosProductCount(),
          getPosConfigCache('products_sync_meta'),
        ]);

        if (!active) return;
        setPosProductsCacheCount(count);
        setPosProductsLastSync(
          metaRecord?.value?.lastDeltaSync ||
          metaRecord?.value?.lastFullSync ||
          null
        );
      } catch (error) {
        console.warn('[POS] No se pudo leer estado del catálogo local:', error);
      }

    })();

    const timer = window.setInterval(() => {
      if (navigator.onLine) {
        refreshPosProductsCache({ silent: true });
      }
    }, POS_PRODUCT_CACHE_DELTA_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [refreshPosProductsCache]);

  /* ====================================================================
   * Cache / sincronización de reglas POS
   * ==================================================================== */
  const refreshPosRules = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setPosRulesLoading(true);

      try {
        const { data } = await api.get('/ventas_reglas/pos-cache', { timeout: POS_FAST_API_TIMEOUT_MS });

        const reglas = Array.isArray(data?.reglas) ? data.reglas : [];

        setPosRules(reglas);
        setPosRulesLastSync(data?.server_time || new Date().toISOString());
        isOnlineRef.current = true;
        setIsOnline(true);

        await replacePosRulesCache(reglas);

        if (!silent) {
          setSnackbar({
            open: true,
            severity: 'success',
            message: `Reglas POS actualizadas: ${reglas.length}`,
          });
        }

        return reglas;
      } catch (error) {
        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);
        }

        let cached = [];

        try {
          cached = await getPosRulesCache();
        } catch (cacheError) {
          console.error('No se pudo leer cache de reglas POS:', cacheError);
        }

        setPosRules(cached);

        if (!silent) {
          setSnackbar({
            open: true,
            severity: cached.length ? 'warning' : 'error',
            message: cached.length
              ? 'No se pudieron actualizar reglas POS. Se usará cache local.'
              : 'No se pudieron cargar reglas POS.',
          });
        }

        return cached;
      } finally {
        if (!silent) setPosRulesLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      refreshPosRules({ silent: true });
    }, 5 * 60 * 1000);

    return () => window.clearInterval(timer);
  }, [refreshPosRules]);

  /* ====================================================================
   * Borrador local POS
   * ==================================================================== */
  useEffect(() => {
    const draft = loadPosDraft();

    if (!draft) return;

    if (Array.isArray(draft.items)) {
      // Repara también borradores creados por versiones anteriores donde un
      // pesable podía quedar con quantity aumentado pero gross sin actualizar.
      setItems(
        relabelVariablePriceCartItems(
          draft.items.map((item) =>
            item?.isWeight
              ? buildWeightedCartLine(item, item.quantity ?? item.cantidad ?? 0)
              : item
          )
        )
      );
    }

    if (draft.customer) {
      setCustomer(draft.customer);
    }

    if (draft.documentType) {
      setDocumentType(draft.documentType);
    }

    if (draft.invoiceDeliveryMode) {
      setInvoiceDeliveryMode(draft.invoiceDeliveryMode);
    }

    if (draft.invoiceTransferData && typeof draft.invoiceTransferData === 'object') {
      setInvoiceTransferData(
        mergeInvoiceTransferDefaults(
          draft.invoiceTransferData,
          createInvoiceTransferData({
            cajaData,
            customer: draft.customer || null,
          })
        )
      );
    }

    if (draft.importSource) {
      setImportSource(draft.importSource);
    }

    if (draft.authorizedRuleIds) {
      setAuthorizedRuleIds(draft.authorizedRuleIds);
    }

    if (draft.clientSaleId) {
      clientSaleIdRef.current = String(draft.clientSaleId);
    }

    if ((draft.items && draft.items.length > 0) || draft.customer || draft.importSource) {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Se restauró una venta pendiente.',
      });
    }
  }, []);

  useEffect(() => {
    const hasSomethingToSave =
      (items && items.length > 0) ||
      importSource ||
      Object.keys(authorizedRuleIds || {}).length > 0 ||
      (customer && customer.id && (!defaultCustomer || customer.id !== defaultCustomer.id)) ||
      (documentType && documentType !== 'boleta');

    if (!hasSomethingToSave) {
      clearPosDraft();
      return;
    }

    savePosDraft({
      items,
      customer,
      documentType,
      invoiceDeliveryMode,
      invoiceTransferData,
      importSource,
      authorizedRuleIds,
      clientSaleId: getOrCreateClientSaleId(),
      savedAt: new Date().toISOString(),
    });
  }, [
    items,
    customer,
    documentType,
    invoiceDeliveryMode,
    invoiceTransferData,
    importSource,
    defaultCustomer,
    authorizedRuleIds,
    getOrCreateClientSaleId,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasPendingSale =
        (items && items.length > 0) ||
        importSource ||
        Object.keys(authorizedRuleIds || {}).length > 0 ||
        (customer && customer.id && (!defaultCustomer || customer.id !== defaultCustomer.id)) ||
        (documentType && documentType !== 'boleta');

      if (!hasPendingSale) return;

      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [items, customer, documentType, importSource, defaultCustomer, authorizedRuleIds]);

  /* ====================================================================
   * Opciones de documento según cliente
   * ==================================================================== */
  const docOptions = useMemo(() => {
    const allowed = getAllowedDocTypes(customer);
    const options = [];

    if (allowed.includes('boleta')) {
      options.push({
        key: 'boleta',
        Icon: DescriptionIcon,
        label: 'Boleta',
      });
    }

    if (allowed.includes('factura')) {
      options.push({
        key: 'factura',
        Icon: AssignmentIcon,
        label: 'Factura',
      });
    }

    return options;
  }, [customer]);

  useEffect(() => {
    const allowed = getAllowedDocTypes(customer);

    if (!allowed.length) {
      if (documentType !== 'boleta') setDocumentType('boleta');
      return;
    }

    if (!allowed.includes(documentType)) {
      const fallback = allowed.includes('boleta') ? 'boleta' : allowed[0];
      setDocumentType(fallback);
    }
  }, [customer, documentType]);

  useEffect(() => {
    if (customer && customer.rut === GENERIC_RUT) {
      setDocumentType('boleta');
    }
  }, [customer]);

  useEffect(() => {
    if (documentType !== 'factura') return;

    const defaults = createInvoiceTransferData({ cajaData, customer });

    setInvoiceTransferData((prev) => {
      const next = mergeInvoiceTransferDefaults(prev, defaults);

      // El retiro directo no requiere identificar transporte ni destino.
      // Los valores previos del formulario quedan disponibles si se cambia a despacho.

      return next;
    });

    if (!invoiceDeliveryMode) {
      setInvoiceDeliveryMode(INVOICE_DELIVERY_MODE.RETIRO_CLIENTE);
      setInvoiceTransferDialogOpen(true);
    }
  }, [documentType, invoiceDeliveryMode, cajaData, customer]);

  const updateInvoiceTransferField = useCallback((field, value) => {
    setInvoiceTransferData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const hydrateInvoiceTransferFromContext = useCallback(() => {
    const defaults = createInvoiceTransferData({ cajaData, customer });

    setInvoiceTransferData((prev) =>
      mergeInvoiceTransferDefaults(prev, defaults)
    );
  }, [cajaData, customer]);

  const handleInvoiceDeliveryModeChange = useCallback(
    (nextMode) => {
      const mode = String(nextMode || '').trim();
      const defaults = createInvoiceTransferData({ cajaData, customer });
      const issuer = getInvoiceIssuerSnapshot(cajaData);

      setInvoiceDeliveryMode(mode);

      setInvoiceTransferData((prev) => {
        const next = mergeInvoiceTransferDefaults(prev, defaults);

        // En retiro directo no se recopilan datos adicionales de transporte.
        // Se conservan internamente los datos previos si vuelve a elegir despacho.

        if (
          mode === INVOICE_DELIVERY_MODE.DESPACHO &&
          (!next.transportista_tipo ||
            next.transportista_tipo === TRANSPORTER_TYPE.COMPRADOR)
        ) {
          next.transportista_tipo = TRANSPORTER_TYPE.EMISOR;
          next.transportista_rut = formatRutChile(issuer.rut);
          next.transportista_nombre = issuer.nombre;
        }

        return next;
      });
    },
    [cajaData, customer]
  );

  const handleInvoiceDestinationTypeChange = useCallback(
    (nextType) => {
      const destinationType = String(nextType || DESTINATION_TYPE.OTRA);

      setInvoiceTransferData((prev) => {
        const next = {
          ...prev,
          destino_tipo: destinationType,
        };

        if (destinationType === DESTINATION_TYPE.CLIENTE) {
          const destination = getCustomerInvoiceDestination(customer);
          next.destino_direccion = destination.direccion;
          next.destino_comuna = destination.comuna;
          next.destino_ciudad = destination.ciudad;
          next.destino_fuente = destination.fuente;
        } else {
          next.destino_fuente = '';
        }

        return next;
      });
    },
    [customer]
  );

  const handleInvoiceTransporterTypeChange = useCallback(
    (nextType) => {
      const transporterType = String(nextType || '');
      const issuer = getInvoiceIssuerSnapshot(cajaData);

      setInvoiceTransferData((prev) => {
        const next = {
          ...prev,
          transportista_tipo: transporterType,
        };

        if (transporterType === TRANSPORTER_TYPE.COMPRADOR) {
          next.transportista_rut = formatRutChile(customer?.rut);
          next.transportista_nombre = getCustomerDisplayName(customer);
        } else if (transporterType === TRANSPORTER_TYPE.EMISOR) {
          next.transportista_rut = formatRutChile(issuer.rut);
          next.transportista_nombre = issuer.nombre;
        } else if (transporterType === TRANSPORTER_TYPE.TERCERO) {
          next.transportista_rut = '';
          next.transportista_nombre = '';
        }

        return next;
      });
    },
    [cajaData, customer]
  );

  const openInvoiceTransferDialog = useCallback(() => {
    hydrateInvoiceTransferFromContext();
    setInvoiceOriginManualEdit(false);
    setInvoiceTransferDialogOpen(true);
  }, [hydrateInvoiceTransferFromContext]);

  const closeInvoiceTransferDialog = useCallback(() => {
    setInvoiceTransferDialogOpen(false);
  }, []);

  const confirmInvoiceTransferDialog = useCallback(() => {
    const error = validateInvoiceTransferData(
      invoiceDeliveryMode,
      invoiceTransferData
    );

    if (error) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: error,
      });
      return;
    }

    setInvoiceTransferDialogOpen(false);
    setInvoiceOriginManualEdit(false);
    setSnackbar({
      open: true,
      severity: 'success',
      message:
        invoiceDeliveryMode === INVOICE_DELIVERY_MODE.NO_AMPARA
          ? 'Factura configurada sin traslado amparado.'
          : invoiceDeliveryMode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE
            ? 'Retiro directo confirmado. No se requieren datos del transporte.'
            : 'Datos de traslado de la Factura 33 listos.',
    });
  }, [invoiceDeliveryMode, invoiceTransferData]);

  /* ====================================================================
   * Totales base y totales con reglas POS
   * ==================================================================== */
  const baseTotalNet = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const value = item.isWeight ? Number(item.net || 0) : Number(item.net || 0) * qty;
      return sum + value;
    }, 0);
  }, [items]);

  const baseTotalTax = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const value = item.isWeight ? Number(item.tax || 0) : Number(item.tax || 0) * qty;
      return sum + value;
    }, 0);
  }, [items]);

  const baseTotalGross = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const value = item.isWeight ? Number(item.gross || 0) : Number(item.gross || 0) * qty;
      return sum + value;
    }, 0);
  }, [items]);

  const paymentMethodForRules = useMemo(() => {
    if (selectedPaymentMethodConfig) return selectedPaymentMethodConfig;
    if (selectedPaymentMethod) return selectedPaymentMethod;

    if (paymentModalOpen && availablePaymentMethods.length > 0) {
      return findCashPaymentMethod(availablePaymentMethods) || null;
    }

    return null;
  }, [
    selectedPaymentMethodConfig,
    selectedPaymentMethod,
    paymentModalOpen,
    availablePaymentMethods,
  ]);

  const posRulesEvaluation = useMemo(() => {
    return evaluatePosRules({
      items,
      rules: posRules,
      paymentMethod: paymentMethodForRules,
      authorizedRuleIds,
      now: new Date(),
    });
  }, [
    items,
    posRules,
    paymentMethodForRules,
    authorizedRuleIds,
  ]);

  const importedOrderDiscountGross = useMemo(() => {
    const sourceType = String(importSource?.type || '').toLowerCase();
    const sourceMode = String(importSource?.modo_importacion || '').toLowerCase();

    const esDocumentoConDescuentosImportados =
      sourceType === 'pedido' ||
      sourceType === 'pedido_pendiente_dte' ||
      sourceType === 'automotriz_mantencion' ||
      sourceMode === 'pedido_pendiente_dte' ||
      sourceMode === 'automotriz_mantencion' ||
      importSource?.puede_emitir_pendientes === true;

    if (!esDocumentoConDescuentosImportados) {
      return 0;
    }

    const discountFromItems = items.reduce((sum, item) => {
      return sum + numberOrZero(item.imported_discount_gross_line);
    }, 0);

    if (discountFromItems > 0) {
      return -Math.round(discountFromItems);
    }

    const rawDiscountNet = numberOrZero(
      importSource?.raw?.totales?.descuento_total ??
        importSource?.raw?.pedido_raw?.descuento_total ??
        importSource?.header?.descuento_total ??
        0
    );

    if (rawDiscountNet <= 0) return 0;

    return -Math.round(rawDiscountNet * 1.19);
  }, [importSource, items]);

  const totalRulesAdjustment = useMemo(() => {
    return Number(posRulesEvaluation?.totalAjusteBruto || 0) + Number(importedOrderDiscountGross || 0);
  }, [posRulesEvaluation, importedOrderDiscountGross]);

  const totalGross = useMemo(() => {
    return Math.max(0, Math.round(baseTotalGross + totalRulesAdjustment));
  }, [baseTotalGross, totalRulesAdjustment]);

  const taxRatio = useMemo(() => {
    return baseTotalGross > 0 ? baseTotalTax / baseTotalGross : 0;
  }, [baseTotalGross, baseTotalTax]);

  const totalTax = useMemo(() => {
    return Math.round(totalGross * taxRatio);
  }, [totalGross, taxRatio]);

  const totalNet = useMemo(() => {
    return Math.max(0, totalGross - totalTax);
  }, [totalGross, totalTax]);

  const discount = 0;

  const totalDiscount = useMemo(() => {
    return Math.abs(Math.min(totalRulesAdjustment, 0));
  }, [totalRulesAdjustment]);

  const totalPayable = totalGross;

  const importedOrderPaid = useMemo(() => {
    return isImportedPaidOrder(importSource, totalPayable);
  }, [importSource, totalPayable]);

  const importedPaymentLabel = useMemo(() => {
    return getImportedPaymentLabel(importSource);
  }, [importSource]);

  const rulesSnapshot = useMemo(() => {
    const baseSnapshot = buildRulesSnapshot({
      isOnline,
      posRules,
      posRulesLastSync,
      posRulesEvaluation,
      authorizedRuleIds,
      baseTotalGross,
      totalRulesAdjustment,
      totalPayable,
    });

    if (importedOrderDiscountGross >= 0) {
      return baseSnapshot;
    }

    const importedAdjustment = {
      regla_id: 'IMPORT_PEDIDO_DESCUENTO',
      codigo: 'IMPORT_PEDIDO_DESCUENTO',
      nombre: 'Descuento heredado del pedido',
      glosa: 'Descuento heredado del pedido',
      tipo_regla: 'IMPORTACION_PEDIDO',
      tipo_ajuste: 'DESCUENTO',
      tipo_calculo: 'MONTO_FIJO_VENTA',
      monto: Math.round(importedOrderDiscountGross),
      detalle: items
        .filter((item) => numberOrZero(item.imported_discount_gross_line) > 0)
        .map((item) => ({
          producto_id: Number(item.product_id || item.id_producto || item.id),
          sku: item.sku || '',
          nombre: item.name || item.nombre || '',
          cantidad: Number(item.quantity || 0),
          descuento_neto: Number(item.imported_discount_net_line || 0),
          descuento_bruto: Number(item.imported_discount_gross_line || 0),
        })),
    };

    return {
      ...baseSnapshot,
      reglas_aplicadas: [
        ...(Array.isArray(baseSnapshot.reglas_aplicadas) ? baseSnapshot.reglas_aplicadas : []),
        {
          regla_id: importedAdjustment.regla_id,
          codigo: importedAdjustment.codigo,
          nombre: importedAdjustment.nombre,
          tipo_regla: importedAdjustment.tipo_regla,
          tipo_ajuste: importedAdjustment.tipo_ajuste,
          tipo_calculo: importedAdjustment.tipo_calculo,
          monto: importedAdjustment.monto,
          autorizada: true,
        },
      ],
      ajustes: [
        ...(Array.isArray(baseSnapshot.ajustes) ? baseSnapshot.ajustes : []),
        importedAdjustment,
      ],
      resumen: {
        ...(baseSnapshot.resumen || {}),
        total_original_bruto: Math.round(Number(baseTotalGross || 0)),
        total_ajustes_bruto: Math.round(Number(totalRulesAdjustment || 0)),
        total_final_bruto: Math.round(Number(totalPayable || 0)),
        descuento_origen_pedido_bruto: Math.abs(Math.round(importedOrderDiscountGross)),
      },
    };
  }, [
    isOnline,
    posRules,
    posRulesLastSync,
    posRulesEvaluation,
    authorizedRuleIds,
    baseTotalGross,
    totalRulesAdjustment,
    totalPayable,
    importedOrderDiscountGross,
    items,
  ]);

  const canContinueNonCash = useMemo(() => {
    if (importedOrderPaid) return true;

    if (!selectedPaymentMethodConfig) return false;

    if (selectedPaymentRequiresVoucher) {
      return String(voucherNumber || '').trim().length > 0;
    }

    return true;
  }, [importedOrderPaid, selectedPaymentMethodConfig, selectedPaymentRequiresVoucher, voucherNumber]);

  const cashPaymentSummary = useMemo(
    () => getCashRoundingSummary(totalPayable),
    [totalPayable]
  );

  const cashTotalPayable = cashPaymentSummary.cashTotal;
  const cashRoundingAmount = cashPaymentSummary.rounding;

  const canContinueCash = useMemo(() => {
    if (importedOrderPaid) return true;

    const given = Number(cashGivenState || 0);

    if (!selectedPaymentIsCash) return canContinueNonCash;

    return given >= cashTotalPayable;
  }, [
    importedOrderPaid,
    cashGivenState,
    selectedPaymentIsCash,
    cashTotalPayable,
    canContinueNonCash,
  ]);

  const setCashGiven = useCallback(
    (value) => {
      setCashGivenStateInternal(value);

      const given = Number(value || 0);
      const nextChange = selectedPaymentIsCash
        ? Math.max(given - cashTotalPayable, 0)
        : 0;

      setChange(nextChange);
    },
    [selectedPaymentIsCash, cashTotalPayable]
  );

  useEffect(() => {
    if (!selectedPaymentIsCash) {
      setChange(0);
      return;
    }

    const given = Number(cashGivenState || 0);
    setChange(Math.max(given - cashTotalPayable, 0));
  }, [cashGivenState, selectedPaymentIsCash, cashTotalPayable]);

  useEffect(() => {
    setAuthorizedRuleIds((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;

      const currentRuleIds = new Set((posRules || []).map((rule) => String(rule.id)));
      const next = {};

      Object.entries(prev).forEach(([id, value]) => {
        if (currentRuleIds.has(String(id))) {
          next[id] = value;
        }
      });

      return next;
    });
  }, [posRules]);

  /* ====================================================================
   * Contador contingencia / offline
   * ==================================================================== */
  const refreshOfflineQueueCount = useCallback(async () => {
    try {
      const pending = await getOfflineSalesFromDB();
      setOfflineQueueCount(pending.filter((row) => !row.synced).length);
    } catch (error) {
      console.error('Error al contar ventas offline:', error);
      setOfflineQueueCount(0);
    }
  }, []);

  const refreshContingencySales = useCallback(
    async ({ showLoading = false, notifyError = false } = {}) => {
      // Los refrescos automáticos (worker, chequeos y sync) no deben alterar
      // el estado visual del modal. IndexedDB es local y normalmente tarda
      // milisegundos; activar/desactivar loading en cada lectura producía
      // el efecto de parpadeo del modal.
      if (showLoading) setVentasContingenciaLoading(true);

      try {
        const rows = await getOfflineSalesFromDB({ includeAllUsers: false });
        setVentasContingencia(rows);
        setOfflineQueueCount(rows.filter((row) => !row.synced).length);
        return rows;
      } catch (error) {
        console.error('Error al cargar ventas de contingencia:', error);

        // Conservamos el último snapshot válido. Vaciar el arreglo durante
        // un fallo transitorio hacía que la lista/estado vacío destellara.
        if (notifyError) {
          setSnackbar({
            open: true,
            severity: 'error',
            message: 'No se pudieron cargar las ventas de contingencia.',
          });
        }

        return null;
      } finally {
        if (showLoading) setVentasContingenciaLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    refreshOfflineQueueCount();
  }, [refreshOfflineQueueCount]);

  const openVentasContingencia = useCallback(async () => {
    setVentasContingenciaOpen(true);
    await refreshContingencySales({ showLoading: true, notifyError: true });
  }, [refreshContingencySales]);

  const closeVentasContingencia = useCallback(() => {
    setVentasContingenciaOpen(false);
  }, []);

  /* ====================================================================
   * Foco de modales
   * ==================================================================== */
  useEffect(() => {
    if (productSearchModalOpen) {
      const timer = setTimeout(() => {
        productSearchRef.current?.focus?.();
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [productSearchModalOpen]);

  useEffect(() => {
    if (customerModalOpen) {
      const timer = setTimeout(() => {
        customerSearchRef.current?.focus?.();
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [customerModalOpen]);

  useEffect(() => {
    if (editQtyModalOpen) {
      const timer = setTimeout(() => {
        if (editQtyInputRef.current) {
          editQtyInputRef.current.focus();
          editQtyInputRef.current.select?.();
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [editQtyModalOpen]);

  useEffect(() => {
    if (variablePriceModalOpen) {
      const timer = setTimeout(() => {
        if (variablePriceInputRef.current) {
          variablePriceInputRef.current.focus();
          variablePriceInputRef.current.select?.();
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [variablePriceModalOpen]);

  /* ====================================================================
   * Estado de caja
   * ==================================================================== */
  const fetchCajaEstado = useCallback(async () => {
    setCajaEstadoLoading(true);

    try {
      const { data } = await api.get('/pos_cajas/estado', {
        timeout: POS_FAST_API_TIMEOUT_MS,
      });

      const abierta =
        data?.abierta === true ||
        data?.isAbierta === true ||
        data?.caja_abierta === true ||
        data?.estado === 'abierta' ||
        data?.estado === 'ABIERTA' ||
        data?.caja?.estado === 'abierta' ||
        data?.caja?.estado === 'ABIERTA';

      const cajaBase =
        data?.caja ||
        data?.data ||
        data?.estado_caja ||
        data ||
        null;

      // Conservamos el objeto de caja tal como lo usa el POS, pero anexamos
      // el contexto de sucursal/emisor cuando /pos_cajas/estado lo entrega
      // como campos hermanos. Esto permite autocompletar el origen efectivo
      // de una Factura 33 sin pedirlo nuevamente al cajero.
      const caja =
        cajaBase && typeof cajaBase === 'object'
          ? {
              ...cajaBase,
              sucursal:
                cajaBase.sucursal ||
                data?.sucursal ||
                data?.branch ||
                data?.punto_venta?.sucursal ||
                null,
              empresa:
                cajaBase.empresa ||
                data?.empresa ||
                data?.emisor ||
                data?.company ||
                null,
            }
          : cajaBase;

      cajaDataRef.current = caja;
      isCajaAbiertaRef.current = Boolean(abierta);
      setCajaData(caja);
      setIsCajaAbierta(Boolean(abierta));
      setCajaFromCache(false);
      isOnlineRef.current = true;
      setIsOnline(true);

      try {
        await putPosConfigCache(
          'cash_session',
          {
            abierta: Boolean(abierta),
            caja,
            serverData: data,
            validatedAt: new Date().toISOString(),
          },
          { userScoped: true }
        );
      } catch (cacheError) {
        console.warn('[POS] No se pudo cachear estado de caja:', cacheError);
      }

      return {
        abierta: Boolean(abierta),
        caja,
        fromCache: false,
      };
    } catch (error) {
      console.error('Error consultando estado de caja:', error);

      if (isRecoverablePosConnectivityError(error)) {
        isOnlineRef.current = false;
        setIsOnline(false);

        // Caída repentina durante una sesión ya iniciada: el estado que está en
        // memoria es más reciente que cualquier snapshot de IndexedDB. No dejamos
        // que una copia antigua (por ejemplo "cerrada") pise una caja que el POS
        // ya tenía confirmada como abierta.
        if (cajaDataRef.current) {
          setCajaData(cajaDataRef.current);
          setIsCajaAbierta(isCajaAbiertaRef.current);
          setCajaFromCache(true);

          setSnackbar({
            open: true,
            severity: 'warning',
            message: isCajaAbiertaRef.current
              ? 'API no disponible. Se conserva la caja abierta ya confirmada para continuar en contingencia.'
              : 'API no disponible. Se conserva el último estado confirmado de caja.',
          });

          return {
            abierta: isCajaAbiertaRef.current,
            caja: cajaDataRef.current,
            fromCache: true,
            fromMemory: true,
          };
        }

        try {
          const cached = await getPosConfigCache('cash_session', { userScoped: true });
          const snapshot = cached?.value || null;

          if (snapshot?.caja) {
            cajaDataRef.current = snapshot.caja;
            isCajaAbiertaRef.current = snapshot.abierta === true;
            setCajaData(snapshot.caja);
            setIsCajaAbierta(snapshot.abierta === true);
            setCajaFromCache(true);

            setSnackbar({
              open: true,
              severity: snapshot.abierta ? 'warning' : 'error',
              message: snapshot.abierta
                ? 'API no disponible. Se mantiene la última caja abierta conocida para trabajar en contingencia.'
                : 'API no disponible y la última caja conocida estaba cerrada.',
            });

            return {
              abierta: snapshot.abierta === true,
              caja: snapshot.caja,
              fromCache: true,
            };
          }
        } catch (cacheError) {
          console.error('[POS] No se pudo recuperar la caja local:', cacheError);
        }

      }

      isCajaAbiertaRef.current = false;
      cajaDataRef.current = null;
      setIsCajaAbierta(false);
      setCajaData(null);
      setCajaFromCache(false);

      setSnackbar({
        open: true,
        severity: 'error',
        message:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'No se pudo consultar el estado de caja y no existe una copia local utilizable.',
      });

      return {
        abierta: false,
        caja: null,
        fromCache: false,
      };
    } finally {
      setCajaEstadoLoading(false);
    }
  }, []);

  useEffect(() => {
    const onCajaActualizada = () => {
      fetchCajaEstado();
    };

    window.addEventListener('cajaActualizada', onCajaActualizada);

    return () => {
      window.removeEventListener('cajaActualizada', onCajaActualizada);
    };
  }, [fetchCajaEstado]);

  const handleOpenCajaModal = () => {
    setCajaModalOpen(true);
  };

  const handleCloseCajaModal = () => {
    setCajaModalOpen(false);
  };

  const handleOpenMovimientosCaja = useCallback(() => {
    if (!isCajaAbierta) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Debe abrir caja antes de registrar ingresos o egresos.',
      });
      return;
    }

    setMovCajaModalOpen(true);
  }, [isCajaAbierta]);

  /* ====================================================================
   * Cliente genérico / cliente por defecto
   * ==================================================================== */
  const fetchDefaultCustomer = useCallback(async () => {
    try {
      const data = await getClients({
        rut: GENERIC_RUT,
        search: GENERIC_RUT,
        q: GENERIC_RUT,
        limit: 10,
      });

      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.clientes)
          ? data.clientes
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.data)
              ? data.data
              : [];

      const generic =
        rows.find((row) => String(row.rut || '').trim() === GENERIC_RUT) ||
        rows[0] ||
        null;

      if (generic) {
        setDefaultCustomer(generic);
        setDefaultCustomerId(generic.id || generic.id_cliente || null);
        setCustomer((prev) => prev || generic);

        if (String(generic.rut || '').trim() === GENERIC_RUT) {
          setDocumentType('boleta');
        }

        try {
          await cachePosCustomers([generic]);
          await putPosConfigCache('default_customer', generic);
        } catch (cacheError) {
          console.warn('[POS] No se pudo cachear cliente genérico:', cacheError);
        }

        return generic;
      }
    } catch (error) {
      console.error('Error cargando cliente genérico:', error);

      if (isRecoverablePosConnectivityError(error)) {
        isOnlineRef.current = false;
        setIsOnline(false);
      }

      try {
        const cached = await getPosConfigCache('default_customer');
        const generic = cached?.value || null;

        if (generic?.id || generic?.id_cliente) {
          setDefaultCustomer(generic);
          setDefaultCustomerId(generic.id || generic.id_cliente || null);
          setCustomer((prev) => prev || generic);
          setDocumentType('boleta');
          return generic;
        }
      } catch (cacheError) {
        console.error('[POS] No se pudo leer cliente genérico local:', cacheError);
      }

      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No se pudo cargar el cliente genérico del POS ni desde el cache local.',
      });
    }

    return null;
  }, []);

  /* ====================================================================
   * Normalización producto POS
   * ==================================================================== */
  const normalizeProductForCart = useCallback((product = {}) => {
    const productId =
      product.id ||
      product.id_producto ||
      product.product_id ||
      product.productId ||
      null;

    const nombre =
      product.nombre ||
      product.name ||
      product.descripcion ||
      product.sku ||
      'Producto';

    const sku = product.sku || product.codigo || product.codigo_sku || '';

    const barcode = getProductBarcode(product);

    const gross =
      Number(product.precio_bruto) ||
      Number(product.price) ||
      Number(product.precio) ||
      Math.round(Number(product.precio_neto || 0) * 1.19) ||
      0;

    const net =
      Number(product.precio_neto) ||
      Math.round(gross / 1.19) ||
      0;

    const tax =
      Number(product.iva) ||
      Number(product.tax) ||
      Math.max(gross - net, 0);

    const stock =
      product.stock_actual != null
        ? Number(product.stock_actual)
        : product.stock != null
          ? Number(product.stock)
          : product.stock_disponible != null
            ? Number(product.stock_disponible)
            : null;

    const taxes = Array.isArray(product.impuestos)
      ? product.impuestos
      : Array.isArray(product.taxes)
        ? product.taxes
        : product.impuesto_porcentaje != null
          ? [{
              clave: product.impuesto_clave || null,
              porcentaje: product.impuesto_porcentaje,
            }]
          : [];

    const categoriasRaw = Array.isArray(product.categorias)
      ? product.categorias
      : Array.isArray(product.categories)
        ? product.categories
        : Array.isArray(product.categorias_producto)
          ? product.categorias_producto
          : Array.isArray(product.product_categories)
            ? product.product_categories
            : [];

    const categorias = categoriasRaw
      .map((cat) => {
        if (!cat) return null;

        if (typeof cat !== 'object') {
          return {
            id: cat,
            nombre: '',
          };
        }

        return {
          ...cat,
          id:
            cat.id ??
            cat.id_categoria ??
            cat.categoria_id ??
            cat.category_id ??
            cat.value ??
            null,
          nombre:
            cat.nombre ??
            cat.categoria_nombre ??
            cat.nombre_categoria ??
            cat.name ??
            cat.label ??
            '',
        };
      })
      .filter((cat) => cat && cat.id != null && cat.id !== '');

    const categoriaPrincipal = categorias[0] || null;

    const idCategoria =
      product.id_categoria ??
      product.categoria_id ??
      product.category_id ??
      product.idCategory ??
      product.categoryId ??
      product.categoria?.id ??
      product.categoria?.id_categoria ??
      product.categoria?.categoria_id ??
      product.category?.id ??
      product.category?.category_id ??
      categoriaPrincipal?.id ??
      null;

    const categoriaNombre =
      product.categoria_nombre ??
      product.nombre_categoria ??
      product.category_name ??
      product.categoria?.nombre ??
      product.categoria?.nombre_categoria ??
      product.category?.name ??
      categoriaPrincipal?.nombre ??
      '';

    return {
      id: productId,
      product_id: productId,
      id_producto: productId,

      name: nombre,
      nombre,
      sku,
      barcode,
      codigo_barras: product.codigo_barras || barcode || '',

      quantity: 1,
      net,
      tax,
      gross,

      price: gross,
      precio: gross,
      precio_bruto: gross,
      precio_neto: net,

      stock,
      stock_actual: stock,

      unidad: product.unidad || product.unidad_medida || 'UND',
      isWeight: parseBoolish(product.isWeight ?? product.pesable, false),
      pesable: parseBoolish(product.pesable ?? product.isWeight, false),
      permite_precio_variable: parseBoolish(
        product.permite_precio_variable ??
          product.permitePrecioVariable ??
          product.variable_price ??
          product.raw?.permite_precio_variable,
        false
      ),
      precio_variable_confirmado: parseBoolish(
        product.precio_variable_confirmado ?? product.variablePriceConfirmed,
        false
      ),
      precio_variable_bruto: Number(
        product.precio_variable_bruto ?? product.variablePriceGross ?? 0
      ) || null,
      taxes,
      impuestos: taxes,

      id_categoria: idCategoria,
      categoria_id: idCategoria,
      category_id: idCategoria,

      categoria_nombre: categoriaNombre,
      nombre_categoria: categoriaNombre,
      category_name: categoriaNombre,

      categorias,

      id_marca: product.id_marca || product.marca_id || null,
      marca_nombre: product.marca_nombre || product.marca || '',

      primera_imagen_publica: product.primera_imagen_publica || '',
      imagen_principal: product.imagen_principal || null,

      precio_variable_nombre_base:
        product.precio_variable_nombre_base ||
        product.variablePriceBaseName ||
        nombre,
      variablePriceBaseName:
        product.variablePriceBaseName ||
        product.precio_variable_nombre_base ||
        nombre,
      precio_variable_orden: Number(
        product.precio_variable_orden ?? product.variablePriceOrder ?? 0
      ) || null,
      variablePriceOrder: Number(
        product.variablePriceOrder ?? product.precio_variable_orden ?? 0
      ) || null,
      descripcion_linea:
        product.descripcion_linea || product.nombre_linea || null,
      nombre_linea:
        product.nombre_linea || product.descripcion_linea || null,

      raw: product,
    };
  }, []);

  const moveItemToTop = useCallback((list, index, patch = {}) => {
    if (!Array.isArray(list) || index < 0 || index >= list.length) return list;

    const item = {
      ...list[index],
      ...patch,
    };

    return [
      item,
      ...list.filter((_, i) => i !== index),
    ];
  }, []);

  const fetchProductFullDetailsForPOS = useCallback(async (product) => {
    const id = product?.id_producto || product?.product_id || product?.id;

    if (!id) return product;

    // Local-first: el catálogo POS ya contiene los campos necesarios para venta.
    // Esto evita una segunda llamada /detalles después de que una búsqueda haya
    // detectado que la API acaba de caer.
    try {
      const cached = await getCachedPosProductById(id);
      if (cached) {
        return {
          ...cached,
          ...product,
          raw: product?.raw || cached?.raw || cached,
        };
      }
    } catch (_) {
      // Si IndexedDB falla, todavía podemos intentar API cuando esté disponible.
    }

    if (!isOnlineRef.current || !navigator.onLine) {
      return product;
    }

    try {
      const { data } = await api.get(`/inventario_productos/${Number(id)}/detalles`, {
        timeout: POS_FAST_API_TIMEOUT_MS,
      });

      const fullProduct =
        data?.producto ||
        data?.item ||
        data?.data?.producto ||
        data?.data?.item ||
        data?.data ||
        data ||
        null;

      if (!fullProduct || typeof fullProduct !== 'object') {
        return product;
      }

      // /inventario_productos/:id/detalles devuelve impuestos fuera de "producto".
      // Los preservamos para que el modal de precio variable calcule el neto/IVA
      // con el porcentaje real (por ejemplo 19%) y no lo infiera desde 0,84 / 1.
      const fullTaxes =
        (Array.isArray(data?.impuestos) && data.impuestos) ||
        (Array.isArray(data?.data?.impuestos) && data.data.impuestos) ||
        (Array.isArray(fullProduct.impuestos) && fullProduct.impuestos) ||
        (Array.isArray(product.impuestos) && product.impuestos) ||
        (Array.isArray(product.taxes) && product.taxes) ||
        [];

      const categorias =
        fullProduct.categorias ||
        fullProduct.categories ||
        fullProduct.categorias_producto ||
        product.categorias ||
        product.categories ||
        [];

      const idCategoria =
        fullProduct.id_categoria ??
        fullProduct.categoria_id ??
        fullProduct.category_id ??
        fullProduct.categoria?.id ??
        fullProduct.categoria?.id_categoria ??
        fullProduct.categoria?.categoria_id ??
        fullProduct.category?.id ??
        product.id_categoria ??
        product.categoria_id ??
        product.category_id ??
        product.categoria?.id ??
        null;

      const merged = {
        ...product,
        ...fullProduct,

        id: fullProduct.id || fullProduct.id_producto || product.id,
        id_producto: fullProduct.id_producto || fullProduct.id || product.id_producto,
        product_id:
          fullProduct.product_id ||
          fullProduct.id_producto ||
          fullProduct.id ||
          product.product_id,

        precio_bruto:
          product.precio_bruto ??
          product.gross ??
          fullProduct.precio_bruto ??
          fullProduct.price ??
          fullProduct.precio,

        precio_neto:
          product.precio_neto ??
          product.net ??
          fullProduct.precio_neto,

        stock_actual:
          fullProduct.stock_actual ??
          fullProduct.stock ??
          product.stock_actual ??
          product.stock,

        impuestos: fullTaxes,
        taxes: fullTaxes,

        categorias,

        id_categoria: idCategoria,
        categoria_id: idCategoria,
        category_id: idCategoria,

        categoria_nombre:
          fullProduct.categoria_nombre ??
          fullProduct.nombre_categoria ??
          fullProduct.category_name ??
          fullProduct.categoria?.nombre ??
          fullProduct.category?.name ??
          product.categoria_nombre ??
          product.nombre_categoria ??
          '',
      };

      return merged;
    } catch (error) {
      console.warn('[POS] No se pudo obtener detalle completo del producto:', error);

      if (isRecoverablePosConnectivityError(error)) {
        isOnlineRef.current = false;
        setIsOnline(false);
        try {
          return (await getCachedPosProductById(id)) || product;
        } catch (_) {
          return product;
        }
      }

      return product;
    }
  }, []);


  const addProductToCart = useCallback(
    (product, quantityToAdd = 1) => {
      if (!product) return;

      if (!isCajaAbierta) {
        setSnackbar({
          open: true,
          message: 'Debe abrir caja antes de agregar productos',
          severity: 'warning',
        });
        return;
      }

      const normalized = normalizeProductForCart(product);

      const isVariablePrice = parseBoolish(normalized.permite_precio_variable, false);
      const variablePriceConfirmed = parseBoolish(normalized.precio_variable_confirmado, false);

      if (isVariablePrice && !variablePriceConfirmed) {
        setVariablePriceProduct(product);
        setVariablePriceValue('');
        setVariablePriceModalOpen(true);
        return 'VARIABLE_PRICE_PENDING';
      }

      const productId = String(normalized.product_id || normalized.id || '');
      const sku = String(normalized.sku || '');
      const barcode = String(normalized.barcode || normalized.codigo_barras || '');
      const qty = Math.max(1, Number(quantityToAdd || 1));

      // Un pesable agregado por su SKU/PLU normal representa kilos.
      // Construimos desde el primer ingreso una línea consistente: precio/kg
      // separado del total monetario de la línea.
      const normalizedToAdd = normalized.isWeight
        ? buildWeightedCartLine(normalized, qty)
        : normalized;

      // El primer producto agregado correctamente a la venta siguiente confirma
      // que el cajero ya avanzó y permite retirar el vuelto pendiente anterior.
      setPostSaleChangeNotice(null);

      setItems((prev) => {
        const index = isVariablePrice
          ? -1
          : prev.findIndex((item) => {
          const sameId =
            productId &&
            String(item.product_id || item.id_producto || item.id || '') === productId;

          const sameSku =
            !productId &&
            sku &&
            String(item.sku || '') === sku;

          const sameBarcode =
            !productId &&
            !sku &&
            barcode &&
            String(item.barcode || item.codigo_barras || '') === barcode;

          return sameId || sameSku || sameBarcode;
        });

        // Los productos de precio variable siempre crean una línea nueva.
        // Así dos montos distintos del mismo SKU nunca se pisan ni se fusionan.

        // Si ya existe: aumenta cantidad y mueve la línea al principio
        if (index >= 0) {
          const current = prev[index];
          const currentQty = Number(current.quantity || 0);
          const nextQty = currentQty + qty;

          if (current.isWeight || normalizedToAdd.isWeight) {
            // En pesables net/tax/gross son TOTALES de línea. Acumulamos tanto
            // el peso como los importes; no dejamos el bruto congelado mientras
            // aumenta quantity.
            const currentLine = buildWeightedCartLine(current, currentQty);
            const addedLine = buildWeightedCartLine(normalizedToAdd, qty);

            const nextNet = roundDecimals(
              Number(currentLine.net || 0) + Number(addedLine.net || 0),
              2
            );
            const nextTax = roundDecimals(
              Number(currentLine.tax || 0) + Number(addedLine.tax || 0),
              2
            );
            const nextGross = roundDecimals(
              Number(currentLine.gross || 0) + Number(addedLine.gross || 0),
              2
            );

            const unitGross = nextQty > 0 ? roundDecimals(nextGross / nextQty, 8) : 0;
            const unitNet = nextQty > 0 ? roundDecimals(nextNet / nextQty, 8) : 0;
            const unitTax = nextQty > 0 ? roundDecimals(nextTax / nextQty, 8) : 0;

            return moveItemToTop(prev, index, {
              ...current,
              ...normalizedToAdd,
              quantity: roundDecimals(nextQty, 3),
              cantidad: roundDecimals(nextQty, 3),

              net: nextNet,
              tax: nextTax,
              gross: nextGross,
              lineTotal: nextGross,
              line_total: nextGross,
              total: nextGross,
              total_neto: nextNet,
              total_iva: nextTax,
              total_bruto: nextGross,

              unitNet,
              unitTax,
              unitGross,
              grossUnit: unitGross,
              precio_neto_unitario: unitNet,
              precio_unitario_neto: unitNet,
              precio_unitario_bruto: unitGross,
              precio_bruto_unitario: unitGross,
              precio_bruto_kg: unitGross,
              peso_kg: roundDecimals(nextQty, 3),
              peso_gramos: Math.round(nextQty * 1000),

              // Mantiene datos calculados o heredados si existían
              imported_discount_net_line:
                current.imported_discount_net_line ??
                normalizedToAdd.imported_discount_net_line ??
                0,

              imported_discount_gross_line:
                current.imported_discount_gross_line ??
                normalizedToAdd.imported_discount_gross_line ??
                0,
            });
          }

          return moveItemToTop(prev, index, {
            ...current,
            ...normalizedToAdd,
            quantity: nextQty,

            // Mantiene datos calculados o heredados si existían
            imported_discount_net_line:
              current.imported_discount_net_line ??
              normalizedToAdd.imported_discount_net_line ??
              0,

            imported_discount_gross_line:
              current.imported_discount_gross_line ??
              normalizedToAdd.imported_discount_gross_line ??
              0,
          });
        }

        // Si es producto nuevo: entra directamente en la primera posición.
        let itemToInsert = normalizedToAdd.isWeight
          ? normalizedToAdd
          : {
              ...normalizedToAdd,
              quantity: qty,
            };

        if (isVariablePrice) {
          const groupKey = getVariablePriceGroupKey(itemToInsert);
          const baseName = String(getVariablePriceBaseName(itemToInsert)).trim() || 'PRODUCTO GENÉRICO';

          const maxExistingOrder = prev.reduce((max, existing) => {
            if (!groupKey || getVariablePriceGroupKey(existing) !== groupKey) return max;

            const order = Number(
              existing.precio_variable_orden ?? existing.variablePriceOrder ?? 0
            );

            return Number.isFinite(order) && order > max ? order : max;
          }, 0);

          const nextOrder = maxExistingOrder + 1;

          itemToInsert = {
            ...itemToInsert,
            precio_variable_nombre_base: baseName,
            variablePriceBaseName: baseName,
            precio_variable_orden: nextOrder,
            variablePriceOrder: nextOrder,
          };
        }

        const relabeled = relabelVariablePriceCartItems([itemToInsert, ...prev]);

        if (isVariablePrice) {
          const groupKey = getVariablePriceGroupKey(itemToInsert);

          if (groupKey) {
            const sameVariableProduct = relabeled
              .filter((item) => getVariablePriceGroupKey(item) === groupKey)
              .sort(
                (a, b) =>
                  Number(a.precio_variable_orden ?? a.variablePriceOrder ?? 0) -
                  Number(b.precio_variable_orden ?? b.variablePriceOrder ?? 0)
              );

            const otherItems = relabeled.filter(
              (item) => getVariablePriceGroupKey(item) !== groupKey
            );

            // El grupo recién marcado queda arriba, pero internamente ordenado
            // 1, 2, 3... según el orden real de ingreso.
            return [...sameVariableProduct, ...otherItems];
          }
        }

        return relabeled;
      });

      setAuthorizedRuleIds({});

      setSnackbar({
        open: true,
        severity: 'success',
        message: `${normalized.name} agregado al carrito.`,
      });
    },
    [isCajaAbierta, normalizeProductForCart, moveItemToTop]
  );

  const addWeightedProductToCart = useCallback(
    (product, weightedBarcode) => {
      if (!product || !weightedBarcode) return false;

      if (!isCajaAbierta) {
        setSnackbar({
          open: true,
          message: 'Debe abrir caja antes de agregar productos',
          severity: 'warning',
        });
        return false;
      }

      const normalized = normalizeProductForCart(product);
      const weightKg = roundDecimals(weightedBarcode.weightKg, 3);

      if (!normalized.isWeight) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: `El producto asociado al PLU ${weightedBarcode.plu} no está marcado como pesable.`,
        });
        return false;
      }

      if (!Number.isFinite(weightKg) || weightKg <= 0) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'El código de balanza no contiene un peso válido.',
        });
        return false;
      }

      const taxes = Array.isArray(product.impuestos)
        ? product.impuestos
        : Array.isArray(product.taxes)
          ? product.taxes
          : Array.isArray(normalized.taxes)
            ? normalized.taxes
            : [];

      const unitNet = Number(product.precio_neto ?? normalized.net ?? 0);
      const configuredUnitGross = Number(
        product.precio_bruto ??
          product.price ??
          product.precio ??
          normalized.gross ??
          0
      );

      let lineNet = roundDecimals(unitNet * weightKg, 2);
      let lineTax = 0;

      if (taxes.length > 0) {
        lineTax = taxes.reduce((sum, taxRow) => {
          const pct = Number(taxRow?.porcentaje ?? taxRow?.pct ?? 0);

          if (!Number.isFinite(pct) || pct <= 0) return sum;

          return sum + roundDecimals(lineNet * (pct / 100), 2);
        }, 0);
      } else if (
        Number.isFinite(configuredUnitGross) &&
        configuredUnitGross > 0 &&
        Number.isFinite(unitNet) &&
        unitNet > 0 &&
        configuredUnitGross > unitNet
      ) {
        const configuredLineGross = roundDecimals(configuredUnitGross * weightKg, 2);
        lineTax = Math.max(0, roundDecimals(configuredLineGross - lineNet, 2));
      }

      let lineGross = roundDecimals(lineNet + lineTax, 2);

      if (
        taxes.length === 0 &&
        Number.isFinite(configuredUnitGross) &&
        configuredUnitGross > 0
      ) {
        lineGross = roundDecimals(configuredUnitGross * weightKg, 2);

        if (!(Number.isFinite(unitNet) && unitNet > 0)) {
          lineNet = lineGross;
          lineTax = 0;
        } else {
          lineTax = Math.max(0, roundDecimals(lineGross - lineNet, 2));
        }
      }

      const unitGross = weightKg > 0
        ? roundDecimals(lineGross / weightKg, 8)
        : configuredUnitGross;

      const unitTax = weightKg > 0
        ? roundDecimals(lineTax / weightKg, 8)
        : 0;

      const weightedItem = {
        ...normalized,
        quantity: weightKg,
        cantidad: weightKg,

        // Para pesables, net/tax/gross representan el total de la línea.
        net: lineNet,
        tax: lineTax,
        gross: lineGross,
        lineTotal: lineGross,
        line_total: lineGross,
        total: lineGross,
        total_neto: lineNet,
        total_iva: lineTax,
        total_bruto: lineGross,

        // Valores unitarios por kilogramo para UI, reglas y payload.
        unitNet,
        unitTax,
        unitGross,
        grossUnit: unitGross,
        precio_neto_unitario: unitNet,
        precio_unitario_bruto: unitGross,
        precio_bruto_unitario: unitGross,
        precio_bruto_kg: unitGross,

        isWeight: true,
        pesable: true,
        unidad: product.unidad || product.unidad_medida || 'KG',
        taxes,
        impuestos: taxes,

        weightedBarcode: weightedBarcode.barcode,
        codigo_barras_pesable: weightedBarcode.barcode,
        plu: weightedBarcode.plu,
        peso_kg: weightKg,
        peso_gramos: weightedBarcode.weightGrams,
      };

      const productId = String(weightedItem.product_id || weightedItem.id || '');
      const sku = String(weightedItem.sku || '');

      // Igual que en productos normales: solo una marcación válida de la
      // siguiente venta elimina el aviso de vuelto pendiente.
      setPostSaleChangeNotice(null);

      setItems((prev) => {
        const index = prev.findIndex((item) => {
          if (!item?.isWeight) return false;

          const sameId =
            productId &&
            String(item.product_id || item.id_producto || item.id || '') === productId;

          const sameSku =
            !productId &&
            sku &&
            String(item.sku || '') === sku;

          return sameId || sameSku;
        });

        if (index < 0) {
          return [weightedItem, ...prev];
        }

        const current = prev[index];
        const nextWeight = roundDecimals(Number(current.quantity || 0) + weightKg, 3);
        const nextNet = roundDecimals(Number(current.net || 0) + lineNet, 2);
        const nextTax = roundDecimals(Number(current.tax || 0) + lineTax, 2);
        const nextGross = roundDecimals(Number(current.gross || 0) + lineGross, 2);

        return moveItemToTop(prev, index, {
          ...current,
          ...weightedItem,
          quantity: nextWeight,
          cantidad: nextWeight,
          net: nextNet,
          tax: nextTax,
          gross: nextGross,
          lineTotal: nextGross,
          line_total: nextGross,
          total: nextGross,
          total_neto: nextNet,
          total_iva: nextTax,
          total_bruto: nextGross,
          peso_kg: nextWeight,
          peso_gramos: Math.round(nextWeight * 1000),
        });
      });

      setAuthorizedRuleIds({});
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${normalized.name} · ${weightKg.toFixed(3)} kg agregado al carrito.`,
      });

      return true;
    },
    [isCajaAbierta, moveItemToTop, normalizeProductForCart]
  );

  const clearSale = useCallback(() => {
    setItems([]);
    setImportSource(null);
    setAuthorizedRuleIds({});
    setPendingRuleAuthorization(null);
    setSelectedPaymentMethod('');
    setCashGivenStateInternal('');
    setVoucherNumber('');
    setChange(0);
    setPaymentStep(0);
    setPaymentModalOpen(false);

    setInvoiceDeliveryMode('');
    setInvoiceTransferData(
      createInvoiceTransferData({
        cajaData,
        customer: defaultCustomer || null,
      })
    );
    setInvoiceTransferDialogOpen(false);

    if (defaultCustomer) {
      setCustomer(defaultCustomer);
      setDocumentType('boleta');
    } else {
      setCustomer(null);
      setDocumentType('boleta');
    }

    clientSaleIdRef.current = null;
    clearPosDraft();
  }, [defaultCustomer, cajaData]);

  /* ====================================================================
   * BÚSQUEDA DE PRODUCTOS
   * ==================================================================== */
  const handleOpenProductSearchModal = (mode = 'search') => {
    if (!isCajaAbierta) {
      setSnackbar({
        open: true,
        message: 'Debe abrir caja antes de agregar productos',
        severity: 'warning',
      });
      return;
    }

    setModalMode(mode);
    setProductSearchModalOpen(true);
    setProductSearchTerm('');
    setProductResults([]);
    setTotalProductos(0);
    setSelectedIndex(-1);
    setSuggestions([]);

    setTimeout(() => productSearchRef.current?.focus?.(), 100);
  };

  const handleCloseProductSearchModal = () => {
    setProductSearchModalOpen(false);
    setProductSearchTerm('');
    setProductResults([]);
    setTotalProductos(0);
    setSelectedIndex(-1);
    setSuggestions([]);
    setModalMode('search');
  };

  const normalizeProductSearchRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.productos)) return data.productos;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const fetchPosProductSearchRows = useCallback(
    async (term, limit = 30) => {
      const q = String(term || '').trim();
      if (!q) return { rows: [], source: 'none' };

      // Híbrido de rendimiento:
      // 1) SKU/barcode exacto se resuelve localmente mediante índices (muy rápido).
      // 2) Si API está sana, búsquedas por texto/nombre van directo al endpoint optimizado.
      // 3) Si API cae, recién entonces usamos la búsqueda amplia del catálogo local.
      try {
        const exactCached = await getCachedPosProductExact(q);
        if (exactCached) {
          return {
            rows: [exactCached],
            data: { productos: [exactCached], total: 1 },
            source: 'cache-exact',
          };
        }
      } catch (cacheError) {
        console.warn('[POS] No se pudo consultar índice local exacto:', cacheError);
      }

      if (isOnlineRef.current && navigator.onLine) {
        try {
          const { data } = await api.get('/inventario_productos/pos', {
            params: {
              search: q,
              limit,
            },
            timeout: POS_FAST_API_TIMEOUT_MS,
          });

          const apiRows = normalizeProductSearchRows(data);
          isOnlineRef.current = true;
          setIsOnline(true);

          if (apiRows.length > 0) {
            cachePosProductsPage(apiRows).catch((cacheError) => {
              console.warn('[POS] No se pudieron cachear productos encontrados:', cacheError);
            });
          }

          return {
            rows: apiRows,
            data,
            source: 'api',
          };
        } catch (error) {
          if (!isRecoverablePosConnectivityError(error)) {
            throw error;
          }

          console.warn('[POS] API de productos no disponible; usando catálogo local.', error);
          isOnlineRef.current = false;
          setIsOnline(false);
        }
      }

      const cachedRows = await searchCachedPosProducts(q, limit);
      return {
        rows: cachedRows,
        data: { productos: cachedRows, total: cachedRows.length },
        source: 'cache',
      };
    },
    []
  );

  const handleCloseVariablePriceModal = useCallback(() => {
    setVariablePriceModalOpen(false);
    setVariablePriceProduct(null);
    setVariablePriceValue('');
  }, []);

  const handleVariablePriceValueChange = useCallback((event) => {
    const raw = String(event.target.value ?? '');

    if (raw === '') {
      setVariablePriceValue('');
      return;
    }

    const digitsOnly = raw.replace(/[^0-9]/g, '');
    setVariablePriceValue(digitsOnly);
  }, []);

  const confirmVariablePrice = useCallback(() => {
    if (!variablePriceProduct) return;

    const gross = Math.round(Number(variablePriceValue || 0));

    if (!Number.isFinite(gross) || gross <= 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Ingrese un valor bruto mayor que $0.',
      });
      return;
    }

    const normalized = normalizeProductForCart(variablePriceProduct);
    const pct = getItemTaxPercentForPayload(normalized);
    const split = splitGrossCLP(gross, pct);

    const pricedProduct = {
      ...variablePriceProduct,

      // Precio confirmado por caja. Para productos variables la línea representa
      // un monto bruto y se agrega con cantidad 1; la cantidad nunca se usa como monto.
      precio_bruto: gross,
      price: gross,
      precio: gross,
      gross,

      precio_neto: split.net,
      net: split.net,
      tax: split.tax,

      permite_precio_variable: true,
      precio_variable_confirmado: true,
      precio_variable_bruto: gross,
      variablePriceConfirmed: true,
      variablePriceGross: gross,
    };

    handleCloseVariablePriceModal();
    addProductToCart(pricedProduct, 1);
  }, [
    addProductToCart,
    handleCloseVariablePriceModal,
    normalizeProductForCart,
    variablePriceProduct,
    variablePriceValue,
  ]);

  const handleVariablePriceKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      confirmVariablePrice();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCloseVariablePriceModal();
    }
  }, [confirmVariablePrice, handleCloseVariablePriceModal]);

  const handleProductSearch = async (term) => {
    const q = String(term || '').trim();
    const requestSeq = ++productSearchRequestSeqRef.current;

    setProductSearchTerm(term);

    if (!q || q.length < 2) {
      setProductResults([]);
      setSuggestions([]);
      setTotalProductos(0);
      setSelectedIndex(-1);
      return;
    }

    try {
      const result = await fetchPosProductSearchRows(q, 30);

      // Evita que una respuesta anterior (por ejemplo "BU") pise una búsqueda
      // más nueva ("BUJIA") cuando el usuario escribe rápido.
      if (requestSeq !== productSearchRequestSeqRef.current) return;

      const normalizedRows = result.rows.map((row) => normalizeProductForCart(row));

      const total =
        Number(result.data?.totalProductos) ||
        Number(result.data?.total) ||
        Number(result.data?.count) ||
        normalizedRows.length;

      setProductResults(normalizedRows);
      setSuggestions(normalizedRows.slice(0, 8));
      setTotalProductos(total);
      setSelectedIndex(normalizedRows.length > 0 ? 0 : -1);
      setModalMode('search');

      if (result.source === 'cache' && normalizedRows.length === 0) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: `No se encontró "${q}" en el catálogo local del POS.`,
        });
      }
    } catch (error) {
      if (requestSeq !== productSearchRequestSeqRef.current) return;

      console.error('Error buscando productos:', error);

      setProductResults([]);
      setSuggestions([]);
      setTotalProductos(0);
      setSelectedIndex(-1);

      setSnackbar({
        open: true,
        severity: 'error',
        message:
          error.response?.data?.error ||
          error.response?.data?.message ||
          'No se pudo buscar productos.',
      });
    }
  };



  const handleSelectProduct = async (product) => {
    if (!product) return;

    const fullProduct = await fetchProductFullDetailsForPOS(product);

    addProductToCart(fullProduct, 1);
    handleCloseProductSearchModal();
  };

  const handleSelectSuggestion = async (product) => {
    if (!product) return;

    const fullProduct = await fetchProductFullDetailsForPOS(product);

    addProductToCart(fullProduct, 1);
    handleCloseProductSearchModal();
  };

  const handleKeyDownProductSearch = (event) => {
    const list = modalMode === 'suggestions' ? suggestions : productResults;

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (!list.length) return;

      setSelectedIndex((prev) => {
        if (prev < 0) return 0;
        return Math.min(prev + 1, list.length - 1);
      });

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (!list.length) return;

      setSelectedIndex((prev) => {
        if (prev <= 0) return 0;
        return prev - 1;
      });

      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (list.length > 0 && selectedIndex >= 0) {
        const selected = list[selectedIndex];

        if (modalMode === 'suggestions') {
          handleSelectSuggestion(selected);
        } else {
          handleSelectProduct(selected);
        }

        return;
      }

      if (productSearchTerm.trim()) {
        handleProductSearch(productSearchTerm);
      }

      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCloseProductSearchModal();
    }
  };

  const handleBarcodeLookup = useCallback(
    async (code) => {
      const q = String(code || '').trim();

      if (!q) return false;

      if (!isCajaAbierta) {
        setSnackbar({
          open: true,
          message: 'Debe abrir caja antes de agregar productos',
          severity: 'warning',
        });
        return false;
      }

      const weightedBarcode = parseWeightedBarcode(q);

      setLoading(true);

      try {
        if (weightedBarcode) {
          const result = await fetchPosProductSearchRows(weightedBarcode.plu, 20);
          const normalizedRows = result.rows.map((row) => normalizeProductForCart(row));

          const exactWeighted = normalizedRows.find((item) => {
            const raw = item.raw || item;
            return item.isWeight && productMatchesWeightedPlu(raw, weightedBarcode.plu);
          });

          const exactAny = normalizedRows.find((item) =>
            productMatchesWeightedPlu(item.raw || item, weightedBarcode.plu)
          );

          const candidate = exactWeighted || exactAny || null;

          if (!candidate) {
            setSnackbar({
              open: true,
              severity: 'warning',
              message: `No se encontró un producto para el PLU ${weightedBarcode.plu}${result.source === 'cache' ? ' en el catálogo local' : ''}.`,
            });
            return false;
          }

          const fullProduct = await fetchProductFullDetailsForPOS(candidate);
          const isWeighable = parseBoolish(
            fullProduct?.pesable ??
              fullProduct?.isWeight ??
              candidate?.pesable ??
              candidate?.isWeight,
            false
          );

          if (!isWeighable) {
            setSnackbar({
              open: true,
              severity: 'warning',
              message: `El PLU ${weightedBarcode.plu} corresponde a un producto que no está marcado como pesable.`,
            });
            return false;
          }

          return addWeightedProductToCart(
            {
              ...candidate,
              ...fullProduct,
              pesable: true,
              isWeight: true,
            },
            weightedBarcode
          );
        }

        const result = await fetchPosProductSearchRows(q, 10);
        const normalizedRows = result.rows.map((row) => normalizeProductForCart(row));

        const exact =
          normalizedRows.find((item) => String(item.codigo_barras || item.barcode || '') === q) ||
          normalizedRows.find((item) => String(item.sku || '') === q) ||
          normalizedRows[0];

        if (!exact) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: `No se encontró producto para el código ${q}${result.source === 'cache' ? ' en el catálogo local' : ''}.`,
          });
          return false;
        }

        const fullProduct = await fetchProductFullDetailsForPOS(exact);
        addProductToCart(fullProduct, 1);
        return true;
      } catch (error) {
        console.error('Error buscando producto por código:', error);

        setSnackbar({
          open: true,
          severity: 'error',
          message:
            error.response?.data?.error ||
            error.response?.data?.message ||
            'No se pudo buscar el producto.',
        });

        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      addProductToCart,
      addWeightedProductToCart,
      fetchProductFullDetailsForPOS,
      fetchPosProductSearchRows,
      isCajaAbierta,
      normalizeProductForCart,
    ]
  );



  /* ====================================================================
   * CLIENTES
   * ==================================================================== */
  const handleOpenCustomerModal = () => {
    setCustomerModalOpen(true);
    setCustomerSearch('');
    setCustomers([]);
    setTotalClientes(0);
    setSelectedCustomerIndex(-1);

    setTimeout(() => customerSearchRef.current?.focus?.(), 100);
  };

  const handleCustomerSearch = async (eventOrTerm) => {
    const value =
      eventOrTerm && eventOrTerm.target
        ? eventOrTerm.target.value
        : eventOrTerm;

    const q = String(value || '').trim();

    setCustomerSearch(value || '');

    if (!q || q.length < 2) {
      setCustomers([]);
      setTotalClientes(0);
      setSelectedCustomerIndex(-1);
      return;
    }

    setLoading(true);

    try {
      let rows = [];
      let total = 0;
      let source = 'cache';

      if (isOnlineRef.current && navigator.onLine) {
        try {
          const data = await getClients({
            q,
            search: q,
            buscar: q,
            limit: 30,
          });

          rows = Array.isArray(data)
            ? data
            : Array.isArray(data?.clientes)
              ? data.clientes
              : Array.isArray(data?.items)
                ? data.items
                : Array.isArray(data?.data)
                  ? data.data
                  : [];

          total =
            Number(data?.totalClientes) ||
            Number(data?.total) ||
            Number(data?.count) ||
            rows.length;

          source = 'api';
          isOnlineRef.current = true;
          setIsOnline(true);

          try {
            await cachePosCustomers(rows);
          } catch (cacheError) {
            console.warn('[POS] No se pudieron cachear clientes encontrados:', cacheError);
          }
        } catch (error) {
          if (!isRecoverablePosConnectivityError(error)) {
            throw error;
          }

          isOnlineRef.current = false;
          setIsOnline(false);
          rows = await searchCachedPosCustomers(q, 30);
          total = rows.length;
        }
      } else {
        rows = await searchCachedPosCustomers(q, 30);
        total = rows.length;
      }

      setCustomers(rows);
      setTotalClientes(total);
      setSelectedCustomerIndex(rows.length > 0 ? 0 : -1);

      if (source === 'cache' && rows.length === 0) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'Cliente no encontrado en la copia local. En contingencia solo están disponibles clientes previamente cacheados.',
        });
      }
    } catch (error) {
      console.error('Error buscando clientes:', error);

      setCustomers([]);
      setTotalClientes(0);
      setSelectedCustomerIndex(-1);

      setSnackbar({
        open: true,
        severity: 'error',
        message:
          error.response?.data?.error ||
          error.response?.data?.message ||
          'No se pudo buscar clientes.',
      });
    } finally {
      setLoading(false);
    }
  };


  const handleSelectCustomer = async (selected) => {
    if (!selected) return;

    let resolvedCustomer = selected;
    const selectedId = positiveIntOrNull(selected.id ?? selected.id_cliente);

    /*
     * La búsqueda de clientes puede devolver una fila resumida. Para Factura 33
     * necesitamos la ficha completa porque las direcciones vienen normalmente en:
     * direccion_despacho / direccion_facturacion / direccion_principal / direcciones[].
     * Al seleccionar el cliente intentamos enriquecerlo desde el endpoint de detalle.
     */
    if (selectedId && isOnlineRef.current && navigator.onLine) {
      try {
        const response = await api.get(`/ventas_clientes/${selectedId}`, {
          timeout: POS_FAST_API_TIMEOUT_MS,
        });

        const detail =
          response.data?.cliente ||
          response.data?.customer ||
          response.data ||
          null;

        if (detail && positiveIntOrNull(detail.id ?? detail.id_cliente)) {
          resolvedCustomer = {
            ...selected,
            ...detail,
          };
        }
      } catch (error) {
        console.warn('[POS] No se pudo cargar ficha completa del cliente:', error);

        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);
        }
      }
    }

    cachePosCustomers([resolvedCustomer]).catch((error) => {
      console.warn('[POS] No se pudo cachear cliente seleccionado:', error);
    });

    setCustomer(resolvedCustomer);
    setCustomerModalOpen(false);
    setCustomerSearch('');
    setCustomers([]);
    setTotalClientes(0);
    setSelectedCustomerIndex(-1);

    const allowed = getAllowedDocTypes(resolvedCustomer);

    let nextDocumentType = documentType;

    if (
      pendingDocumentTypeAfterCustomer &&
      allowed.includes(pendingDocumentTypeAfterCustomer)
    ) {
      nextDocumentType = pendingDocumentTypeAfterCustomer;
    } else if (!allowed.includes(documentType)) {
      nextDocumentType = allowed.includes('boleta') ? 'boleta' : allowed[0] || 'boleta';
    }

    setDocumentType(nextDocumentType);

    if (nextDocumentType === 'factura') {
      const transferDefaults = createInvoiceTransferData({
        cajaData,
        customer: resolvedCustomer,
      });

      setInvoiceDeliveryMode(INVOICE_DELIVERY_MODE.RETIRO_CLIENTE);
      setInvoiceTransferData({
        ...transferDefaults,
        transportista_tipo: TRANSPORTER_TYPE.COMPRADOR,
        transportista_rut: formatRutChile(resolvedCustomer?.rut),
        transportista_nombre: getCustomerDisplayName(resolvedCustomer),
      });
      setInvoiceTransferDialogOpen(true);
    }

    setPendingDocumentTypeAfterCustomer(null);

    const destination = getCustomerInvoiceDestination(resolvedCustomer);
    const destinationSuffix = destination.direccion
      ? ` · Destino preparado: ${destination.direccion}${
          destination.comuna ? `, ${destination.comuna}` : ''
        }`
      : '';

    setSnackbar({
      open: true,
      severity: 'success',
      message: `Cliente seleccionado: ${
        getCustomerDisplayName(resolvedCustomer) || resolvedCustomer.rut || ''
      }${destinationSuffix}`,
    });
  };

  const handleClearCustomer = () => {
    setInvoiceDeliveryMode('');
    setInvoiceTransferData(
      createInvoiceTransferData({
        cajaData,
        customer: defaultCustomer || null,
      })
    );
    setInvoiceTransferDialogOpen(false);

    if (defaultCustomer) {
      setCustomer(defaultCustomer);
      setDocumentType('boleta');
    } else {
      setCustomer(null);
      setDocumentType('boleta');
    }
  };

  const handleKeyDownCustomer = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (!customers.length) return;

      setSelectedCustomerIndex((prev) => {
        if (prev < 0) return 0;
        return Math.min(prev + 1, customers.length - 1);
      });

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (!customers.length) return;

      setSelectedCustomerIndex((prev) => {
        if (prev <= 0) return 0;
        return prev - 1;
      });

      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (customers.length > 0 && selectedCustomerIndex >= 0) {
        handleSelectCustomer(customers[selectedCustomerIndex]);
        return;
      }

      if (customerSearch.trim()) {
        handleCustomerSearch(customerSearch);
      }

      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setCustomerModalOpen(false);
    }
  };

  /* ====================================================================
   * ÍTEMS: eliminar / editar cantidad
   * ==================================================================== */

  const handleOpenEditQtyModal = (item, index) => {
    setItemToEditQty({
      item,
      index,
    });

    setEditQtyValue(String(item.quantity || 1));
    setEditQtyModalOpen(true);
  };

  const handleCloseEditQtyModal = () => {
    setEditQtyModalOpen(false);
    setItemToEditQty(null);
    setEditQtyValue('');
  };

  const handleEditQtyValueChange = (event) => {
    const value = event.target.value;

    if (value === '') {
      setEditQtyValue('');
      return;
    }

    if (!/^\d+([.,]\d{0,3})?$/.test(value)) return;

    setEditQtyValue(value.replace(',', '.'));
  };

  const applyEditQty = () => {
    if (!itemToEditQty || itemToEditQty.index == null) return;

    const qty = Number(editQtyValue);

    if (!Number.isFinite(qty) || qty <= 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Ingrese una cantidad válida.',
      });
      return;
    }

    setItems((prev) => {
      const current = prev[itemToEditQty.index];

      if (current?.isWeight) {
        return moveItemToTop(
          prev,
          itemToEditQty.index,
          buildWeightedCartLine(current, qty)
        );
      }

      return moveItemToTop(prev, itemToEditQty.index, {
        quantity: qty,
      });
    });

    setAuthorizedRuleIds({});
    handleCloseEditQtyModal();

    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Cantidad actualizada.',
    });
  };

  const handleEditQtyKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleRequestEditQtyFromModal();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCloseEditQtyModal();
    }
  };

  const handleRequestEditQty = (item, index) => {
    if (parseBoolish(item?.permite_precio_variable, false)) {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'En productos de precio variable la cantidad se mantiene en 1. Para otro monto, agregue el producto nuevamente.',
      });
      return;
    }

    setItemToEditQty({
      item,
      index,
    });

    setEditQtyValue(String(item.quantity || 1));
    setEditQtyPinOpen(true);
  };

  const handleConfirmEditQtyPin = () => {
    setEditQtyPinOpen(false);
    setEditQtyModalOpen(true);
  };

  const handleCancelEditQtyPin = () => {
    setEditQtyPinOpen(false);
    setItemToEditQty(null);
    setEditQtyValue('');
  };

const requestLineActionPin = useCallback((action) => {
  setPendingLineAction(action);
  setLineActionPinOpen(true);
}, []);

const handleDeleteItemClick = useCallback((item, index) => {
  requestLineActionPin({
    type: 'delete',
    item,
    index,
    title: 'Autorizar eliminación',
    message: `Ingresa PIN para eliminar "${getItemDisplayName(item)}" de la venta.`,
  });
}, [requestLineActionPin]);

const requestIncrementItemQuantity = useCallback((item, index) => {
  if (parseBoolish(item?.permite_precio_variable, false)) {
    setSnackbar({
      open: true,
      severity: 'info',
      message: 'En productos de precio variable la cantidad se mantiene en 1. Agregue otra línea para ingresar otro monto.',
    });
    return;
  }

  setItems((prev) => {
    const current = prev[index];

    if (!current) return prev;

    return moveItemToTop(prev, index, {
      quantity: Number(current.quantity || 0) + 1,
    });
  });

  setAuthorizedRuleIds({});

  setSnackbar({
    open: true,
    severity: 'success',
    message: 'Cantidad aumentada.',
  });
}, [moveItemToTop]);

const requestDecrementItemQuantity = useCallback((item, index) => {
  if (parseBoolish(item?.permite_precio_variable, false)) {
    requestLineActionPin({
      type: 'delete',
      item,
      index,
      title: 'Autorizar eliminación',
      message: `Ingresa PIN para eliminar "${getItemDisplayName(item)}" de la venta.`,
    });
    return;
  }

  requestLineActionPin({
    type: 'decrement',
    item,
    index,
    title: 'Autorizar reducción de cantidad',
    message: `Ingresa PIN para reducir la cantidad de "${getItemDisplayName(item)}".`,
  });
}, [requestLineActionPin]);

const handleCloseLineActionPin = useCallback(() => {
  setLineActionPinOpen(false);
  setPendingLineAction(null);
}, []);

const applyLineActionAfterPin = useCallback(() => {
  if (!pendingLineAction || pendingLineAction.index == null) {
    handleCloseLineActionPin();
    return;
  }

  const { type, index } = pendingLineAction;

  if (type === 'delete') {
    setItems((prev) => relabelVariablePriceCartItems(prev.filter((_, i) => i !== index)));

    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Producto eliminado.',
    });
  }

  if (type === 'increment') {
    setItems((prev) => {
      const current = prev[index];

      if (!current) return prev;

      return moveItemToTop(prev, index, {
        quantity: Number(current.quantity || 0) + 1,
      });
    });

    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Cantidad aumentada.',
    });
  }

  if (type === 'decrement') {
    setItems((prev) => {
      const current = prev[index];

      if (!current) return prev;

      const nextQty = Number(current.quantity || 0) - 1;

      if (nextQty <= 0) {
        return relabelVariablePriceCartItems(prev.filter((_, i) => i !== index));
      }

      return moveItemToTop(prev, index, {
        quantity: nextQty,
      });
    });

    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Cantidad reducida.',
    });
  }

  setAuthorizedRuleIds({});
  handleCloseLineActionPin();
}, [pendingLineAction, handleCloseLineActionPin, moveItemToTop]);


  /* ====================================================================
   * Atajos globales simples
   * ==================================================================== */
  useEffect(() => {
    const handleGlobalKeyDown = async (event) => {
      const target = event.target;
      const tagName = String(target?.tagName || '').toLowerCase();
      const isTyping =
        tagName === 'input' ||
        tagName === 'textarea' ||
        target?.isContentEditable;

      if (
        productSearchModalOpen ||
        customerModalOpen ||
        paymentModalOpen ||
        invoiceTransferDialogOpen ||
        cajaModalOpen ||
        editQtyModalOpen ||
        editQtyPinOpen ||
        variablePriceModalOpen ||
        reprintPinOpen ||
        ventasPendientesOpen ||
        ventasContingenciaOpen ||
        pendingRuleAuthorization
      ) {
        return;
      }

       if (event.key === 'F1') {
        event.preventDefault();
        handleSolicitarNuevaVenta();
        return;
      }

      if (event.key === 'F2') {
        event.preventDefault();
        setConsultorPreciosOpen(true);
        return;
      }

      if (event.key === 'F3') {
        event.preventDefault();
        handleOpenProductSearchModal('search');
        return;
      }

      if (event.key === 'F4') {
        event.preventDefault();
        handleOpenImportModal();
        return;
      }

      if (event.key === 'F6') {
        event.preventDefault();
        handleGuardarVentaEnEspera();
        return;
      }

      if (event.key === 'F7') {
        event.preventDefault();
        handleOpenVentasPendientes();
        return;
      }

      if (event.key === 'F8') {
        event.preventDefault();
        handleOpenCustomerModal();
        return;
      }

      if (event.key === 'F9') {
        event.preventDefault();
        handleOpenPaymentModal();
        return;
      }

      if (event.key === 'F10') {
        event.preventDefault();
        handleOpenMovimientosCaja();
        return;
      }

      if (event.key === 'F11') {
        event.preventDefault();
        openVentasContingencia();
        return;
      }

      if (isTyping) return;

      if (event.key === 'Enter') {
        const code = String(barcodeBuffer || '').trim();

        if (code.length >= 3) {
          event.preventDefault();
          setBarcodeBuffer('');
          await handleBarcodeLookup(code);
        }

        return;
      }

      if (/^[0-9A-Za-z]$/.test(event.key)) {
        setBarcodeBuffer((prev) => `${prev}${event.key}`);
      }

      if (event.key === 'Backspace') {
        setBarcodeBuffer((prev) => prev.slice(0, -1));
      }

      if (event.key === 'Escape') {
        setBarcodeBuffer('');
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [
    barcodeBuffer,
    cajaModalOpen,
    customerModalOpen,
    editQtyModalOpen,
    editQtyPinOpen,
    variablePriceModalOpen,
    handleBarcodeLookup,
    handleOpenMovimientosCaja,
    items.length,
    openVentasContingencia,
    paymentModalOpen,
    invoiceTransferDialogOpen,
    pendingRuleAuthorization,
    productSearchModalOpen,
    reprintPinOpen,
    ventasContingenciaOpen,
    ventasPendientesOpen,
  ]);

  /* ====================================================================
   * NUEVA VENTA / VENTA EN ESPERA
   * ==================================================================== */
  const [nuevaVentaPinOpen, setNuevaVentaPinOpen] = useState(false);
  const [pendingPaymentAfterRuleAuth, setPendingPaymentAfterRuleAuth] = useState(null);

  const hasActiveSale = useMemo(() => {
    return (
      items.length > 0 ||
      Boolean(importSource) ||
      Boolean(customer && defaultCustomer && customer.id !== defaultCustomer.id) ||
      documentType !== 'boleta'
    );
  }, [items.length, importSource, customer, defaultCustomer, documentType]);

  const handleSolicitarNuevaVenta = useCallback(() => {
    if (!hasActiveSale) {
      clearSale();
      return;
    }

    setNuevaVentaPinOpen(true);
  }, [clearSale, hasActiveSale]);

  const handleCerrarNuevaVentaPin = useCallback(() => {
    setNuevaVentaPinOpen(false);
  }, []);

  const handleNuevaVentaAutorizada = useCallback(() => {
    setNuevaVentaPinOpen(false);
    clearSale();

    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Nueva venta iniciada.',
    });
  }, [clearSale]);

  const handleGuardarVentaEnEspera = useCallback(() => {
    if (!items.length) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No hay productos para dejar en espera.',
      });
      return;
    }

    const venta = {
      id: crearFolioVentaEnEspera(),
      folio: crearFolioVentaEnEspera(),
      createdAt: new Date().toISOString(),

      items,
      customer,
      documentType,
      invoiceDeliveryMode,
      invoiceTransferData,
      importSource,
      authorizedRuleIds,
      clientSaleId: getOrCreateClientSaleId(),

      totals: {
        baseTotalNet,
        baseTotalTax,
        baseTotalGross,
        totalNet,
        totalTax,
        totalGross,
        totalPayable,
        totalRulesAdjustment,
      },

      reglas_pos: rulesSnapshot,
    };

    const next = [venta, ...ventasEnEspera];

    guardarVentasEnEspera(next);
    setVentasEnEspera(next);

    clearSale();

    setSnackbar({
      open: true,
      severity: 'success',
      message: `Venta ${venta.folio} dejada en espera.`,
    });
  }, [
    items,
    customer,
    documentType,
    invoiceDeliveryMode,
    invoiceTransferData,
    importSource,
    authorizedRuleIds,
    baseTotalNet,
    baseTotalTax,
    baseTotalGross,
    totalNet,
    totalTax,
    totalGross,
    totalPayable,
    totalRulesAdjustment,
    rulesSnapshot,
    ventasEnEspera,
    clearSale,
    getOrCreateClientSaleId,
  ]);

  const handleRetomarVentaEnEspera = useCallback(
    (venta) => {
      if (!venta) return;

      if (items.length > 0) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message:
            'Ya existe una venta en curso. Primero déjala en espera o inicia una nueva venta.',
        });
        return;
      }

      setItems(Array.isArray(venta.items) ? venta.items : []);
      setCustomer(venta.customer || defaultCustomer || null);
      setDocumentType(venta.documentType || 'boleta');
      setInvoiceDeliveryMode(venta.invoiceDeliveryMode || '');
      setInvoiceTransferData(
        mergeInvoiceTransferDefaults(
          venta.invoiceTransferData || {},
          createInvoiceTransferData({
            cajaData,
            customer: venta.customer || defaultCustomer || null,
          })
        )
      );
      setImportSource(venta.importSource || null);
      setAuthorizedRuleIds(venta.authorizedRuleIds || venta.reglas_pos?.autorizaciones || {});
      clientSaleIdRef.current = venta.clientSaleId || createClientSaleId();

      const next = ventasEnEspera.filter((row) => row.id !== venta.id);

      guardarVentasEnEspera(next);
      setVentasEnEspera(next);
      setVentasPendientesOpen(false);

      setSnackbar({
        open: true,
        severity: 'success',
        message: `Venta ${venta.folio || venta.id} retomada.`,
      });
    },
    [items.length, ventasEnEspera, defaultCustomer, cajaData]
  );

  const handleSolicitarEliminarVentaEnEspera = useCallback((venta) => {
    setVentaEnEsperaDelete(venta);
    setVentaEnEsperaDeletePinOpen(true);
  }, []);

  const handleCancelarEliminarVentaEnEspera = useCallback(() => {
    setVentaEnEsperaDelete(null);
    setVentaEnEsperaDeletePinOpen(false);
  }, []);

  const handleEliminarVentaEnEsperaAutorizada = useCallback(() => {
    if (!ventaEnEsperaDelete) {
      setVentaEnEsperaDeletePinOpen(false);
      return;
    }

    const next = ventasEnEspera.filter((row) => row.id !== ventaEnEsperaDelete.id);

    guardarVentasEnEspera(next);
    setVentasEnEspera(next);
    setVentaEnEsperaDelete(null);
    setVentaEnEsperaDeletePinOpen(false);

    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Venta en espera eliminada.',
    });
  }, [ventaEnEsperaDelete, ventasEnEspera]);

  const handleLimpiarVentasEnEspera = useCallback(() => {
    const ok = window.confirm('¿Eliminar todas las ventas en espera?');

    if (!ok) return;

    guardarVentasEnEspera([]);
    setVentasEnEspera([]);

    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Ventas en espera eliminadas.',
    });
  }, []);

  /* ====================================================================
   * IMPORTAR DOCUMENTO A POS
   * ==================================================================== */
  const toNum = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

const pickLine = useCallback((line) => {
  const qty = Math.max(0.000001, toNum(line?.cantidad ?? line?.qty ?? line?.quantity ?? 1, 1));

  const positiveProductId = positiveIntOrNull(
    line?.id_producto ??
      line?.producto_id ??
      line?.product_id ??
      line?.idProducto ??
      line?.producto?.id ??
      line?.producto?.id_producto
  );

  const automotrizItemId = positiveIntOrNull(
    line?.mantencion_item_id ?? line?.automotriz_item_id ?? line?.id
  );

  const isAutomotriz = Boolean(
    automotrizItemId &&
    (
      line?.tipo_item != null ||
      line?.afecta_inventario != null ||
      line?.es_item_generico != null ||
      line?.reserva_id != null
    )
  );

  const pedidoItemId =
    positiveIntOrNull(
      line?.pedido_item_id ??
        line?.pedidoItemId ??
        line?.id_pedido_item ??
        line?.id_item_pedido ??
        line?.linea_pedido_id ??
        line?.id_linea_pedido
    ) ||
    (
      positiveIntOrNull(line?.id) &&
      (
        line?.pedido_id != null ||
        line?.cantidad_pendiente != null ||
        line?.cantidad_facturada != null ||
        line?.estado_facturacion != null
      )
        ? positiveIntOrNull(line.id)
        : null
    );

  const pedidoId = positiveIntOrNull(
    line?.pedido_id ?? line?.pedidoId ?? line?.id_pedido ?? line?.pedido?.id
  );

  const impuestoLinea = toNum(
    line?.total_impuestos ?? line?.impuesto ?? line?.iva ?? line?.tax ?? 0,
    0
  );

  let net = null;
  if (line?.precio_unit_neto != null) net = toNum(line.precio_unit_neto, 0);
  else if (line?.neto_unit_final != null) net = toNum(line.neto_unit_final, 0);
  else if (line?.precio_unitario_neto != null) net = toNum(line.precio_unitario_neto, 0);
  else if (line?.precio_unitario != null) net = toNum(line.precio_unitario, 0);
  else if (line?.precio_neto != null) net = toNum(line.precio_neto, 0);
  else if (line?.subtotal_neto != null && qty) net = Math.max(0, toNum(line.subtotal_neto, 0) / qty);
  else net = 0;

  let pct =
    line?.tasa_impuesto_total != null
      ? toNum(line.tasa_impuesto_total, 0)
      : line?.pct_impuestos != null
        ? toNum(line.pct_impuestos, 0)
        : line?.impuesto_pct != null
          ? toNum(line.impuesto_pct, 0)
          : line?.iva_pct != null
            ? toNum(line.iva_pct, 0)
            : line?.pct_iva != null
              ? toNum(line.pct_iva, 0)
              : null;

  const netUnit = Number(toNum(net, 0).toFixed(8));
  const netLine = netUnit * qty;
  if ((pct == null || pct === 0) && impuestoLinea > 0 && netLine > 0) {
    pct = Number(((impuestoLinea / netLine) * 100).toFixed(2));
  }
  const finalPct = pct == null ? 19 : pct;

  let discountNetLine;
  let discountNetUnit;

  if (isAutomotriz && line?.descuento_neto != null) {
    discountNetLine = Math.max(0, toNum(line.descuento_neto, 0));
    discountNetUnit = qty > 0 ? discountNetLine / qty : 0;
  } else {
    discountNetUnit = Math.max(
      0,
      toNum(
        line?.descuento ??
          line?.descuento_unit ??
          line?.desc_unit_monto ??
          line?.item_raw?.descuento ??
          0,
        0
      )
    );
    discountNetLine = Number((discountNetUnit * qty).toFixed(8));
  }

  const discountGrossLine = Math.round(
    discountNetLine * (1 + Number(finalPct || 0) / 100)
  );

  return {
    productId: positiveProductId,
    cartId: positiveProductId || (automotrizItemId ? -automotrizItemId : null),
    pedidoItemId,
    pedidoId,
    automotrizItemId,
    isAutomotriz,
    afectaInventario: isAutomotriz
      ? parseBoolish(line?.afecta_inventario, false)
      : true,
    bodegaId: positiveIntOrNull(line?.bodega_id ?? line?.id_bodega),
    reservaId: positiveIntOrNull(line?.reserva_id),
    tipoItem: line?.tipo_item || line?.tipo || null,
    esItemGenerico: Boolean(line?.es_item_generico || (isAutomotriz && !positiveProductId)),

    qty,
    net: netUnit,
    pct: finalPct,

    name:
      line?.nombre ||
      line?.producto_nombre ||
      line?.name ||
      line?.descripcion ||
      line?.producto?.nombre ||
      '',

    description: line?.descripcion || line?.nombre || line?.producto_nombre || '',
    sku: line?.sku || line?.producto?.sku || '',
    barcode: getProductBarcode(line),
    stockHint: line?.stock_actual,

    discountNetUnit,
    discountNetLine,
    discountGrossLine,

    cantidadOriginal: line?.cantidad_original ?? line?.cantidad ?? null,
    cantidadFacturada: line?.cantidad_facturada ?? line?.facturada ?? null,
    cantidadPendiente: line?.cantidad_pendiente ?? line?.pendiente ?? null,

    raw: line,
  };
}, []);

const importDocToPOS = useCallback(
  async (sourceType, docId, importOptions = {}) => {
    if (!isCajaAbierta) {
      setSnackbar({ open: true, severity: 'warning', message: 'Debe abrir caja antes de importar documentos.' });
      return;
    }

    if (!isOnline) {
      setSnackbar({ open: true, severity: 'warning', message: 'No se puede importar en modo contingencia.' });
      return;
    }

    if (items.length > 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Hay una venta en curso. Primero déjala en espera o inicia una nueva venta.',
      });
      return;
    }

    try {
      setLoading(true);

      const sourceTypeNorm = String(sourceType || '').trim().toLowerCase();
      const esMantencion = sourceTypeNorm === 'automotriz_mantencion';
      const esPedidoPendienteDteInicial =
        sourceTypeNorm === 'pedido_pendiente_dte' ||
        importOptions?.modo_importacion === 'pedido_pendiente_dte' ||
        importOptions?.pedido?.modo_importacion === 'pedido_pendiente_dte' ||
        importOptions?.puede_emitir_pendientes === true;

      let header = null;
      let lineas = [];
      let importedDocData = null;
      let maintenanceContext = null;

      if (esMantencion) {
        if (importOptions?.pos_contexto) {
          importedDocData = importOptions?.detalle || { pos_contexto: importOptions.pos_contexto };
          maintenanceContext = importOptions.pos_contexto;
        } else {
          const { data } = await api.get(`/automotriz_mantenciones/${docId}/pos-contexto`, { timeout: POS_FAST_API_TIMEOUT_MS });
          importedDocData = data;
          maintenanceContext = data?.pos_contexto || data?.contexto || data;
        }

        header = {
          ...(maintenanceContext || {}),
          id: Number(maintenanceContext?.mantencion_id || docId),
          cliente_id: maintenanceContext?.cliente_id || maintenanceContext?.customer_id || null,
          total: maintenanceContext?.totales?.total || 0,
          total_neto: maintenanceContext?.totales?.subtotal_neto || 0,
          total_impuestos: maintenanceContext?.totales?.impuestos || 0,
        };
        lineas = Array.isArray(maintenanceContext?.items) ? maintenanceContext.items : [];
      } else if (sourceTypeNorm === 'cotizacion') {
        const { data } = await api.get(`/ventas_cotizaciones/${docId}`, { timeout: POS_FAST_API_TIMEOUT_MS });
        importedDocData = data;
        header = data?.cotizacion || data?.quote || data?.pedido || data?.order || data?.header || data;
        lineas = data?.items || data?.lineas || data?.detalle || data?.cotizacion?.items || data?.pedido?.items || [];
      } else {
        const { data } = await api.get(`/ventas_pedidos/${docId}`, { timeout: POS_FAST_API_TIMEOUT_MS });
        importedDocData = data;
        header = data?.pedido || data?.order || data?.header || data;

        const modoImportacionDetalle =
          data?.modo_importacion ||
          data?.facturacion?.modo_importacion ||
          data?.estados?.facturacion?.modo_importacion ||
          importOptions?.modo_importacion ||
          '';

        const onlyPending =
          esPedidoPendienteDteInicial ||
          modoImportacionDetalle === 'pedido_pendiente_dte' ||
          data?.estado_facturacion === 'DTE_PARCIAL_PENDIENTE' ||
          data?.facturacion?.codigo === 'DTE_PARCIAL_PENDIENTE' ||
          data?.puede_emitir_pendientes === true ||
          data?.facturacion?.puede_emitir_pendientes === true;

        if (onlyPending) {
          lineas =
            (Array.isArray(data?.items_para_pos) && data.items_para_pos.length
              ? data.items_para_pos
              : Array.isArray(data?.items_pendientes_facturacion) && data.items_pendientes_facturacion.length
                ? data.items_pendientes_facturacion
                : Array.isArray(data?.facturacion?.items_pendientes) && data.facturacion.items_pendientes.length
                  ? data.facturacion.items_pendientes
                  : Array.isArray(data?.estados?.facturacion?.items_pendientes)
                    ? data.estados.facturacion.items_pendientes
                    : []);

          if (!lineas.length) {
            setSnackbar({ open: true, severity: 'warning', message: 'El pedido no tiene ítems pendientes de facturación para cargar al POS.' });
            return;
          }
        } else {
          lineas = data?.items || data?.lineas || data?.detalle || data?.pedido?.items || [];
        }
      }

      if (!header) throw new Error('No se encontró el encabezado del documento.');

      const customerId =
        header?.cliente_id ??
        header?.customer_id ??
        header?.cliente?.id ??
        importedDocData?.cliente?.id ??
        importedDocData?.pedido_raw?.cliente_id ??
        null;

      if (!positiveIntOrNull(customerId)) throw new Error('Documento sin cliente asociado.');

      const customerResponse = await api.get(`/ventas_clientes/${Number(customerId)}`, { timeout: POS_FAST_API_TIMEOUT_MS });
      const fetchedCustomer = customerResponse.data?.cliente || customerResponse.data?.customer || customerResponse.data;
      if (!fetchedCustomer?.id) throw new Error('No se pudo cargar el cliente del documento.');

      const esImportacionPendienteDte =
        !esMantencion &&
        (
          esPedidoPendienteDteInicial ||
          sourceTypeNorm === 'pedido_pendiente_dte' ||
          importedDocData?.modo_importacion === 'pedido_pendiente_dte' ||
          importedDocData?.facturacion?.modo_importacion === 'pedido_pendiente_dte' ||
          importedDocData?.estado_facturacion === 'DTE_PARCIAL_PENDIENTE' ||
          importedDocData?.facturacion?.codigo === 'DTE_PARCIAL_PENDIENTE' ||
          importedDocData?.puede_emitir_pendientes === true ||
          importedDocData?.facturacion?.puede_emitir_pendientes === true
        );

      const groups = new Map();

      for (const rawLine of lineas || []) {
        const line = pickLine(rawLine);

        if (!line.productId && !(esMantencion && line.automotrizItemId)) continue;

        const groupKey = esMantencion
          ? `mantencion_item:${line.automotrizItemId}`
          : esImportacionPendienteDte && line.pedidoItemId
            ? `pedido_item:${line.pedidoItemId}`
            : `producto:${line.productId}`;

        const previous = groups.get(groupKey);
        if (!previous) {
          groups.set(groupKey, { ...line });
        } else {
          groups.set(groupKey, {
            ...previous,
            qty: Number(previous.qty || 0) + Number(line.qty || 0),
            discountNetLine: Number(previous.discountNetLine || 0) + Number(line.discountNetLine || 0),
            discountGrossLine: Number(previous.discountGrossLine || 0) + Number(line.discountGrossLine || 0),
          });
        }
      }

      if (esImportacionPendienteDte) {
        const missingIds = Array.from(groups.values()).filter((line) => !positiveIntOrNull(line.pedidoItemId));
        if (missingIds.length) throw new Error('El endpoint no entregó el ID de línea del pedido para uno o más ítems pendientes.');
      }

      const importedItems = [];

      for (const line of groups.values()) {
        const grossUnit = Math.round(Number(line.net || 0) * (1 + Number(line.pct || 0) / 100));
        const netUnit = Number(Number(line.net || 0).toFixed(8));
        const taxUnit = Math.max(0, grossUnit - netUnit);
        const pedidoItemId = positiveIntOrNull(line.pedidoItemId);
        const pedidoId = !esMantencion ? positiveIntOrNull(line.pedidoId) || Number(docId) : null;
        const cartId = line.cartId || line.productId;

        importedItems.push({
          ...normalizeProductForCart({
            id: cartId,
            id_producto: cartId,
            nombre: line.name,
            sku: line.sku,
            codigo_barras: line.barcode,
            precio_neto: netUnit,
            precio_bruto: grossUnit,
            stock_actual: line.stockHint ?? null,
          }),

          id: cartId,
          product_id: line.productId || null,
          id_producto: line.productId || null,
          quantity: Number(line.qty || 1),
          net: netUnit,
          tax: taxUnit,
          gross: grossUnit,
          taxes: [{ porcentaje: Number(line.pct || 0) }],

          imported_discount_net_unit: Number(line.discountNetUnit || 0),
          imported_discount_net_line: Number(line.discountNetLine || 0),
          imported_discount_gross_line: Number(line.discountGrossLine || 0),

          pedido_item_id: pedidoItemId,
          source_item_id: pedidoItemId,
          source_item_type: pedidoItemId ? 'pedido_item' : null,
          pedido_id: pedidoId,

          mantencion_item_id: positiveIntOrNull(line.automotrizItemId),
          automotriz_item_id: positiveIntOrNull(line.automotrizItemId),
          tipo_origen: esMantencion ? 'automotriz_mantencion' : null,
          tipo_item: line.tipoItem || null,
          afecta_inventario: esMantencion ? Boolean(line.afectaInventario) : true,
          bodega_id: positiveIntOrNull(line.bodegaId),
          reserva_id: positiveIntOrNull(line.reservaId),
          es_item_generico: Boolean(line.esItemGenerico),
          descripcion: line.description || line.name,

          cantidad_original: line.cantidadOriginal ?? null,
          cantidad_facturada: line.cantidadFacturada ?? null,
          cantidad_pendiente: line.cantidadPendiente ?? null,

          item_raw: line.raw,
          raw: line.raw,
        });
      }

      if (!importedItems.length) throw new Error('El documento no tiene ítems válidos para importar.');

      const totalItemsImportados = importedItems.reduce(
        (sum, item) => sum + Number(item.gross || 0) * Number(item.quantity || 0),
        0
      );

      const totalDocumento = esImportacionPendienteDte
        ? totalItemsImportados
        : toNum(
            maintenanceContext?.totales?.total ??
              header?.total_bruto ??
              header?.total ??
              header?.monto_total,
            totalItemsImportados
          ) || totalItemsImportados;

      const pagoOrigen =
        sourceTypeNorm === 'pedido' || sourceTypeNorm === 'pedido_pendiente_dte'
          ? extractPagoOrigenFromPedidoResponse(importedDocData, header, totalDocumento)
          : null;

      setItems(importedItems);
      setCustomer(fetchedCustomer);

      const importedDocumentType = getAllowedDocTypes(fetchedCustomer).includes('factura')
        ? 'factura'
        : 'boleta';

      setDocumentType(importedDocumentType);

      if (importedDocumentType === 'factura') {
        const transferDefaults = createInvoiceTransferData({
          cajaData,
          customer: fetchedCustomer,
        });

        setInvoiceDeliveryMode(INVOICE_DELIVERY_MODE.RETIRO_CLIENTE);
        setInvoiceTransferData({
          ...transferDefaults,
          transportista_tipo: TRANSPORTER_TYPE.COMPRADOR,
          transportista_rut: formatRutChile(fetchedCustomer?.rut),
          transportista_nombre: getCustomerDisplayName(fetchedCustomer),
        });
        setInvoiceTransferDialogOpen(true);
      } else {
        setInvoiceDeliveryMode('');
        setInvoiceTransferDialogOpen(false);
      }

      setImportSource({
        type: esMantencion
          ? 'automotriz_mantencion'
          : esImportacionPendienteDte
            ? 'pedido_pendiente_dte'
            : sourceTypeNorm,
        id: Number(docId),
        header,
        raw: importedDocData,
        pago_origen: pagoOrigen,
        mantencion_id: esMantencion ? Number(docId) : null,
        vehiculo: maintenanceContext?.metadata?.vehiculo || null,
        modo_importacion: esMantencion
          ? 'automotriz_mantencion'
          : importedDocData?.modo_importacion ||
            importedDocData?.facturacion?.modo_importacion ||
            importOptions?.modo_importacion ||
            (esImportacionPendienteDte ? 'pedido_pendiente_dte' : sourceTypeNorm),
        puede_emitir_pendientes: esImportacionPendienteDte,
        items_para_pos: lineas,
        items_pendientes_facturacion: esImportacionPendienteDte ? lineas : [],
      });

      setImportModalOpen(false);
      isOnlineRef.current = true;
      setIsOnline(true);
      setSnackbar({
        open: true,
        severity: 'success',
        message: esMantencion
          ? `Mantención cargada al POS con ${importedItems.length} ítem(s).`
          : esImportacionPendienteDte
            ? `Pedido parcial importado con ${importedItems.length} ítem(s) pendiente(s).`
            : sourceTypeNorm === 'pedido'
              ? `Pedido importado al POS${pagoOrigen?.existe ? ' con pago de origen detectado.' : '.'}`
              : 'Cotización importada al POS.',
      });
    } catch (error) {
      console.error('Error importando documento al POS:', error);
      if (isRecoverablePosConnectivityError(error)) {
        isOnlineRef.current = false;
        setIsOnline(false);
      }
      setSnackbar({
        open: true,
        severity: 'error',
        message: error.response?.data?.error || error.response?.data?.message || error.message || 'No se pudo importar el documento.',
      });
    } finally {
      setLoading(false);
    }
  },
  [isCajaAbierta, isOnline, items.length, normalizeProductForCart, pickLine, cajaData]
);

  /* ====================================================================
   * VALIDACIONES Y AUTORIZACIÓN DE REGLAS POS
   * ==================================================================== */
  const getPendingRuleForAuthorization = useCallback(() => {
    const pending = Array.isArray(posRulesEvaluation?.reglasPendientesPin)
      ? posRulesEvaluation.reglasPendientesPin
      : [];

    return pending[0] || null;
  }, [posRulesEvaluation]);

  const ensureRulesAuthorizedBeforePayment = useCallback(
    (paymentRequest = {}) => {
      const pending = getPendingRuleForAuthorization();

      if (!pending) return true;

      setPendingPaymentAfterRuleAuth(paymentRequest);
      setPendingRuleAuthorization(pending);

      return false;
    },
    [getPendingRuleForAuthorization]
  );

  const handleCloseRuleAuthorization = useCallback(() => {
    setPendingRuleAuthorization(null);
    setPendingPaymentAfterRuleAuth(null);
  }, []);

  const handleRuleAuthorizationVerified = useCallback(() => {
    if (!pendingRuleAuthorization) {
      setPendingRuleAuthorization(null);
      return;
    }

    const ruleId = String(pendingRuleAuthorization.id);

    setAuthorizedRuleIds((prev) => ({
      ...prev,
      [ruleId]: {
        authorized: true,
        regla_id: pendingRuleAuthorization.id,
        regla_codigo: pendingRuleAuthorization.codigo,
        regla_nombre: pendingRuleAuthorization.nombre,
        monto_estimado: pendingRuleAuthorization.monto_estimado,
        authorizedAt: new Date().toISOString(),
      },
    }));

    const pendingRequest = pendingPaymentAfterRuleAuth;

    setPendingRuleAuthorization(null);
    setPendingPaymentAfterRuleAuth(null);

    if (pendingRequest?.continuePayment === true) {
      window.setTimeout(() => {
        if (pendingRequest.kind === 'cash') {
          confirmPaymentCash();
        } else if (pendingRequest.kind === 'debit') {
          confirmPaymentDebit();
        } else if (pendingRequest.kind === 'mixed') {
          handleSubmit('mixed', {
            usarPagoOrigen: false,
            cerrarModalPago: true,
            payments: pendingRequest.payments || [],
          });
        } else if (pendingRequest.kind === 'previous') {
          handleSubmit(null, {
            usarPagoOrigen: true,
            cerrarModalPago: true,
          });
        }
      }, 0);
    }
  }, [pendingRuleAuthorization, pendingPaymentAfterRuleAuth]);

  const validateBeforePayment = useCallback(() => {
    if (!isCajaAbierta) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Debe abrir caja antes de registrar una venta.',
      });
      return false;
    }

    if (!items.length) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Agregue productos antes de pagar.',
      });
      return false;
    }

    const invalidVariablePriceItem = items.find((item) => {
      const isVariable = parseBoolish(
        item?.permite_precio_variable ?? item?.raw?.permite_precio_variable,
        false
      );

      if (!isVariable) return false;

      const isImported = Boolean(
        getPedidoItemIdForPayload(item) ||
          getAutomotrizItemIdForPayload(item)
      );

      if (isImported) return false;

      const confirmed = parseBoolish(
        item?.precio_variable_confirmado ?? item?.variablePriceConfirmed,
        false
      );

      const qty = Number(item?.quantity ?? item?.cantidad ?? 0);
      const gross = Number(item?.precio_variable_bruto ?? item?.variablePriceGross ?? item?.gross ?? 0);

      return !confirmed || qty !== 1 || !Number.isFinite(gross) || gross <= 0;
    });

    if (invalidVariablePriceItem) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Hay un producto de precio variable sin valor bruto válido. Elimínelo y vuelva a marcarlo para ingresar el monto.',
      });
      return false;
    }

    if (!customer?.id) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Seleccione un cliente para continuar.',
      });
      return false;
    }

    if (!documentType) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Seleccione el tipo de documento.',
      });
      return false;
    }

    if (documentType === 'factura') {
      const transferError = validateInvoiceTransferData(
        invoiceDeliveryMode,
        invoiceTransferData
      );

      if (transferError) {
        setInvoiceTransferDialogOpen(true);
        setSnackbar({
          open: true,
          severity: 'warning',
          message: transferError,
        });
        return false;
      }
    }

    return true;
  }, [
    isCajaAbierta,
    items,
    customer,
    documentType,
    invoiceDeliveryMode,
    invoiceTransferData,
  ]);

  const handleOpenPaymentModal = useCallback(async () => {
    // El vuelto anterior NO se limpia al abrir el pago ni por tiempo.
    // Solo se retira al marcar/agregar correctamente el primer producto
    // de la venta siguiente.
    if (!validateBeforePayment()) return;

    if (importedOrderPaid) {
      setPaymentModalOpen(true);
      setPaymentStep(0);
      setSelectedPaymentMethod('');
      setCashGivenStateInternal('');
      setChange(0);
      setVoucherNumber('');
      return;
    }

    let methods = availablePaymentMethods;

    if (!methods.length) {
      methods = await fetchPaymentMethodsConfig();
    }

    if (!methods.length) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No hay métodos de pago activos y visibles en POS.',
      });
      return;
    }

    const cashMethod = findCashPaymentMethod(methods);
    const defaultMethod = cashMethod || methods[0];

    setSelectedPaymentMethod(
      defaultMethod.selectionValue ||
        getPaymentSelectionValue(defaultMethod) ||
        defaultMethod.key ||
        defaultMethod.clave ||
        ''
    );

    setCashGivenStateInternal('');
    setChange(0);
    setVoucherNumber('');
    setPaymentStep(0);
    setPaymentModalOpen(true);
  }, [
    validateBeforePayment,
    importedOrderPaid,
    availablePaymentMethods,
    fetchPaymentMethodsConfig,
  ]);

  /* ====================================================================
   * PAYLOAD / VENTA POS
   * ==================================================================== */
  const getCajaId = useCallback(() => {
    return (
      cajaData?.caja?.id ??
      cajaData?.caja_id ??
      cajaData?.id_caja ??
      cajaData?.id ??
      null
    );
  }, [cajaData]);

  const getLastDteCajaStorageKey = useCallback(
    (cajaIdParam = null) => {
      const tenantId = getCurrentTenantIdFromStorage() || 'sin_tenant';
      const cajaId = cajaIdParam ?? getCajaId() ?? 'sin_caja';

      return `integranube_pos_last_dte_${tenantId}_${cajaId}`;
    },
    [getCajaId]
  );

  const saveLastDteForCurrentCaja = useCallback(
    (record) => {
      if (!record?.idVenta) return;

      try {
        const cajaId = record.cajaId ?? getCajaId();
        const key = getLastDteCajaStorageKey(cajaId);

        localStorage.setItem(
          key,
          JSON.stringify({
            ...record,
            cajaId,
            savedAt: new Date().toISOString(),
          })
        );
      } catch (err) {
        console.error('No se pudo guardar último DTE POS:', err);
      }
    },
    [getCajaId, getLastDteCajaStorageKey]
  );

  const loadLastDteForCurrentCaja = useCallback(() => {
    try {
      const key = getLastDteCajaStorageKey();
      const raw = localStorage.getItem(key);

      if (!raw) return null;

      const parsed = JSON.parse(raw);

      if (!parsed?.idVenta) return null;

      return parsed;
    } catch (err) {
      console.error('No se pudo leer último DTE POS:', err);
      return null;
    }
  }, [getLastDteCajaStorageKey]);

  const buildPdfUrlFromLastDte = useCallback((venta = {}) => {
    const idVenta =
      venta?.idVenta ||
      venta?.ventaId ||
      venta?.id_venta ||
      venta?.venta_id ||
      venta?.id ||
      null;

    if (!idVenta) return null;

    if (venta?.pdfUrl) {
      const url = String(venta.pdfUrl);

      if (url.includes('/pdf') && !url.includes('formato=')) {
        return `${url}${url.includes('?') ? '&' : '?'}formato=80mm`;
      }

      return url;
    }

    const tipoDte = String(
      venta?.dteTipo ||
        venta?.codigo_tipo ||
        venta?.tipo_dte ||
        venta?.TipoDTE ||
        ''
    );

    const lastDocumentType = String(
      venta?.documentType ||
        venta?.document_type ||
        venta?.tipo_documento ||
        ''
    ).toLowerCase();

    if (tipoDte === '33') return `/facturacion_dte33/${idVenta}/pdf?formato=80mm`;
    if (tipoDte === '34') return `/facturacion_dte34/${idVenta}/pdf?formato=80mm`;
    if (tipoDte === '39') return `/facturacion_dte39/${idVenta}/pdf?formato=80mm`;
    if (tipoDte === '41') return `/facturacion_dte41/${idVenta}/pdf?formato=80mm`;

    if (lastDocumentType === 'factura') {
      return `/facturacion_dte33/${idVenta}/pdf?formato=80mm`;
    }

    if (lastDocumentType === 'factura_exenta') {
      return `/facturacion_dte34/${idVenta}/pdf?formato=80mm`;
    }

    if (lastDocumentType === 'boleta_exenta') {
      return `/facturacion_dte41/${idVenta}/pdf?formato=80mm`;
    }

    return `/facturacion_dte39/${idVenta}/pdf?formato=80mm`;
  }, []);

  const normalizeLastDteFromVentaResponse = useCallback(
    (responseData = {}, ventaFallback = null) => {
      const data = responseData?.data || responseData || {};

      const venta =
        data?.venta ||
        data?.data?.venta ||
        ventaFallback ||
        null;

      const dte =
        data?.dte ||
        data?.DTE ||
        data?.data?.dte ||
        data?.data?.DTE ||
        data?.documento ||
        data?.data?.documento ||
        null;

      const idVenta =
        venta?.idVenta ||
        venta?.ventaId ||
        venta?.id_venta ||
        venta?.venta_id ||
        venta?.id ||
        dte?.id_venta ||
        dte?.venta_id ||
        data?.idVenta ||
        data?.ventaId ||
        data?.id_venta ||
        data?.venta_id ||
        data?.id ||
        null;

      if (!idVenta) return null;

      const dteTipo =
        dte?.codigo_tipo ||
        dte?.tipo_dte ||
        dte?.TipoDTE ||
        dte?.tipo ||
        dte?.tipoDte ||
        data?.codigo_tipo ||
        data?.tipo_dte ||
        data?.TipoDTE ||
        data?.tipoDte ||
        null;

      const pdfUrl = buildPdfUrlFromLastDte({
        idVenta: Number(idVenta),
        documentType:
          data?.document_type ||
          data?.tipo_documento ||
          venta?.documentType ||
          venta?.tipo_documento ||
          documentType ||
          'boleta',
        dteTipo: dteTipo != null ? String(dteTipo) : null,
        codigo_tipo: dteTipo != null ? String(dteTipo) : null,
      });

      return {
        idVenta: Number(idVenta),
        cajaId: getCajaId(),
        documentType:
          data?.document_type ||
          data?.tipo_documento ||
          venta?.documentType ||
          venta?.tipo_documento ||
          documentType ||
          'boleta',
        dteTipo: dteTipo != null ? String(dteTipo) : null,
        folio: dte?.folio ?? venta?.folio ?? null,
        pdfUrl,
        source: 'POS_FRONTEND',
        savedAt: new Date().toISOString(),
      };
    },
    [documentType, getCajaId, buildPdfUrlFromLastDte]
  );

  useEffect(() => {
    if (!isCajaAbierta) {
      setLastVenta(null);
      return;
    }

    const saved = loadLastDteForCurrentCaja();

    if (saved?.idVenta) {
      setLastVenta(saved);
    }
  }, [isCajaAbierta, loadLastDteForCurrentCaja]);

  const getPaymentReference = useCallback(
    ({ usarPagoOrigen = false } = {}) => {
      if (usarPagoOrigen) {
        const pagoOrigen = normalizePagoOrigen(
          importSource?.pago_origen || importSource?.pagoOrigen,
          totalPayable
        );

        return pagoOrigen.referencia || voucherNumber || null;
      }

      return voucherNumber || null;
    },
    [importSource, totalPayable, voucherNumber]
  );

const buildSalePayload = useCallback(
  ({
    paymentKind = null,
    usarPagoOrigen = false,
    payments = null,
  } = {}) => {
    const selectedMethodMeta = selectedPaymentMethodConfig;

    const esPedidoPendienteDte = isImportSourcePedidoPendienteDte(importSource);
    const sourceRefTypeBackend = getSourceRefTypeForBackend(importSource);

    const sourceRef =
      sourceRefTypeBackend && importSource?.id
        ? {
            type: sourceRefTypeBackend,
            id: Number(importSource.id),
          }
        : null;

    const pagoOrigen = getPagoOrigenFromImportSource(importSource, totalPayable);

    const esPedidoImportadoPagado =
      usarPagoOrigen === true &&
      isImportedPaidOrder(importSource, totalPayable);

    const mainTaxPct = getMainTaxPercentFromItems(items);

    const officialImportedGross = esPedidoImportadoPagado
      ? resolveImportedOrderOfficialTotal(importSource, pagoOrigen, totalPayable)
      : 0;

    /*
     * Primero calculamos SIEMPRE desde las líneas actuales del carrito.
     * Esta es la regla principal para evitar que un pedido pagado completo
     * fuerce el total cuando se eliminaron productos para despacho posterior.
     */
    let itemsPayload = buildSaleItemsPayload(items, {
      rulesSnapshot,
      forceTotalGross: null,
    });

    let itemsPayloadTotals = sumSaleItemsPayloadTotals(itemsPayload);

    /*
     * Solo usamos el total oficial del pedido si el carrito actual coincide
     * con el total completo del pedido.
     *
     * Si el usuario eliminó líneas, el carrito será menor que el pedido pagado,
     * por lo tanto NO se debe usar el total del documento origen.
     */
    const carritoCoincideConDocumentoOrigen =
      esPedidoImportadoPagado &&
      !esPedidoPendienteDte &&
      officialImportedGross > 0 &&
      Math.abs(itemsPayloadTotals.gross - roundCLP(officialImportedGross)) <= 1;

    const debeUsarTotalDocumentoOrigen = carritoCoincideConDocumentoOrigen;

    if (debeUsarTotalDocumentoOrigen) {
      itemsPayload = buildSaleItemsPayload(items, {
        rulesSnapshot,
        forceTotalGross: officialImportedGross,
        forceTotalGrossTolerance: 1,
      });

      itemsPayloadTotals = sumSaleItemsPayloadTotals(itemsPayload);
    }

    let payloadTotalNet = itemsPayloadTotals.net;
    let payloadTotalTax = itemsPayloadTotals.tax;
    let payloadTotalGross = itemsPayloadTotals.gross;
    let payloadTotalPayable = itemsPayloadTotals.gross;

    const esPedidoParcialManual =
      esPedidoImportadoPagado &&
      sourceRefTypeBackend === 'pedido' &&
      !esPedidoPendienteDte &&
      officialImportedGross > 0 &&
      payloadTotalGross < roundCLP(officialImportedGross) - 1;

    const pagoOrigenTotalPagado = roundCLP(pagoOrigen?.total_pagado || officialImportedGross || 0);

    const pagoOrigenAplicado = usarPagoOrigen
      ? Math.min(payloadTotalPayable, pagoOrigenTotalPagado || payloadTotalPayable)
      : 0;

    const pagoOrigenRemanente = usarPagoOrigen
      ? Math.max(0, pagoOrigenTotalPagado - pagoOrigenAplicado)
      : 0;

    const pedidoItemIdsPendientes = esPedidoPendienteDte
      ? itemsPayload
          .map((item) => getPedidoItemIdForPayload(item))
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];

    const mixedPaymentSummary =
      !usarPagoOrigen && paymentKind === 'mixed'
        ? normalizeMixedPaymentsForPayload(payments, payloadTotalPayable)
        : null;

    const isMixedPayment =
      paymentKind === 'mixed' &&
      Array.isArray(mixedPaymentSummary?.payments) &&
      mixedPaymentSummary.payments.length > 0;

    const rawMethodKey = isMixedPayment
      ? ''
      : (
          selectedMethodMeta?.clave ||
          selectedMethodMeta?.key ||
          selectedPaymentMethod ||
          (usarPagoOrigen ? pagoOrigen.metodo_pago_clave : '') ||
          ''
        );

    const paymentMethodLabel = isMixedPayment
      ? 'Pago mixto'
      : (
          selectedMethodMeta?.label ||
          selectedMethodMeta?.nombre ||
          (usarPagoOrigen ? pagoOrigen.metodo_pago : '') ||
          metodoPagoTexto(rawMethodKey) ||
          rawMethodKey ||
          'No informado'
        );

    const resolvedMetodoPagoId = isMixedPayment
      ? null
      : (
          selectedMethodMeta?.methodId ||
          selectedMethodMeta?.id ||
          selectedMethodMeta?.relationId ||
          null
        );

    const paymentReference = isMixedPayment
      ? null
      : getPaymentReference({ usarPagoOrigen });

    const isCurrentCashPayment =
      !usarPagoOrigen && (paymentKind === 'cash' || selectedPaymentIsCash);

    const currentCashSummary = getCashRoundingSummary(payloadTotalPayable);

    /*
     * total_amount / total_bruto conservan el total tributario.
     * amount_paid representa el efectivo físico que entra a la caja.
     */
    const amountPaid = isMixedPayment
      ? Number(mixedPaymentSummary.amountPaid || 0)
      : isCurrentCashPayment
        ? currentCashSummary.cashTotal
        : payloadTotalPayable;

    const amountReceived = isMixedPayment
      ? Number(mixedPaymentSummary.amountReceived || 0)
      : usarPagoOrigen
        ? payloadTotalPayable
        : paymentKind === 'debit'
          ? payloadTotalPayable
          : isCurrentCashPayment
            ? Number(cashGivenState || amountPaid || 0)
            : payloadTotalPayable;

    const changeGiven = isMixedPayment
      ? Number(mixedPaymentSummary.changeGiven || 0)
      : isCurrentCashPayment
        ? Math.max(Number(amountReceived || 0) - Number(amountPaid || 0), 0)
        : 0;

    const remainingBalance = isMixedPayment
      ? Number(mixedPaymentSummary.remainingBalance || 0)
      : 0;

    const cashRounding = isMixedPayment
      ? Number(mixedPaymentSummary.cashRounding || 0)
      : isCurrentCashPayment
        ? currentCashSummary.rounding
        : 0;

    const status = isMixedPayment
      ? mixedPaymentSummary.status
      : 'completed';

    const effectiveDocumentType = documentType || 'boleta';
    const posCajaId = getCajaId();

    const invoiceTransferPayload =
      effectiveDocumentType === 'factura'
        ? buildInvoiceTransferPayload(invoiceDeliveryMode, invoiceTransferData)
        : null;

    const ventaOrigenId =
      importSource?.raw?.id_venta ||
      importSource?.raw?.venta_id ||
      importSource?.raw?.facturacion?.id_venta ||
      importSource?.raw?.estados?.facturacion?.id_venta ||
      null;

    return {
      client_sale_id: getOrCreateClientSaleId(),

      customer_id: Number(customer.id),
      cliente_id: Number(customer.id),

      ...(isMixedPayment
        ? {
            payment_method_name: 'Pago mixto',
            metodo_pago_nombre: 'Pago mixto',
            payments: mixedPaymentSummary.payments,
          }
        : {
            metodo_pago_clave: rawMethodKey || null,
            metodo_pago_nombre: paymentMethodLabel || null,
            metodo_pago: paymentMethodLabel || null,
            payment_method: paymentMethodLabel || null,
            payment_method_type: rawMethodKey || null,

            ...(resolvedMetodoPagoId
              ? {
                  metodo_pago_id: Number(resolvedMetodoPagoId),
                  payment_method_id: Number(resolvedMetodoPagoId),
                }
              : {}),

            ...(paymentReference
              ? {
                  voucher_number: paymentReference,
                  referencia: paymentReference,
                  reference: paymentReference,
                }
              : {}),
          }),

      amount_paid: Number(amountPaid),
      amount_received: Number(amountReceived),
      change_given: Number(changeGiven),
      cash_rounding: Number(cashRounding),
      remaining_balance: Number(remainingBalance),
      status,

      document_type: effectiveDocumentType,
      tipo_documento: effectiveDocumentType,
      currency: 'CLP',

      ...(effectiveDocumentType === 'factura'
        ? {
            ampara_traslado:
              invoiceDeliveryMode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE ||
              invoiceDeliveryMode === INVOICE_DELIVERY_MODE.DESPACHO,
            tipo_entrega: invoiceDeliveryMode || null,
            datos_traslado: invoiceTransferPayload,
          }
        : {}),

      tax: Number(payloadTotalTax),
      total_amount: Number(payloadTotalPayable),
      total_neto: Number(payloadTotalNet),
      total_iva: Number(payloadTotalTax),
      total_bruto: Number(payloadTotalGross),

      total_ajuste_reglas: Number(totalRulesAdjustment || 0),
      total_ajustes_bruto: Number(totalRulesAdjustment || 0),
      total_descuentos: Math.abs(Math.min(Number(totalRulesAdjustment || 0), 0)),

      ...(posCajaId != null ? { pos_caja_id: Number(posCajaId) } : {}),
      ...(sourceRef ? { source_ref: sourceRef } : {}),

      modo_importacion: esPedidoPendienteDte
        ? 'pedido_pendiente_dte'
        : importSource?.modo_importacion || importSource?.type || null,

      emitir_pendientes_dte: esPedidoPendienteDte,
      items_scope: esPedidoPendienteDte
        ? 'PENDIENTES_DTE'
        : esPedidoParcialManual
          ? 'PARCIAL_MANUAL'
          : 'FULL',

      pedido_id:
        sourceRefTypeBackend === 'pedido'
          ? Number(importSource?.id || 0) || null
          : null,

      mantencion_id:
        sourceRefTypeBackend === 'automotriz_mantencion'
          ? Number(importSource?.id || importSource?.mantencion_id || 0) || null
          : null,

      completar_mantencion:
        sourceRefTypeBackend === 'automotriz_mantencion',

      venta_origen_id: ventaOrigenId ? Number(ventaOrigenId) : null,

      pedido_item_ids: esPedidoPendienteDte
        ? pedidoItemIdsPendientes
        : null,

      pedido_items_pendientes_ids: esPedidoPendienteDte
        ? pedidoItemIdsPendientes
        : null,

      usar_pago_origen: usarPagoOrigen,
      heredar_pago_origen: usarPagoOrigen,

      importado_pagado: esPedidoImportadoPagado,
      pedido_parcial_manual: esPedidoParcialManual,
      usar_total_documento_origen: debeUsarTotalDocumentoOrigen,

      pago_origen: usarPagoOrigen
        ? {
            tipo: sourceRefTypeBackend || 'pedido',
            id: importSource?.id ? Number(importSource.id) : null,
            origen_tipo: pagoOrigen?.origen_tipo || sourceRefTypeBackend || 'pedido',
            origen_id: pagoOrigen?.origen_id || importSource?.id || null,
            fuente: pagoOrigen?.fuente || 'pedido',

            total_pagado: Number(pagoOrigen?.total_pagado || 0),
            total_aplicado: Number(payloadTotalPayable),
            remanente_estimado: Number(pagoOrigenRemanente),
            saldo: Number(pagoOrigen?.saldo || 0),

            metodo_pago: paymentMethodLabel,
            metodo_pago_clave: rawMethodKey,
            referencia: paymentReference,
            movimientos: pagoOrigen?.movimientos || [],
          }
        : null,

      totales_originales: {
        total_neto: Number(baseTotalNet),
        total_iva: Number(baseTotalTax),
        total_bruto: Number(baseTotalGross),
      },

      totales_documento_origen: debeUsarTotalDocumentoOrigen
        ? {
            fuente: 'pedido_importado_pagado',
            total_bruto: Number(payloadTotalGross),
            total_neto: Number(payloadTotalNet),
            total_iva: Number(payloadTotalTax),
            total_pagado: Number(pagoOrigen?.total_pagado || payloadTotalPayable),
            impuesto_pct: Number(mainTaxPct || 19),
          }
        : null,

      validacion_totales: {
        modo: debeUsarTotalDocumentoOrigen ? 'DOCUMENTO_ORIGEN' : 'POS_NORMAL',
        tolerancia_clp: debeUsarTotalDocumentoOrigen ? 1 : 0,
      },

      reglas_pos: rulesSnapshot,

      items: itemsPayload,
    };
  },
  [
    selectedPaymentMethodConfig,
    importSource,
    totalPayable,
    selectedPaymentMethod,
    getPaymentReference,
    cashGivenState,
    selectedPaymentIsCash,
    documentType,
    invoiceDeliveryMode,
    invoiceTransferData,
    getCajaId,
    customer,
    totalTax,
    totalNet,
    totalGross,
    totalRulesAdjustment,
    baseTotalNet,
    baseTotalTax,
    baseTotalGross,
    rulesSnapshot,
    items,
    getOrCreateClientSaleId,
  ]
);

const getPrintUrlFromResponse = useCallback((responseData, ventaFallback = null) => {
  const data = responseData?.data || responseData || {};

  const venta =
    data?.venta ||
    data?.data?.venta ||
    ventaFallback ||
    null;

  const dte =
    data?.dte ||
    data?.DTE ||
    data?.data?.dte ||
    data?.data?.DTE ||
    data?.documento ||
    data?.data?.documento ||
    null;

  const ventaId =
    venta?.idVenta ||
    venta?.ventaId ||
    venta?.id_venta ||
    venta?.venta_id ||
    venta?.id ||
    dte?.id_venta ||
    dte?.venta_id ||
    data?.idVenta ||
    data?.ventaId ||
    data?.id_venta ||
    data?.venta_id ||
    data?.id ||
    null;

  const tipoDte =
    dte?.codigo_tipo ||
    dte?.tipo_dte ||
    dte?.TipoDTE ||
    dte?.tipo ||
    dte?.tipoDte ||
    data?.codigo_tipo ||
    data?.tipo_dte ||
    data?.TipoDTE ||
    data?.tipoDte ||
    null;

  if (!ventaId) return null;

  if (String(tipoDte) === '33') return `/facturacion_dte33/${ventaId}/pdf?formato=80mm`;
  if (String(tipoDte) === '34') return `/facturacion_dte34/${ventaId}/pdf?formato=80mm`;
  if (String(tipoDte) === '39') return `/facturacion_dte39/${ventaId}/pdf?formato=80mm`;
  if (String(tipoDte) === '41') return `/facturacion_dte41/${ventaId}/pdf?formato=80mm`;

  if (documentType === 'factura') return `/facturacion_dte33/${ventaId}/pdf?formato=80mm`;
  if (documentType === 'factura_exenta') return `/facturacion_dte34/${ventaId}/pdf?formato=80mm`;
  if (documentType === 'boleta_exenta') return `/facturacion_dte41/${ventaId}/pdf?formato=80mm`;

  return `/facturacion_dte39/${ventaId}/pdf?formato=80mm`;
}, [documentType]);

const printPdfBlobFast = useCallback((blob) => {
  if (!blob) return;

  const blobUrl = URL.createObjectURL(blob);
  const previousActiveElement = document.activeElement;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.tabIndex = -1;
  iframe.setAttribute('aria-hidden', 'true');
  iframe.src = blobUrl;

  let cleaned = false;

  const restorePosFocus = () => {
    try {
      window.focus();
    } catch (_) {}

    try {
      if (
        previousActiveElement &&
        previousActiveElement !== document.body &&
        document.contains(previousActiveElement) &&
        typeof previousActiveElement.focus === 'function'
      ) {
        previousActiveElement.focus({ preventScroll: true });
      } else if (document.body && typeof document.body.focus === 'function') {
        document.body.focus({ preventScroll: true });
      }
    } catch (_) {}
  };

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;

    try {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    } catch (_) {}

    try {
      URL.revokeObjectURL(blobUrl);
    } catch (_) {}
  };

  iframe.onload = () => {
    try {
      const win = iframe.contentWindow;

      if (!win) {
        cleanup();
        restorePosFocus();
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'No se pudo abrir el documento para impresión.',
        });
        return;
      }

      window.setTimeout(() => {
        try {
          // Importante: NO hacer win.focus().
          // El lector de código de barras funciona como teclado y el POS escucha
          // sus teclas en la ventana principal. Enfocar el iframe invisible hacía
          // que las marcaciones siguientes terminaran dentro del PDF.
          win.print();
        } catch (err) {
          console.error('Error imprimiendo PDF POS:', err);
          setSnackbar({
            open: true,
            severity: 'error',
            message: 'No se pudo invocar la impresión del PDF.',
          });
        } finally {
          // Edge/Chrome pueden mover el foco al contexto de impresión aun sin
          // focus() explícito. Lo recuperamos inmediatamente y una vez más después.
          window.setTimeout(restorePosFocus, 0);
          window.setTimeout(restorePosFocus, 250);
        }

        window.setTimeout(cleanup, 30000);
      }, 150);
    } catch (err) {
      console.error('Error preparando impresión PDF POS:', err);
      cleanup();
      restorePosFocus();

      setSnackbar({
        open: true,
        severity: 'error',
        message: 'No se pudo preparar el PDF para impresión.',
      });
    }
  };

  iframe.onerror = () => {
    cleanup();
    restorePosFocus();
    setSnackbar({
      open: true,
      severity: 'error',
      message: 'No se pudo cargar el PDF para impresión.',
    });
  };

  document.body.appendChild(iframe);
}, [setSnackbar]);

const downloadAndPrintPdfFast = useCallback(
  async (pdfUrlToCall) => {
    if (!pdfUrlToCall) return false;

    try {
      const resp = await api.get(pdfUrlToCall, {
        responseType: 'blob',
        timeout: POS_FAST_API_TIMEOUT_MS,
      });

      isOnlineRef.current = true;
      setIsOnline(true);
      printPdfBlobFast(resp.data);

      return true;
    } catch (error) {
      console.error('Error descargando PDF POS:', error);

      if (isRecoverablePosConnectivityError(error)) {
        isOnlineRef.current = false;
        setIsOnline(false);
      }

      setSnackbar({
        open: true,
        severity: 'warning',
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.mensaje ||
          'La venta fue registrada, pero no se pudo descargar el PDF para imprimir.',
      });

      return false;
    }
  },
  [printPdfBlobFast]
);

  const handleSubmit = useCallback(
    async (
      paymentKind = null,
      {
        usarPagoOrigen = false,
        cerrarModalPago = true,
        payments = null,
      } = {}
    ) => {
      if (!validateBeforePayment()) return;

      const isMixedPayment = !usarPagoOrigen && paymentKind === 'mixed';
      const mixedPaymentSummary = isMixedPayment
        ? normalizeMixedPaymentsForPayload(payments, totalPayable)
        : null;

      if (isMixedPayment) {
        const mixedPayments = mixedPaymentSummary?.validation || [];

        if (mixedPayments.length < 2) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'El pago mixto debe contener al menos dos métodos de pago.',
          });
          return;
        }

        const relations = mixedPayments
          .map((pago) => Number(pago.payment_relation_id || 0))
          .filter((id) => Number.isInteger(id) && id > 0);

        if (relations.length !== mixedPayments.length) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'Todos los pagos mixtos deben tener una relación de pago válida.',
          });
          return;
        }

        if (new Set(relations).size !== relations.length) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'No repita la misma relación de pago dentro del pago mixto.',
          });
          return;
        }

        const methodIdentities = mixedPayments.map((pago) => {
          const methodId = Number(pago.payment_method_id || 0);
          if (Number.isInteger(methodId) && methodId > 0) return `id:${methodId}`;
          return `key:${normalizePosPaymentMethodKey(pago.payment_method_key || '')}`;
        });

        if (new Set(methodIdentities).size !== methodIdentities.length) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'No repita el mismo método dentro del pago mixto.',
          });
          return;
        }

        if (mixedPayments.filter((pago) => pago.__isCash).length > 1) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'El pago mixto permite una sola línea de efectivo.',
          });
          return;
        }

        const missingReference = mixedPayments.find(
          (pago) =>
            pago.__requiresVoucher &&
            !String(
              pago.voucher_number ||
              pago.reference ||
              pago.referencia ||
              ''
            ).trim()
        );

        if (missingReference) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: `Ingrese la referencia de ${missingReference.payment_method_name || 'uno de los pagos'}.`,
          });
          return;
        }

        const invalidCash = mixedPayments.find(
          (pago) =>
            pago.__isCash &&
            Number(pago.amount_received || 0) < Number(pago.amount_paid || 0)
        );

        if (invalidCash) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'El efectivo recibido no cubre la parte asignada al pago en efectivo.',
          });
          return;
        }

        if (
          Math.abs(
            Number(mixedPaymentSummary.totalApplied || 0) -
            Math.round(Number(totalPayable || 0))
          ) > 0
        ) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'La suma de los pagos mixtos debe cubrir exactamente el total de la venta.',
          });
          return;
        }
      }

      if (
        !ensureRulesAuthorizedBeforePayment({
          continuePayment: true,
          kind: usarPagoOrigen
            ? 'previous'
            : isMixedPayment
              ? 'mixed'
              : paymentKind === 'cash'
                ? 'cash'
                : 'debit',
          ...(isMixedPayment
            ? { payments: mixedPaymentSummary?.payments || [] }
            : {}),
        })
      ) {
        return;
      }

      if (!usarPagoOrigen && !isMixedPayment) {
        if (!selectedPaymentMethodConfig) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'Seleccione un método de pago.',
          });
          return;
        }

        if (selectedPaymentRequiresVoucher && !String(voucherNumber || '').trim()) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'Ingrese referencia o voucher del pago.',
          });
          return;
        }

        if (paymentKind === 'cash' && !canContinueCash) {
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'El efectivo recibido no cubre el total.',
          });
          return;
        }
      }

      const payload = buildSalePayload({
        paymentKind,
        usarPagoOrigen,
        payments: isMixedPayment
          ? mixedPaymentSummary?.payments || []
          : null,
      });

      const paymentSnapshot = buildPaymentSnapshot({
        selectedPaymentMethodConfig,
        selectedPaymentMethod,
        amountReceived: payload.amount_received,
        voucherNumber: payload.voucher_number,
        change: payload.change_given,
        importedPaymentLabel,
        payments: payload.payments || null,
      });

      let saleHealthcheckTimer = null;
      const saleAbortController = new AbortController();

      try {
        setLoading(true);

        // La generación normal de una venta/DTE puede tardar varios segundos, por
        // eso mantenemos su timeout amplio. Pero si la API muere a mitad de la
        // operación, un probe separado detecta la caída y cancela la espera para
        // pasar a contingencia sin dejar "Procesando..." pegado.
        saleHealthcheckTimer = window.setTimeout(async () => {
          try {
            await api.get('/pos_cajas/estado', {
              timeout: POS_API_PROBE_TIMEOUT_MS,
            });
          } catch (probeError) {
            if (isRecoverablePosConnectivityError(probeError)) {
              isOnlineRef.current = false;
              setIsOnline(false);
              try {
                saleAbortController.abort();
              } catch (_) {
                // noop
              }
            }
          }
        }, POS_SALE_HEALTHCHECK_DELAY_MS);

        const resp = await api.post('/ventas_pos', payload, {
          timeout: POS_SALE_API_TIMEOUT_MS,
          signal: saleAbortController.signal,
        });

        isOnlineRef.current = true;
        setIsOnline(true);

        const responseData = resp.data || {};
        const nuevaVenta = responseData.venta || responseData.data?.venta || responseData;

        const lastDteRecord = normalizeLastDteFromVentaResponse(responseData, nuevaVenta);

        if (lastDteRecord?.idVenta) {
          setLastVenta(lastDteRecord);
          saveLastDteForCurrentCaja(lastDteRecord);
        }

        const pdfUrl = lastDteRecord?.pdfUrl || getPrintUrlFromResponse(responseData, nuevaVenta);

        if (pdfUrl) {
          await downloadAndPrintPdfFast(pdfUrl);
        }

        if (cerrarModalPago) {
          setPaymentModalOpen(false);
        }

        const postSaleChange = Number(payload.change_given || 0);

        if (postSaleChange > 0) {
          setPostSaleChangeNotice({
            id: `${Date.now()}-${lastDteRecord?.folio || lastDteRecord?.idVenta || 'venta'}`,
            amount: postSaleChange,
            folio: lastDteRecord?.folio || null,
          });
        }

        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Venta registrada correctamente.',
        });

        // La nueva venta queda utilizable de inmediato. El refresco de caja no
        // debe mantener el flujo de cobro/lector esperando innecesariamente.
        clearSale();
        setLoading(false);
        void fetchCajaEstado();
      } catch (error) {
        console.error('Error al registrar venta POS:', error);

        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);

          try {
            const offlineRecord = buildOfflineSaleRecord({
              payload,
              customer,
              documentType,
              sourceRef: payload.source_ref || null,
              itemsSnapshot: items,
              totals: {
                totalNet,
                totalTax,
                totalGross,
                totalPayable,
                baseTotalNet,
                baseTotalTax,
                baseTotalGross,
                totalRulesAdjustment,
              },
              payment: paymentSnapshot,
              rulesSnapshot,
            });

            await saveOfflineSaleToDB(offlineRecord);
            await refreshOfflineQueueCount();
            await refreshContingencySales();

            try {
              printContingencyTicket(offlineRecord);
            } catch (printError) {
              console.error('No se pudo imprimir comprobante de contingencia:', printError);
            }

            if (cerrarModalPago) {
              setPaymentModalOpen(false);
            }

            const postSaleChange = Number(payload.change_given || 0);

            if (postSaleChange > 0) {
              setPostSaleChangeNotice({
                id: `${Date.now()}-${offlineRecord.offlineId || 'contingencia'}`,
                amount: postSaleChange,
                folio: offlineRecord.offlineId || null,
              });
            }

            setSnackbar({
              open: true,
              severity: 'warning',
              message:
                'API no disponible. La venta fue guardada en contingencia y se sincronizará cuando el servicio vuelva a responder.',
            });

            clearSale();
          } catch (offlineError) {
            console.error('No se pudo guardar la venta offline:', offlineError);

            setSnackbar({
              open: true,
              severity: 'error',
              message:
                offlineError.message ||
                'No se pudo registrar la venta ni guardarla en contingencia.',
            });
          }

          return;
        }

        setSnackbar({
          open: true,
          severity: 'error',
          message:
            error.response?.data?.message ||
            error.response?.data?.error ||
            'No se pudo registrar la venta.',
        });
      } finally {
        if (saleHealthcheckTimer) {
          window.clearTimeout(saleHealthcheckTimer);
        }
        setLoading(false);
      }
    },
    [
      validateBeforePayment,
      ensureRulesAuthorizedBeforePayment,
      selectedPaymentMethodConfig,
      selectedPaymentRequiresVoucher,
      voucherNumber,
      canContinueCash,
      buildSalePayload,
      selectedPaymentMethod,
      importedPaymentLabel,
      getPrintUrlFromResponse,
      clearSale,
      fetchCajaEstado,
      customer,
      documentType,
      items,
      totalNet,
      totalTax,
      totalGross,
      totalPayable,
      baseTotalNet,
      baseTotalTax,
      baseTotalGross,
      totalRulesAdjustment,
      rulesSnapshot,
      refreshOfflineQueueCount,
      refreshContingencySales,
      normalizeLastDteFromVentaResponse,
      saveLastDteForCurrentCaja,
      downloadAndPrintPdfFast,

    ]
  );

  const confirmPaymentDebit = useCallback(async () => {
    await handleSubmit('debit', {
      usarPagoOrigen: false,
      cerrarModalPago: true,
    });
  }, [handleSubmit]);

  const confirmPaymentCash = useCallback(async () => {
    await handleSubmit('cash', {
      usarPagoOrigen: false,
      cerrarModalPago: true,
    });
  }, [handleSubmit]);

  const confirmMixedPayment = useCallback(async (mixedPayments = []) => {
    await handleSubmit('mixed', {
      usarPagoOrigen: false,
      cerrarModalPago: true,
      payments: mixedPayments,
    });
  }, [handleSubmit]);

  /* ====================================================================
   * REIMPRESIÓN ÚLTIMO DTE DE LA CAJA
   * ==================================================================== */
  const handleRequestReprint = useCallback(() => {
    const ventaToPrint = lastVenta || loadLastDteForCurrentCaja();

    if (!ventaToPrint?.idVenta) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No hay un último DTE registrado para esta caja.',
      });
      return;
    }

    setLastVenta(ventaToPrint);
    setReprintPinOpen(true);
  }, [lastVenta, loadLastDteForCurrentCaja]);

  const handleReprintAfterPin = useCallback(async () => {
    setReprintPinOpen(false);

    const ventaToPrint = lastVenta || loadLastDteForCurrentCaja();

    if (!ventaToPrint?.idVenta) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No se encontró una venta válida para reimprimir.',
      });
      return;
    }

    const pdfUrl = buildPdfUrlFromLastDte(ventaToPrint);

    if (!pdfUrl) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'No se pudo determinar el PDF del último DTE de la caja.',
      });
      return;
    }

    setReprintLoading(true);

    try {
      await downloadAndPrintPdfFast(pdfUrl);

      setSnackbar({
        open: true,
        severity: 'success',
        message: `Reimpresión enviada${ventaToPrint.folio ? ` · Folio ${ventaToPrint.folio}` : ''}.`,
      });
    } finally {
      setReprintLoading(false);
    }
  }, [
    lastVenta,
    loadLastDteForCurrentCaja,
    buildPdfUrlFromLastDte,
    downloadAndPrintPdfFast,
  ]);
  /* ====================================================================
   * PIN edición / eliminación heredados
   * ==================================================================== */
  const handleCloseEditQtyPin = useCallback(() => {
    handleCancelEditQtyPin();
  }, [handleCancelEditQtyPin]);

  const handleEditQtyAfterPin = useCallback(() => {
    setEditQtyPinOpen(false);
    applyEditQty();
  }, [applyEditQty]);

  const handleRequestEditQtyFromModal = useCallback(() => {
    if (!itemToEditQty) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No hay producto seleccionado.',
      });
      return;
    }

    const nuevaCantidad = Number(editQtyValue);
    const cantidadActual = Number(itemToEditQty.item?.quantity || 0);

    // applyEditQty mantiene la validación y muestra el mensaje correspondiente.
    if (!Number.isFinite(nuevaCantidad) || nuevaCantidad <= 0) {
      applyEditQty();
      return;
    }

    // Aumentar o mantener la cantidad: sin PIN.
    if (nuevaCantidad >= cantidadActual) {
      applyEditQty();
      return;
    }

    // Reducir cantidad: requiere PIN.
    setEditQtyModalOpen(false);
    setEditQtyPinOpen(true);
  }, [itemToEditQty, editQtyValue, applyEditQty]);

    const editQtyRequiresAuthorization = useMemo(() => {
    const cantidadActual = Number(itemToEditQty?.item?.quantity ?? 0);
    const nuevaCantidad = Number(editQtyValue);

    // Solo requiere PIN cuando se disminuye una cantidad válida.
    return (
      Number.isFinite(nuevaCantidad) &&
      nuevaCantidad > 0 &&
      nuevaCantidad < cantidadActual
    );
  }, [itemToEditQty, editQtyValue]);

  /* ====================================================================
   * CONTINGENCIA / SINCRONIZACIÓN OFFLINE
   * ==================================================================== */
  const syncingOfflineRef = useRef(false);

  const syncOneOfflineSale = useCallback(
    async (record, { silent = false } = {}) => {
      if (!record?.offlineId) {
        return null;
      }

      setOfflineSaleSyncingId(record.offlineId);

      try {
        await updateOfflineSaleInDB(record.offlineId, {
          syncing: true,
          syncError: null,
          lastSyncAttemptAt: new Date().toISOString(),
        });

        const currentRecord = await getOfflineSaleById(record.offlineId);
        const synced = await syncOfflineSaleRecord(currentRecord || record);

        isOnlineRef.current = true;
        setIsOnline(true);

        if (!silent) {
          setSnackbar({
            open: true,
            severity: 'success',
            message: `Venta ${record.offlineId} sincronizada correctamente.`,
          });
        }

        await refreshOfflineQueueCount();
        await refreshContingencySales();

        return synced;
      } catch (error) {
        console.error('Error sincronizando venta offline:', error);

        await markOfflineSaleSyncError(record, error);

        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);
        }

        if (!silent) {
          setSnackbar({
            open: true,
            severity: 'error',
            message:
              error.response?.data?.message ||
              error.response?.data?.error ||
              error.message ||
              `No se pudo sincronizar la venta ${record.offlineId}.`,
          });
        }

        await refreshOfflineQueueCount();
        await refreshContingencySales();

        return null;
      } finally {
        setOfflineSaleSyncingId(null);
      }
    },
    [refreshOfflineQueueCount, refreshContingencySales]
  );

  const syncOfflineSales = useCallback(
    async ({ force = false, silent = false, includeReview = false } = {}) => {
      if (syncingOfflineRef.current) return;

      if (!force && !isOnlineRef.current) return;

      syncingOfflineRef.current = true;

      try {
        const rows = await getOfflineSalesFromDB();
        const allPending = rows.filter((record) => !record.synced);
        const pending = includeReview
          ? allPending
          : allPending.filter(isOfflineSaleAutoRetryEligible);
        const reviewCount = Math.max(0, allPending.length - pending.length);

        if (!pending.length) {
          await refreshOfflineQueueCount();
          await refreshContingencySales();

          if (!silent) {
            setSnackbar({
              open: true,
              severity: reviewCount > 0 ? 'warning' : 'info',
              message: reviewCount > 0
                ? `${reviewCount} venta(s) pendiente(s) requieren revisión antes de reintentar.`
                : 'No hay ventas de contingencia pendientes.',
            });
          }

          return {
            syncedCount: 0,
            errorCount: 0,
            reviewCount,
          };
        }

        // Sólo mostramos "Sincronizando ventas" cuando efectivamente hay
        // registros elegibles que se intentarán enviar al backend.
        setSyncingOffline(true);

        let syncedCount = 0;
        let errorCount = 0;
        let networkStopped = false;

        for (const record of pending) {
          if (networkStopped) break;

          try {
            const result = await syncOneOfflineSale(record, { silent: true });

            if (result) {
              syncedCount += 1;
            } else {
              errorCount += 1;
            }
          } catch (error) {
            errorCount += 1;

            if (isRecoverablePosConnectivityError(error)) {
              networkStopped = true;
              isOnlineRef.current = false;
              setIsOnline(false);
            }
          }
        }

        await refreshOfflineQueueCount();
        await refreshContingencySales();

        if (!silent) {
          if (syncedCount > 0 && errorCount === 0) {
            setSnackbar({
              open: true,
              severity: 'success',
              message: `Se sincronizaron ${syncedCount} venta(s) de contingencia.`,
            });
          } else if (syncedCount > 0 && errorCount > 0) {
            setSnackbar({
              open: true,
              severity: 'warning',
              message: `Se sincronizaron ${syncedCount} venta(s), pero ${errorCount} quedaron con error.`,
            });
          } else if (errorCount > 0) {
            setSnackbar({
              open: true,
              severity: 'error',
              message: 'No se pudieron sincronizar las ventas de contingencia.',
            });
          }
        }

        return {
          syncedCount,
          errorCount,
          reviewCount,
        };
      } catch (error) {
        console.error('Error general sincronizando ventas offline:', error);

        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);
        }

        if (!silent) {
          setSnackbar({
            open: true,
            severity: 'error',
            message:
              error.response?.data?.message ||
              error.response?.data?.error ||
              error.message ||
              'Error sincronizando ventas offline.',
          });
        }
      } finally {
        syncingOfflineRef.current = false;
        setSyncingOffline(false);

        await refreshOfflineQueueCount();
        await refreshContingencySales();
      }
    },
    [
      refreshOfflineQueueCount,
      refreshContingencySales,
      syncOneOfflineSale,
    ]
  );

  /* ====================================================================
   * RECUPERACIÓN AUTOMÁTICA DE API
   *
   * navigator.onLine solo indica conectividad de red. Si Cloudflare/API
   * responde 502/503/52x, el navegador sigue "online" y no dispara el
   * evento 'online'. Por eso, mientras el POS está en contingencia, hacemos
   * un sondeo liviano y reanudamos toda la sincronización al recuperarse.
   * ==================================================================== */
  const recoverPosApiConnectivity = useCallback(
    async ({ announce = false } = {}) => {
      if (apiRecoveryInFlightRef.current || !navigator.onLine) return false;

      apiRecoveryInFlightRef.current = true;
      setApiRecoveryChecking(true);

      try {
        await api.get('/pos_cajas/estado', {
          timeout: POS_API_PROBE_TIMEOUT_MS,
        });

        const wasOffline = !isOnlineRef.current;
        isOnlineRef.current = true;
        setIsOnline(true);

        if (wasOffline || announce) {
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'API recuperada. Sincronizando información del POS…',
          });
        }

        // Revalidar el estado operativo completo. Promise.allSettled evita que
        // un endpoint secundario impida recuperar el resto del POS.
        await Promise.allSettled([
          fetchCajaEstado(),
          fetchPaymentMethodsConfig(),
          refreshPosRules({ silent: true }),
          refreshPosProductsCache({ silent: true }),
          fetchDefaultCustomer(),
        ]);

        await syncOfflineSales({ force: true, silent: true });

        return true;
      } catch (error) {
        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);
          return false;
        }

        // La API respondió, pero con un error funcional/autorización. No lo
        // confundimos con una recuperación válida del POS.
        console.warn('[POS] API alcanzable pero no operativa para recuperación:', error);
        return false;
      } finally {
        apiRecoveryInFlightRef.current = false;
        setApiRecoveryChecking(false);
      }
    },
    [
      fetchCajaEstado,
      fetchDefaultCustomer,
      fetchPaymentMethodsConfig,
      refreshPosProductsCache,
      refreshPosRules,
      syncOfflineSales,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    let timerId = null;

    const scheduleNext = () => {
      if (cancelled) return;
      timerId = window.setTimeout(async () => {
        if (!cancelled && navigator.onLine && !isOnlineRef.current) {
          await recoverPosApiConnectivity({ announce: false });
        }
        scheduleNext();
      }, POS_API_RECOVERY_INTERVAL_MS);
    };

    // Si ya entramos a contingencia durante el arranque, no esperamos al
    // primer intervalo completo para iniciar la vigilancia.
    const initialTimer = window.setTimeout(async () => {
      if (!cancelled && navigator.onLine && !isOnlineRef.current) {
        await recoverPosApiConnectivity({ announce: false });
      }
      scheduleNext();
    }, 1200);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      if (timerId) window.clearTimeout(timerId);
    };
  }, [recoverPosApiConnectivity]);

  /* ====================================================================
   * ARRANQUE CONTROLADO DEL POS
   * ==================================================================== */
  useEffect(() => {
    if (startupRanRef.current) return;
    startupRanRef.current = true;

    let cancelled = false;
    const startedAt = Date.now();

    const setStep = (id, status, detail = '') => {
      if (!cancelled) updateStartupStep(id, { status, detail });
    };

    const finishAfterMinimumVisibleTime = async () => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, 700 - elapsed);
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
      if (cancelled) return;
      setStartupSummary('POS listo');
      await new Promise((resolve) => window.setTimeout(resolve, 350));
      if (!cancelled) setStartupModalOpen(false);
    };

    (async () => {
      setStartupModalOpen(true);
      setStartupSummary('Preparando operación segura del POS…');

      setStep('storage', 'running', 'Abriendo base local del POS…');
      try {
        if (!hasIndexedDB) throw new Error('IndexedDB no está disponible en este navegador.');
        await openOfflineDB();
        const [cachedProducts, pendingSales] = await Promise.all([
          getCachedPosProductCount(),
          getOfflineSalesFromDB(),
        ]);
        if (!cancelled) {
          setPosProductsCacheCount(cachedProducts);
          setOfflineQueueCount(pendingSales.filter((row) => !row.synced).length);
        }
        setStep(
          'storage',
          'done',
          `${cachedProducts.toLocaleString('es-CL')} productos locales · ${pendingSales.filter((row) => !row.synced).length} venta(s) pendiente(s)`
        );
      } catch (error) {
        console.error('[POS startup] almacenamiento local:', error);
        setStep('storage', 'error', error?.message || 'No se pudo preparar el almacenamiento local.');
      }

      setStartupSummary('Verificando configuración y caja…');
      ['caja', 'payments', 'customer', 'rules'].forEach((id) => setStep(id, 'running', 'Consultando…'));

      await Promise.all([
        (async () => {
          try {
            const result = await fetchCajaEstado();
            setStep(
              'caja',
              result?.fromCache ? 'warning' : 'done',
              result?.fromCache
                ? `Usando última caja conocida (${result?.abierta ? 'abierta' : 'cerrada'})`
                : `Caja ${result?.abierta ? 'abierta' : 'cerrada'} verificada`
            );
          } catch (error) {
            setStep('caja', 'warning', 'No fue posible verificar la caja; se aplicó el fallback disponible.');
          }
        })(),
        (async () => {
          try {
            const methods = await fetchPaymentMethodsConfig();
            const count = Array.isArray(methods) ? methods.length : 0;
            setStep('payments', count > 0 ? 'done' : 'warning', count > 0 ? `${count} método(s) de pago disponibles` : 'Sin métodos de pago disponibles');
          } catch (error) {
            setStep('payments', 'warning', 'Se conservará la configuración local disponible.');
          }
        })(),
        (async () => {
          try {
            const generic = await fetchDefaultCustomer();
            setStep('customer', generic ? 'done' : 'warning', generic ? 'Cliente genérico disponible' : 'No se pudo confirmar el cliente genérico');
          } catch (error) {
            setStep('customer', 'warning', 'Se intentará utilizar la copia local disponible.');
          }
        })(),
        (async () => {
          try {
            const rules = await refreshPosRules({ silent: true });
            const count = Array.isArray(rules) ? rules.length : 0;
            setStep('rules', 'done', `${count} regla(s) POS disponibles`);
          } catch (error) {
            setStep('rules', 'warning', 'Se conservarán las reglas locales disponibles.');
          }
        })(),
      ]);

      setStartupSummary('Sincronizando catálogo local…');
      setStep('products', 'running', navigator.onLine ? 'Buscando cambios en productos…' : 'Sin conexión; verificando copia local…');

      let productsSyncResult = null;
      if (navigator.onLine) {
        productsSyncResult = await refreshPosProductsCache({ silent: true });
      }

      let finalProductCount = 0;
      try {
        finalProductCount = await getCachedPosProductCount();
        if (!cancelled) setPosProductsCacheCount(finalProductCount);
      } catch (error) {
        console.warn('[POS startup] No se pudo contar catálogo local:', error);
      }

      if (productsSyncResult) {
        setStep(
          'products',
          'done',
          `${finalProductCount.toLocaleString('es-CL')} productos · ${productsSyncResult.mode === 'full' ? 'sincronización completa' : 'actualización incremental'}`
        );
      } else if (finalProductCount > 0) {
        setStep('products', 'warning', `${finalProductCount.toLocaleString('es-CL')} productos disponibles desde la copia local`);
      } else {
        setStep('products', 'error', 'No existe todavía un catálogo local utilizable.');
      }

      setStartupSummary('Revisando contingencia…');
      setStep('contingency', 'running', 'Revisando ventas pendientes…');
      try {
        const before = await getOfflineSalesFromDB();
        const beforePending = before.filter((row) => !row.synced).length;

        if (navigator.onLine && beforePending > 0) {
          await syncOfflineSales({ force: true, silent: true });
        } else {
          await refreshOfflineQueueCount();
          await refreshContingencySales();
        }

        const after = await getOfflineSalesFromDB();
        const pending = after.filter((row) => !row.synced).length;
        if (!cancelled) setOfflineQueueCount(pending);

        setStep(
          'contingency',
          pending === 0 ? 'done' : 'warning',
          pending === 0 ? 'Sin ventas pendientes de sincronizar' : `${pending} venta(s) pendiente(s); se enviarán al recuperar conexión`
        );
      } catch (error) {
        console.error('[POS startup] contingencia:', error);
        setStep('contingency', 'warning', 'No se pudo sincronizar ahora; las ventas locales se mantienen protegidas.');
      }

      await finishAfterMinimumVisibleTime();
    })().catch(async (error) => {
      console.error('[POS startup] Error general:', error);
      if (!cancelled) {
        setStartupSummary('POS iniciado con advertencias');
        await finishAfterMinimumVisibleTime();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    fetchCajaEstado,
    fetchDefaultCustomer,
    fetchPaymentMethodsConfig,
    refreshContingencySales,
    refreshOfflineQueueCount,
    refreshPosProductsCache,
    refreshPosRules,
    syncOfflineSales,
    updateStartupStep,
  ]);

  const handleActualizarContingencia = useCallback(async () => {
    setVentasContingenciaLoading(true);

    try {
      await refreshContingencySales();

      if (!navigator.onLine) {
        isOnlineRef.current = false;
        setIsOnline(false);

        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'El navegador sigue sin conexión. Se muestran ventas locales.',
        });

        return;
      }

      await syncOfflineSales({
        force: true,
        silent: true,
      });

      await refreshContingencySales();

      isOnlineRef.current = true;
      setIsOnline(true);

      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Conexión verificada. Ventas de contingencia actualizadas.',
      });
    } catch (error) {
      console.error('Error actualizando contingencia:', error);

      if (isRecoverablePosConnectivityError(error)) {
        isOnlineRef.current = false;
        setIsOnline(false);
      }

      setSnackbar({
        open: true,
        severity: 'warning',
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'No se pudo conectar con el servidor. El POS sigue en modo contingencia.',
      });
    } finally {
      setVentasContingenciaLoading(false);
    }
  }, [refreshContingencySales, syncOfflineSales]);

  const handleSyncOfflineSale = useCallback(
    async (record) => {
      if (!record?.offlineId) return;

      if (!navigator.onLine) {
        isOnlineRef.current = false;
        setIsOnline(false);

        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'No hay conexión para sincronizar esta venta.',
        });

        return;
      }

      await syncOneOfflineSale(record);
    },
    [syncOneOfflineSale]
  );

  const handleSyncAllOfflineSales = useCallback(async () => {
    if (!navigator.onLine) {
      isOnlineRef.current = false;
      setIsOnline(false);

      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No hay conexión para sincronizar ventas pendientes.',
      });

      return;
    }

    await syncOfflineSales({
      force: true,
      silent: false,
      includeReview: true,
    });
  }, [syncOfflineSales]);

  const handlePrintContingencyRecord = useCallback((record) => {
    try {
      printContingencyTicket(record);
    } catch (error) {
      console.error('Error imprimiendo comprobante de contingencia:', error);

      setSnackbar({
        open: true,
        severity: 'error',
        message:
          error.message ||
          'No se pudo imprimir el comprobante de contingencia.',
      });
    }
  }, []);

  const getPdfUrlFromOfflineRecord = useCallback((record = {}) => {
    const serverData = record.serverData || {};
    const ventaId =
      record.ventaId ||
      getVentaIdFromServerData(serverData) ||
      serverData?.venta_id ||
      serverData?.id_venta ||
      null;

    const dteInfo = record.dteInfo || getDteInfoFromServerData(serverData);
    const tipoDte = String(dteInfo?.tipo_dte || '');

    const directPdf =
      serverData?.pdf_url ||
      serverData?.pdfUrl ||
      serverData?.dte?.pdf_url ||
      serverData?.data?.pdf_url ||
      serverData?.data?.dte?.pdf_url ||
      null;

    if (directPdf) return directPdf;
    if (!ventaId) return null;

    if (tipoDte === '33') return `/facturacion_dte33/${ventaId}/pdf?formato=80mm`;
    if (tipoDte === '34') return `/facturacion_dte34/${ventaId}/pdf?formato=80mm`;
    if (tipoDte === '39') return `/facturacion_dte39/${ventaId}/pdf?formato=80mm`;
    if (tipoDte === '41') return `/facturacion_dte41/${ventaId}/pdf?formato=80mm`;

    const documentTypeFromRecord = String(record.documentType || '').toLowerCase();

    if (documentTypeFromRecord === 'factura') {
      return `/facturacion_dte33/${ventaId}/pdf?formato=80mm`;
    }

    if (documentTypeFromRecord === 'factura_exenta') {
      return `/facturacion_dte34/${ventaId}/pdf?formato=80mm`;
    }

    if (documentTypeFromRecord === 'boleta_exenta') {
      return `/facturacion_dte41/${ventaId}/pdf?formato=80mm`;
    }

    return `/facturacion_dte39/${ventaId}/pdf?formato=80mm`;
  }, []);


  const handlePrintSyncedOfflineDte = useCallback(
    async (record) => {
      if (!record?.synced) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'Esta venta aún no está sincronizada. Primero debes sincronizarla.',
        });

        return;
      }

      const pdfUrlToCall = getPdfUrlFromOfflineRecord(record);

      if (!pdfUrlToCall) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'No se encontró información suficiente para imprimir el DTE de esta venta.',
        });

        return;
      }

      setVentasContingenciaLoading(true);

      try {
        const pdfResp = await api.get(pdfUrlToCall, {
          responseType: 'blob',
          timeout: POS_FAST_API_TIMEOUT_MS,
        });

        isOnlineRef.current = true;
        setIsOnline(true);
        printPdfBlobFast(pdfResp.data);

      } catch (error) {
        console.error('Error al imprimir DTE de contingencia:', error);

        if (isRecoverablePosConnectivityError(error)) {
          isOnlineRef.current = false;
          setIsOnline(false);
        }

        setSnackbar({
          open: true,
          severity: 'error',
          message:
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.response?.data?.mensaje ||
            'No se pudo descargar el DTE para impresión.',
        });
      } finally {
        setVentasContingenciaLoading(false);
      }
    },
    [getPdfUrlFromOfflineRecord, printPdfBlobFast]
  );

  const handleDeleteOfflineSale = useCallback(
    async (record) => {
      if (!record?.offlineId) return;

      const ok = window.confirm(
        record.synced
          ? `¿Eliminar localmente la venta de contingencia ${record.offlineId}?`
          : `La venta ${record.offlineId} aún no está sincronizada. ¿Eliminarla localmente de todas formas?`
      );

      if (!ok) return;

      try {
        await deleteOfflineSaleFromDB(record.offlineId);
        await refreshOfflineQueueCount();
        await refreshContingencySales();

        setSnackbar({
          open: true,
          severity: 'info',
          message: `Venta de contingencia ${record.offlineId} eliminada localmente.`,
        });
      } catch (error) {
        console.error('Error eliminando venta offline:', error);

        setSnackbar({
          open: true,
          severity: 'error',
          message:
            error.message ||
            'No se pudo eliminar la venta de contingencia.',
        });
      }
    },
    [refreshOfflineQueueCount, refreshContingencySales]
  );

  const handleClearSyncedOfflineSales = useCallback(async () => {
    const ok = window.confirm(
      '¿Eliminar localmente todas las ventas de contingencia ya sincronizadas?'
    );

    if (!ok) return;

    setVentasContingenciaLoading(true);

    try {
      const rows = await getOfflineSalesFromDB();
      const synced = rows.filter((record) => record.synced === true);

      for (const record of synced) {
        await deleteOfflineSaleFromDB(record.offlineId);
      }

      await refreshOfflineQueueCount();
      await refreshContingencySales();

      setSnackbar({
        open: true,
        severity: 'info',
        message:
          synced.length > 0
            ? `Se eliminaron ${synced.length} venta(s) sincronizada(s) del almacenamiento local.`
            : 'No había ventas sincronizadas para limpiar.',
      });
    } catch (error) {
      console.error('Error limpiando ventas sincronizadas:', error);

      setSnackbar({
        open: true,
        severity: 'error',
        message:
          error.message ||
          'No se pudieron limpiar las ventas sincronizadas.',
      });
    } finally {
      setVentasContingenciaLoading(false);
    }
  }, [refreshOfflineQueueCount, refreshContingencySales]);

  const handleRetryErroredOfflineSales = useCallback(async () => {
    if (!navigator.onLine) {
      isOnlineRef.current = false;
      setIsOnline(false);

      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No hay conexión para reintentar ventas con error.',
      });

      return;
    }

    setVentasContingenciaLoading(true);

    try {
      const rows = await getOfflineSalesFromDB();
      const errored = rows.filter((record) => !record.synced && record.syncError);

      if (!errored.length) {
        setSnackbar({
          open: true,
          severity: 'info',
          message: 'No hay ventas con error para reintentar.',
        });

        return;
      }

      let syncedCount = 0;

      for (const record of errored) {
        const result = await syncOneOfflineSale(record, {
          silent: true,
        });

        if (result) syncedCount += 1;
      }

      await refreshOfflineQueueCount();
      await refreshContingencySales();

      setSnackbar({
        open: true,
        severity: syncedCount > 0 ? 'success' : 'warning',
        message:
          syncedCount > 0
            ? `Se sincronizaron ${syncedCount} venta(s) con error.`
            : 'No se pudo sincronizar ninguna venta con error.',
      });
    } catch (error) {
      console.error('Error reintentando ventas con error:', error);

      if (isRecoverablePosConnectivityError(error)) {
        isOnlineRef.current = false;
        setIsOnline(false);
      }

      setSnackbar({
        open: true,
        severity: 'error',
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'No se pudieron reintentar las ventas con error.',
      });
    } finally {
      setVentasContingenciaLoading(false);
    }
  }, [syncOneOfflineSale, refreshOfflineQueueCount, refreshContingencySales]);

  /* ====================================================================
   * ONLINE / OFFLINE LISTENERS
   * ==================================================================== */
  useEffect(() => {
    const updateOnlineStatus = () => {
      const networkAvailable = navigator.onLine;

      if (!networkAvailable) {
        isOnlineRef.current = false;
        setIsOnline(false);
        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'POS en modo contingencia. Las ventas se guardarán localmente.',
        });
        return;
      }

      // Recuperar Internet no garantiza que Cloudflare/API esté disponible.
      // Verificamos el backend antes de declarar nuevamente el POS "Online".
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Conectividad de red recuperada. Verificando API…',
      });

      window.setTimeout(() => {
        recoverPosApiConnectivity({ announce: true });
      }, 500);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    if (!navigator.onLine) {
      isOnlineRef.current = false;
      setIsOnline(false);
    }

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, [recoverPosApiConnectivity]);

  useEffect(() => {
    refreshOfflineQueueCount();
    refreshContingencySales();
  }, [refreshOfflineQueueCount, refreshContingencySales]);

  useEffect(() => {
    if (!isOnline) return;

    const runOfflineQueue = () => {
      syncOfflineSales({
        force: false,
        silent: true,
        includeReview: false,
      });
    };

    // Primer intento poco después de quedar Online.
    const initialTimer = window.setTimeout(runOfflineQueue, 1200);

    // Mientras el POS siga Online, la cola se revisa periódicamente. Esto evita
    // depender de un cambio de isOnline o de una acción manual para enviar ventas.
    const retryTimer = window.setInterval(
      runOfflineQueue,
      POS_OFFLINE_QUEUE_RETRY_INTERVAL_MS
    );

    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(retryTimer);
    };
  }, [isOnline, syncOfflineSales]);

  useEffect(() => {
    if (!ventasContingenciaOpen) return;

    refreshContingencySales();
  }, [ventasContingenciaOpen, refreshContingencySales]);

  /* ====================================================================
   * AUTO PRINT PDF
   * ==================================================================== */
  useEffect(() => {
    if (!shouldAutoPrint || !printUrl) return;

    const iframe = printIframeRef.current;

    if (!iframe) return;

    const timer = window.setTimeout(() => {
      try {
        iframe.contentWindow?.focus?.();
        iframe.contentWindow?.print?.();
      } catch (error) {
        console.error('Error imprimiendo iframe:', error);
      } finally {
        setShouldAutoPrint(false);
      }
    }, 900);

    return () => window.clearTimeout(timer);
  }, [shouldAutoPrint, printUrl]);

  const handleIframeLoad = useCallback(() => {
    if (!shouldAutoPrint) return;

    const iframe = printIframeRef.current;

    if (!iframe) return;

    window.setTimeout(() => {
      try {
        iframe.contentWindow?.focus?.();
        iframe.contentWindow?.print?.();
      } catch (error) {
        console.error('Error autoimprimiendo:', error);
      } finally {
        setShouldAutoPrint(false);
      }
    }, 500);
  }, [shouldAutoPrint]);

  /* ====================================================================
   * HELPERS VISUALES PARA REGLAS POS
   * ==================================================================== */
  const reglasAplicadasVisibles = useMemo(() => {
    return Array.isArray(posRulesEvaluation?.ajustes)
      ? posRulesEvaluation.ajustes
      : [];
  }, [posRulesEvaluation]);

  const reglasPendientesPinVisibles = useMemo(() => {
    return Array.isArray(posRulesEvaluation?.reglasPendientesPin)
      ? posRulesEvaluation.reglasPendientesPin
      : [];
  }, [posRulesEvaluation]);

  const hasReglasPosAplicadas = reglasAplicadasVisibles.length > 0;
  const hasReglasPendientesPin = reglasPendientesPinVisibles.length > 0;

  const renderRulesSummary = useCallback(() => {
    if (!hasReglasPosAplicadas && !hasReglasPendientesPin) return null;

    return (
<Paper
  elevation={0}
  sx={{
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    width: '100%',
    maxWidth: '100%',
  }}
>
  <Box
    sx={{
      px: 2,
      py: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.1 }}>
        Productos
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {items.length
          ? `${items.length} línea(s) en la venta`
          : 'Agrega productos para iniciar la venta.'}
      </Typography>
    </Box>

    <Stack direction="row" spacing={1}>
      <Button
        variant="contained"
        size="small"
        onClick={() => handleOpenProductSearchModal('search')}
        disabled={!isCajaAbierta}
        sx={{
          textTransform: 'none',
          fontWeight: 800,
          whiteSpace: 'nowrap',
        }}
      >
        Buscar
      </Button>

      <Button
        variant="outlined"
        size="small"
        onClick={handleOpenImportModal}
        disabled={!isCajaAbierta || !isOnline || items.length > 0}
        sx={{
          textTransform: 'none',
          fontWeight: 800,
          whiteSpace: 'nowrap',
        }}
      >
        Importar
      </Button>
    </Stack>
  </Box>

  {items.length === 0 ? (
    renderEmptyCart()
  ) : (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header compacto */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr) 96px',
            md: 'minmax(0, 1fr) 112px 96px 104px 40px',
          },
          gap: 1,
          alignItems: 'center',
          px: 2,
          py: 0.85,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" fontWeight={900}>
          Producto
        </Typography>

        <Typography variant="caption" fontWeight={900} align="center">
          Cant.
        </Typography>

        <Typography
          variant="caption"
          fontWeight={900}
          align="right"
          sx={{ display: { xs: 'none', md: 'block' } }}
        >
          Unit.
        </Typography>

        <Typography
          variant="caption"
          fontWeight={900}
          align="right"
          sx={{ display: { xs: 'none', md: 'block' } }}
        >
          Total
        </Typography>

        <Box sx={{ display: { xs: 'none', md: 'block' } }} />
      </Box>

      {/* Filas */}
      <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
        {items.map((item, index) => {
          const qty = Number(item.quantity || 0);

          const unitGross = getItemGrossUnit(item);
          const linePricing = getItemLinePricingForDisplay(item);
          const lineTotal = linePricing.final;

          const productName = getItemDisplayName(item);

          const skuBarcode = [
            item.sku ? `SKU: ${item.sku}` : '',
            item.barcode || item.codigo_barras
              ? `Cod: ${item.barcode || item.codigo_barras}`
              : '',
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <Box
              key={`${item.product_id || item.id_producto || item.id || 'item'}-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr) 96px',
                  md: 'minmax(0, 1fr) 112px 96px 104px 40px',
                },
                gap: 1,
                alignItems: 'center',
                px: 2,
                py: 1,
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {/* Producto */}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={900}
                  title={productName}
                  sx={{
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {productName}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  title={skuBarcode || 'Sin SKU / código'}
                  sx={{
                    display: 'block',
                    mt: 0.25,
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {skuBarcode || 'Sin SKU / código'}
                </Typography>

                {linePricing.hasPromotion && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`Promo · ahorro ${formatter.format(Math.abs(linePricing.adjustment))}`}
                    sx={{ mt: 0.6, height: 20, fontSize: 10, fontWeight: 800 }}
                  />
                )}

                {/* En móvil mostramos precios debajo del producto */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    mt: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Unit: <strong>{formatter.format(unitGross)}</strong>
                  </Typography>

                  {linePricing.hasPromotion ? (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        Normal: {formatter.format(linePricing.original)}
                      </Typography>
                      <Typography variant="caption" color="success.main" fontWeight={900}>
                        Promo: {formatter.format(lineTotal)}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Total: <strong>{formatter.format(lineTotal)}</strong>
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Cantidad */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {item.isWeight ? (
                  <Typography
                    variant="body2"
                    fontWeight={900}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {qty.toFixed(3)} kg
                  </Typography>
                ) : (
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={0.25}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => requestDecrementItemQuantity(item, index)}
                      sx={{
                        minWidth: 24,
                        width: 24,
                        height: 26,
                        px: 0,
                        fontWeight: 900,
                        lineHeight: 1,
                      }}
                    >
                      -
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenEditQtyModal(item, index)}
                      sx={{
                        minWidth: 38,
                        width: 38,
                        height: 26,
                        px: 0,
                        fontWeight: 900,
                        lineHeight: 1,
                      }}
                    >
                      {qty}
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => requestIncrementItemQuantity(item, index)}
                      sx={{
                        minWidth: 24,
                        width: 24,
                        height: 26,
                        px: 0,
                        fontWeight: 900,
                        lineHeight: 1,
                      }}
                    >
                      +
                    </Button>
                  </Stack>
                )}
              </Box>

              {/* Unitario */}
              <Typography
                variant="body2"
                align="right"
                sx={{
                  display: { xs: 'none', md: 'block' },
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                }}
              >
                {formatter.format(unitGross)}
              </Typography>

              {/* Total línea */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  minWidth: 0,
                }}
              >
                {linePricing.hasPromotion && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textDecoration: 'line-through', lineHeight: 1 }}
                  >
                    {formatter.format(linePricing.original)}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  fontWeight={900}
                  color={linePricing.hasPromotion ? 'success.main' : 'text.primary'}
                  sx={{ whiteSpace: 'nowrap', fontSize: 12 }}
                >
                  {formatter.format(lineTotal)}
                </Typography>
              </Box>

              {/* Eliminar */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center',
                }}
              >
                <Tooltip title="Eliminar producto">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDeleteItemClick(item, index)}
                    sx={{
                      width: 28,
                      height: 28,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  )}
</Paper>
    );
  }, [
    hasReglasPosAplicadas,
    hasReglasPendientesPin,
    posRulesLoading,
    posRules.length,
    posRulesLastSync,
    reglasAplicadasVisibles,
    reglasPendientesPinVisibles,
  ]);

  /* ====================================================================
   * HELPERS FINALES UI / MODALES
   * ==================================================================== */
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleChangeDocumentType = useCallback(
    (nextType) => {
      const normalizedNextType = String(nextType || '').trim().toLowerCase();

      // BOLETA es una transición explícita al flujo de consumidor final.
      // No se valida contra el cliente de factura actualmente seleccionado,
      // porque al cambiar a boleta debemos restaurar inmediatamente el
      // cliente genérico (66.666.666-6), tal como al iniciar el POS.
      if (normalizedNextType === 'boleta') {
        setPendingDocumentTypeAfterCustomer(null);
        setDocumentType('boleta');
        setCustomer(defaultCustomer || null);
        setInvoiceTransferDialogOpen(false);
        return;
      }

      const allowed = getAllowedDocTypes(customer);

      if (allowed.includes(normalizedNextType)) {
        setPendingDocumentTypeAfterCustomer(null);
        setDocumentType(normalizedNextType);

        if (normalizedNextType === 'factura') {
          const transferDefaults = createInvoiceTransferData({ cajaData, customer });

          setInvoiceDeliveryMode(INVOICE_DELIVERY_MODE.RETIRO_CLIENTE);
          setInvoiceTransferData((prev) => ({
            ...mergeInvoiceTransferDefaults(prev, transferDefaults),
            transportista_tipo: TRANSPORTER_TYPE.COMPRADOR,
            transportista_rut: formatRutChile(customer?.rut),
            transportista_nombre: getCustomerDisplayName(customer),
          }));
          setInvoiceTransferDialogOpen(true);
        }

        return;
      }

      if (normalizedNextType === 'factura') {
        setPendingDocumentTypeAfterCustomer('factura');
        handleOpenCustomerModal();

        setSnackbar({
          open: true,
          severity: 'info',
          message: 'Seleccione un cliente con giro para emitir factura.',
        });

        return;
      }

      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'El cliente seleccionado no permite ese tipo de documento.',
      });
    },
    [customer, defaultCustomer, cajaData]
  );

  const handleOpenImportModal = useCallback(() => {
    if (!isCajaAbierta) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Debe abrir caja antes de importar documentos.',
      });
      return;
    }

    if (!isOnline) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No se puede importar documentos en modo contingencia.',
      });
      return;
    }

    if (items.length > 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message:
          'Hay una venta en curso. Déjala en espera o inicia una nueva venta antes de importar.',
      });
      return;
    }

    setImportModalOpen(true);
  }, [isCajaAbierta, isOnline, items.length]);

  const handleOpenVentasPendientes = useCallback(() => {
    setVentasEnEspera(cargarVentasEnEspera());
    setVentasPendientesOpen(true);
  }, []);

  useEffect(() => {
    if (!paymentModalOpen) return;

    if (importedOrderPaid) {
      setSelectedPaymentMethod('');
      setCashGivenStateInternal('');
      setVoucherNumber('');
      setChange(0);
      return;
    }

    if (availablePaymentMethods.length > 0) {
      if (!selectedPaymentMethod) {
        const cashMethod =
          availablePaymentMethods.find((method) => isCashMethod(method)) ||
          availablePaymentMethods.find(
            (method) =>
              normalizePosPaymentMethodKey(method?.key || method?.clave) === 'EFECTIVO'
          ) ||
          null;

        const defaultMethod = cashMethod || availablePaymentMethods[0];

        setSelectedPaymentMethod(
          defaultMethod.selectionValue ||
            getPaymentSelectionValue(defaultMethod) ||
            defaultMethod.key ||
            defaultMethod.clave ||
            ''
        );
      }

      return;
    }

    fetchPaymentMethodsConfig().then((methods) => {
      if (methods?.length && !selectedPaymentMethod) {
        const cashMethod =
          methods.find((method) => isCashMethod(method)) ||
          methods.find(
            (method) =>
              normalizePosPaymentMethodKey(method?.key || method?.clave) === 'EFECTIVO'
          ) ||
          null;

        const defaultMethod = cashMethod || methods[0];

        setSelectedPaymentMethod(
          defaultMethod.selectionValue ||
            getPaymentSelectionValue(defaultMethod) ||
            defaultMethod.key ||
            defaultMethod.clave ||
            ''
        );
      }
    });

  }, [
    paymentModalOpen,
    importedOrderPaid,
    availablePaymentMethods,
    selectedPaymentMethod,
    fetchPaymentMethodsConfig,
  ]);

  const posRuleDisplayAdjustments = useMemo(() => {
    return collectPayloadRuleAdjustments(posRulesEvaluation);
  }, [posRulesEvaluation]);

  const getItemRuleAdjustmentForDisplay = useCallback(
    (item) => {
      const productKey = String(getItemProductId(item) || '');
      const skuKey = getItemSku(item);
      const barcodeKey = getItemBarcode(item);

      return (
        Number(posRuleDisplayAdjustments.byProductId.get(productKey) || 0) +
        Number(posRuleDisplayAdjustments.bySku.get(skuKey) || 0) +
        Number(posRuleDisplayAdjustments.byBarcode.get(barcodeKey) || 0)
      );
    },
    [posRuleDisplayAdjustments]
  );

  const getItemLinePricingForDisplay = useCallback(
    (item) => {
      const original = Math.round(getItemGrossLine(item));
      const adjustment = Math.round(getItemRuleAdjustmentForDisplay(item));
      const final = Math.max(0, Math.round(original + adjustment));

      return {
        original,
        adjustment,
        final,
        hasPromotion: adjustment < 0 && final !== original,
      };
    },
    [getItemRuleAdjustmentForDisplay]
  );

  const renderItemLineTotal = useCallback(
    (item) => getItemLinePricingForDisplay(item).final,
    [getItemLinePricingForDisplay]
  );

  const renderEmptyCart = () => (
    <Box
      sx={{
        minHeight: 300,
        display: 'grid',
        placeItems: 'center',
        textAlign: 'center',
        color: 'text.secondary',
      }}
    >
      <Box>
        <PointOfSaleIcon sx={{ fontSize: 64, opacity: 0.25, mb: 1 }} />
        <Typography variant="h6" fontWeight={900} color="text.primary">
          Venta sin productos
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Lee un código de barras o presiona F3 para buscar productos.
        </Typography>
      </Box>
    </Box>
  );

  // El estado principal del navbar representa conectividad y ventas offline.
  // La sincronización de catálogo/reglas se muestra en sus indicadores propios y
  // no debe hacer parecer que se están enviando ventas pendientes.
  const posConnectionStatus =
    syncingOffline
      ? 'syncing'
      : isOnline
        ? 'online'
        : 'contingency';

  const posConnectionLabel =
    posConnectionStatus === 'syncing'
      ? 'Sincronizando ventas…'
      : posConnectionStatus === 'online'
        ? 'Online'
        : 'Modo contingencia';

  const posConnectionColor =
    posConnectionStatus === 'syncing'
      ? 'info'
      : posConnectionStatus === 'online'
        ? 'success'
        : 'error';

  const posConnectionTooltip =
    posConnectionStatus === 'syncing'
      ? 'Enviando ventas de contingencia pendientes al servidor.'
      : posConnectionStatus === 'online'
        ? posProductsSyncing
          ? 'POS conectado y operativo. El catálogo local se está actualizando en segundo plano.'
          : 'POS conectado y operativo. Presiona para ver contingencia.'
        : apiRecoveryChecking
          ? 'Modo contingencia activo. Verificando si la API volvió a estar disponible…'
          : `Modo contingencia activo. Reintento automático cada ${Math.round(POS_API_RECOVERY_INTERVAL_MS / 1000)} s. Presiona para ver ventas pendientes.`;

  /* ====================================================================
   * RENDER
   * ==================================================================== */
  return (
    <Box sx={{ bgcolor: '#f5f6fa', minHeight: '100vh' }}>
      <Dialog
        open={startupModalOpen}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(5px)',
            bgcolor: 'rgba(10, 18, 32, 0.72)',
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  flexShrink: 0,
                }}
              >
                <PointOfSaleIcon sx={{ fontSize: 30 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Preparando Punto de Venta
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {startupSummary}
                </Typography>
              </Box>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {POS_STARTUP_STEPS.map((definition, index) => {
                const step = startupSteps[definition.id] || definition;
                const isRunning = step.status === 'running';
                const isDone = step.status === 'done';
                const isWarning = step.status === 'warning';
                const isError = step.status === 'error';

                return (
                  <Box
                    key={definition.id}
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderBottom: index < POS_STARTUP_STEPS.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: isRunning ? 'action.hover' : 'transparent',
                      transition: 'background-color 180ms ease',
                    }}
                  >
                    <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                      {isRunning ? (
                        <CircularProgress size={22} thickness={5} />
                      ) : isDone ? (
                        <CheckCircleOutlineIcon color="success" />
                      ) : isWarning ? (
                        <WarningAmberIcon color="warning" />
                      ) : isError ? (
                        <ErrorOutlineIcon color="error" />
                      ) : definition.id === 'storage' ? (
                        <StorageIcon color="disabled" fontSize="small" />
                      ) : (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'action.disabled' }} />
                      )}
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: isRunning || isDone ? 800 : 700 }}>
                        {definition.label}
                      </Typography>
                      {step.detail ? (
                        <Typography
                          variant="caption"
                          color={isError ? 'error.main' : isWarning ? 'warning.main' : 'text.secondary'}
                          sx={{ display: 'block', mt: 0.2 }}
                        >
                          {step.detail}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Paper>

            <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                Sincronizando información necesaria para operar
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <NavbarPos
        onToggleCaja={handleOpenCajaModal}
        onF1={handleSolicitarNuevaVenta}
        onF2={() => setConsultorPreciosOpen(true)}
        onF3={() => handleOpenProductSearchModal('search')}
        onF4={handleOpenImportModal}
        onF6={handleGuardarVentaEnEspera}
        onF7={handleOpenVentasPendientes}
        onF8={handleOpenCustomerModal}
        onF9={handleOpenPaymentModal}
        onF10={handleOpenMovimientosCaja}
        onF11={openVentasContingencia}
        cajaAbierta={isCajaAbierta}
        loadingEstado={cajaEstadoLoading}
        documentType={documentType}
        posConnectionStatus={posConnectionStatus}
        posConnectionLabel={posConnectionLabel}
        posConnectionColor={posConnectionColor}
        posConnectionTooltip={posConnectionTooltip}
        offlineQueueCount={offlineQueueCount}
        onConnectionClick={openVentasContingencia}
      />

      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          pt: '76px',
        }}
                >
          <Box
            sx={{
              flexGrow: 1,
              width: { xs: '100%', md: 'calc(100vw - 360px)' },
              maxWidth: { xs: '100%', md: 'calc(100vw - 360px)' },
              pr: { xs: 1, md: 1.5 },
              pl: { xs: 1, md: 1.5 },
              pb: 2,
              minWidth: 0,
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Tooltip
                  title={
                    posProductsLastSync
                      ? `Última sincronización catálogo: ${new Date(posProductsLastSync).toLocaleString('es-CL')}`
                      : 'El catálogo local se cargará automáticamente cuando haya conexión.'
                  }
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      posProductsSyncing
                        ? 'Catálogo local: sincronizando…'
                        : `Catálogo local: ${posProductsCacheCount}`
                    }
                    onClick={() => refreshPosProductsCache({ forceFull: true, silent: false })}
                    sx={{ fontWeight: 800 }}
                  />
                </Tooltip>

                {cajaFromCache && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Caja: copia local"
                    sx={{ fontWeight: 800 }}
                  />
                )}

                <Tooltip title="Importar documento">
                  <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AssignmentIcon />} 
                  onClick={handleOpenImportModal}
                  disabled={!isCajaAbierta || !isOnline || items.length > 0}
                  sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      textTransform: 'none',
                  }}
                >
                  Importar
                </Button>
                                  </span>
                </Tooltip>

                <Tooltip title="Actualizar reglas POS">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => refreshPosRules({ silent: false })}
                      disabled={posRulesLoading}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 800,
                        textTransform: 'none',
                      }}
                    >
                      {posRulesLoading ? 'Actualizando reglas...' : `Reglas POS: ${posRules.length}`}
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Dejar venta actual en espera">
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<PauseCircleOutlineIcon />}
                      disabled={!items.length}
                      onClick={handleGuardarVentaEnEspera}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 800,
                        textTransform: 'none',
                      }}
                    >
                      Dejar en espera
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Ver ventas en espera">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PendingActionsIcon />}
                    onClick={handleOpenVentasPendientes}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      textTransform: 'none',
                    }}
                  >
                    En espera
                    {ventasEnEspera.length > 0 ? ` · ${ventasEnEspera.length}` : ''}
                  </Button>
                </Tooltip>
                <Tooltip
                    title={
                      lastVenta?.folio
                        ? `Reimprimir último DTE emitido en esta caja · Folio ${lastVenta.folio}`
                        : 'Reimprimir último DTE emitido en esta caja'
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={
                          reprintLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <PrintIcon />
                          )
                        }
                        onClick={handleRequestReprint}
                        disabled={!isCajaAbierta || reprintLoading}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 800,
                          textTransform: 'none',
                        }}
                      >
                        Reimprimir DTE
                      </Button>
                    </span>
                  </Tooltip>

              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {importSource && (
                  <Chip
                    color="info"
                    variant="outlined"
                    label={`${String(importSource.type || 'documento').toUpperCase()} #${
                      importSource.id || ''
                    }`}
                  />
                )}

                {importedOrderPaid && (
                  <Chip
                    color="success"
                    variant="outlined"
                    label={`Pago origen: ${importedPaymentLabel}`}
                  />
                )}

              </Stack>
            </Stack>
          </Paper>

          {documentType === 'factura' && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: invoiceDeliveryMode ? 'divider' : 'warning.main',
                bgcolor: 'background.paper',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: invoiceDeliveryMode ? 'action.hover' : 'warning.light',
                      flexShrink: 0,
                    }}
                  >
                    <LocalShippingIcon
                      color={invoiceDeliveryMode ? 'primary' : 'warning'}
                      fontSize="small"
                    />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography variant="subtitle2" fontWeight={900}>
                        Entrega / traslado · Factura 33
                      </Typography>

                      <Chip
                        size="small"
                        color={invoiceDeliveryMode ? 'primary' : 'warning'}
                        variant={invoiceDeliveryMode ? 'outlined' : 'filled'}
                        label={
                          invoiceDeliveryMode
                            ? getInvoiceDeliveryModeLabel(invoiceDeliveryMode)
                            : 'Pendiente de definir'
                        }
                        sx={{ fontWeight: 800 }}
                      />
                    </Stack>

                    {!invoiceDeliveryMode ? (
                      <Typography variant="caption" color="text.secondary">
                        Antes de pagar debes indicar si esta factura ampara el traslado de la mercadería.
                      </Typography>
                    ) : invoiceDeliveryMode === INVOICE_DELIVERY_MODE.NO_AMPARA ? (
                      <Typography variant="caption" color="text.secondary">
                        Esta Factura 33 documentará la venta, pero no será el documento que ampare un traslado.
                      </Typography>
                    ) : invoiceDeliveryMode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE ? (
                      <Typography variant="caption" color="text.secondary">
                        Retiro directo en el establecimiento. Factura suficiente para el traslado
                        (Res. SII 144/2026, resolutivo 15°). Sin datos adicionales de transporte.
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25 }}
                      >
                        {`Origen: ${invoiceTransferData.origen_direccion || 'pendiente'}${
                          invoiceTransferData.origen_comuna
                            ? `, ${invoiceTransferData.origen_comuna}`
                            : ''
                        } · Destino: ${invoiceTransferData.destino_direccion || 'pendiente'}${
                          invoiceTransferData.destino_comuna
                            ? `, ${invoiceTransferData.destino_comuna}`
                            : ''
                        } · Patente: ${
                          parseBoolish(invoiceTransferData.patente_conocida, true)
                            ? invoiceTransferData.patente || 'pendiente'
                            : 'no conocida al emitir'
                        }`}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                <Button
                  variant={invoiceDeliveryMode ? 'outlined' : 'contained'}
                  color={invoiceDeliveryMode ? 'primary' : 'warning'}
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={openInvoiceTransferDialog}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 900,
                    textTransform: 'none',
                    flexShrink: 0,
                  }}
                >
                  {invoiceDeliveryMode ? 'Editar entrega' : 'Definir entrega'}
                </Button>
              </Stack>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Productos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {items.length
                    ? `${items.length} línea(s) en la venta`
                    : 'Agrega productos para iniciar la venta.'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleOpenProductSearchModal('search')}
                  disabled={!isCajaAbierta}
                >
                  Buscar producto
                </Button>
              </Stack>
            </Box>

            {items.length === 0 ? (
              renderEmptyCart()
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ width: '52%' }}>Producto</TableCell>
                      <TableCell align="center" sx={{ width: 120 }}>Cant.</TableCell>
                      <TableCell align="right" sx={{ width: 110 }}>Unit.</TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>Total</TableCell>
                      <TableCell align="center" sx={{ width: 56 }} />
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {items.map((item, index) => {
                      const qty = Number(item.quantity || 0);
                      const unitGross = getItemGrossUnit(item);
                      const linePricing = getItemLinePricingForDisplay(item);
                      const lineTotal = linePricing.final;

                      return (
                        <TableRow key={`${item.product_id || item.id_producto || item.id}-${index}`} hover>
                          <TableCell sx={{ maxWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={900}
                              sx={{
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={getItemDisplayName(item)}
                            >
                              {getItemDisplayName(item)}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={[
                                item.sku ? `SKU: ${item.sku}` : '',
                                item.barcode || item.codigo_barras ? `Cod: ${item.barcode || item.codigo_barras}` : '',
                              ].filter(Boolean).join(' · ')}
                            >
                              {[
                                item.sku ? `SKU: ${item.sku}` : '',
                                item.barcode || item.codigo_barras ? `Cod: ${item.barcode || item.codigo_barras}` : '',
                              ].filter(Boolean).join(' · ') || 'Sin SKU / código'}
                            </Typography>

                            {linePricing.hasPromotion && (
                              <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                label={`Promo · ahorro ${formatter.format(Math.abs(linePricing.adjustment))}`}
                                sx={{ mt: 0.5, height: 20, fontSize: 10, fontWeight: 800 }}
                              />
                            )}
                          </TableCell>

                          <TableCell align="center">
                            {item.isWeight ? (
                              <Typography fontWeight={800}>{qty.toFixed(3)} kg</Typography>
                            ) : (
                              <Stack
                                direction="row"
                                justifyContent="center"
                                alignItems="center"
                                spacing={0.35}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => requestDecrementItemQuantity(item, index)}
                                  sx={{ minWidth: 28, px: 0.25 }}
                                >
                                  -
                                </Button>

                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOpenEditQtyModal(item, index)}
                                  sx={{
                                    minWidth: 44,
                                    px: 0.5,
                                    fontWeight: 900,
                                  }}
                                >
                                  {qty}
                                </Button>

                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => requestIncrementItemQuantity(item, index)}
                                  sx={{ minWidth: 28, px: 0.25 }}
                                >
                                  +
                                </Button>
                              </Stack>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            {formatter.format(unitGross)}
                          </TableCell>

                          <TableCell align="right">
                            {linePricing.hasPromotion && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', textDecoration: 'line-through', lineHeight: 1 }}
                              >
                                {formatter.format(linePricing.original)}
                              </Typography>
                            )}
                            <Typography
                              fontWeight={900}
                              color={linePricing.hasPromotion ? 'success.main' : 'text.primary'}
                            >
                              {formatter.format(lineTotal)}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteItemClick(item, index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
      </Box>

        <PosRightSidebar
          customer={customer}
          onOpenCustomerModal={handleOpenCustomerModal}
          totalPayable={totalPayable}
          totalTax={totalTax}
          documentType={documentType}
          docOptions={docOptions}
          onChangeDocumentType={handleChangeDocumentType}
          onOpenPaymentModal={handleOpenPaymentModal}
          paymentButtonLabel={importedOrderPaid ? 'Emitir venta pagada' : undefined}
          paymentButtonHint={importedOrderPaid ? `Pago heredado: ${importedPaymentLabel}` : undefined}
          itemsCount={items.length}
          totalNet={totalNet}
          totalGross={totalGross}
          baseTotalGross={baseTotalGross}
          totalRulesAdjustment={totalRulesAdjustment}
          rulesSummary={rulesSnapshot}
        />
      </Box>

      {printUrl && (
        <iframe
          ref={printIframeRef}
          title="POS-print-frame"
          src={printUrl}
          style={{
            width: 0,
            height: 0,
            border: 'none',
            position: 'absolute',
            left: -9999,
            top: -9999,
          }}
          onLoad={handleIframeLoad}
        />
      )}

      <CustomerModal
        open={customerModalOpen}
        customers={customers}
        totalClientes={totalClientes}
        customerSearchTerm={customerSearch}
        handleCustomerSearch={handleCustomerSearch}
        onKeyDown={handleKeyDownCustomer}
        inputRef={customerSearchRef}
        selectedCustomerIndex={selectedCustomerIndex}
        handleSelectCustomer={handleSelectCustomer}
        onClose={() => setCustomerModalOpen(false)}
        onClearCustomer={handleClearCustomer}
      />

      <ProductSearchModal
        open={productSearchModalOpen}
        onClose={handleCloseProductSearchModal}
        productSearchTerm={productSearchTerm}
        handleProductSearch={handleProductSearch}
        onKeyDownSearch={handleKeyDownProductSearch}
        productResults={productResults}
        selectedProductIndex={modalMode === 'search' ? selectedIndex : -1}
        handleSelectProduct={handleSelectProduct}
        totalProductos={totalProductos}
        suggestions={suggestions}
        selectedSuggestionIndex={modalMode === 'suggestions' ? selectedIndex : -1}
        handleSelectSuggestion={handleSelectSuggestion}
        suggestionMode={modalMode === 'suggestions'}
        inputRef={productSearchRef}
      />

      <Dialog
        open={invoiceTransferDialogOpen}
        onClose={closeInvoiceTransferDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <LocalShippingIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Entrega y traslado · Factura 33
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Define si esta factura será el documento que ampare el traslado físico de la mercadería.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.25}>
            <Box>
              <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                ¿Cómo se entregará esta mercadería?
              </Typography>

              <RadioGroup
                value={invoiceDeliveryMode}
                onChange={(event) => handleInvoiceDeliveryModeChange(event.target.value)}
              >
                <FormControlLabel
                  value={INVOICE_DELIVERY_MODE.NO_AMPARA}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={800}>
                        La factura no ampara el traslado
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        La venta se factura ahora, pero esta Factura 33 no respaldará el traslado físico.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value={INVOICE_DELIVERY_MODE.RETIRO_CLIENTE}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={800}>
                        El cliente retira la mercadería
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Retiro directo desde el local del vendedor, amparado con la factura 33.
                        No requiere chofer, patente ni transportista.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value={INVOICE_DELIVERY_MODE.DESPACHO}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={800}>
                        Despacharemos la mercadería
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        El emisor o un tercero transportará la mercadería hasta el destino.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>

            {invoiceDeliveryMode === INVOICE_DELIVERY_MODE.NO_AMPARA && (
              <Alert severity="info">
                No se solicitarán datos de transporte. La Factura 33 documentará la venta, pero
                quedará marcada como <strong>no ampara traslado</strong>.
              </Alert>
            )}

            {invoiceDeliveryMode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE && (
              <Alert severity="success">
                <strong>Retiro directo por el comprador.</strong> Conforme al resolutivo 15° de la
                Resolución Exenta SII N.º 144/2026, la factura 33 ampara el traslado
                desde este establecimiento. No se solicitan datos adicionales de
                transporte, destino, conductor ni vehículo. Entrega la factura al
                cliente para que pueda exhibirla durante el traslado.
                Si el vendedor organiza el transporte, selecciona «Despacharemos la mercadería».
              </Alert>
            )}

            {invoiceDeliveryMode === INVOICE_DELIVERY_MODE.DESPACHO && (
              <>
                <Alert severity="warning">
                  La modalidad despacho conserva los datos operacionales, pero su incorporación
                  al XML de la Factura 33 aún requiere adaptar facturacion_dte33.js.
                  No se debe considerar este formulario como una implementación tributaria
                  completa del despacho.
                </Alert>
                <Divider />

                <Box>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight={900}>
                      Origen
                    </Typography>

                    {firstText(
                      invoiceTransferData.origen_direccion,
                      invoiceTransferData.origen_comuna
                    ) && !invoiceOriginManualEdit && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setInvoiceOriginManualEdit(true)}
                      >
                        Cambiar origen
                      </Button>
                    )}
                  </Stack>

                  {firstText(
                    invoiceTransferData.origen_direccion,
                    invoiceTransferData.origen_comuna
                  ) && !invoiceOriginManualEdit ? (
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                      <Typography variant="body2" fontWeight={900}>
                        {invoiceTransferData.origen_nombre || 'Punto de salida de la caja activa'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {invoiceTransferData.origen_direccion || 'Dirección no informada'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[invoiceTransferData.origen_comuna]
                          .filter(Boolean)
                          .join(' · ') || 'Comuna no informada'}
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      {!firstText(
                        invoiceTransferData.origen_direccion,
                        invoiceTransferData.origen_comuna
                      ) && (
                        <Alert severity="warning" sx={{ mb: 1.25 }}>
                          La caja activa no entregó una dirección de salida completa. Completa el origen
                          una vez; lo recomendable es que <strong>/pos_cajas/estado</strong> informe la
                          sucursal y su dirección para dejar este paso totalmente automático.
                        </Alert>
                      )}

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '1.2fr 2fr 1fr' },
                          gap: 1.25,
                        }}
                      >
                        <TextField
                          size="small"
                          label="Sucursal / punto de salida"
                          value={invoiceTransferData.origen_nombre || ''}
                          onChange={(event) =>
                            updateInvoiceTransferField('origen_nombre', event.target.value)
                          }
                          placeholder="Sucursal actual"
                        />
                        <TextField
                          size="small"
                          required
                          label="Dirección de origen"
                          value={invoiceTransferData.origen_direccion || ''}
                          onChange={(event) =>
                            updateInvoiceTransferField('origen_direccion', event.target.value)
                          }
                        />
                        <TextField
                          size="small"
                          required
                          label="Comuna de origen"
                          value={invoiceTransferData.origen_comuna || ''}
                          onChange={(event) =>
                            updateInvoiceTransferField('origen_comuna', event.target.value)
                          }
                        />
                      </Box>

                      {invoiceOriginManualEdit &&
                        firstText(
                          invoiceTransferData.origen_direccion,
                          invoiceTransferData.origen_comuna
                        ) && (
                          <Box sx={{ mt: 0.75, textAlign: 'right' }}>
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => setInvoiceOriginManualEdit(false)}
                            >
                              Usar resumen
                            </Button>
                          </Box>
                        )}
                    </>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={900}>
                    Destino
                  </Typography>

                  <RadioGroup
                    row
                    value={invoiceTransferData.destino_tipo || DESTINATION_TYPE.CLIENTE}
                    onChange={(event) =>
                      handleInvoiceDestinationTypeChange(event.target.value)
                    }
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel
                      value={DESTINATION_TYPE.CLIENTE}
                      control={<Radio size="small" />}
                      label="Dirección del cliente"
                    />
                    <FormControlLabel
                      value={DESTINATION_TYPE.OTRA}
                      control={<Radio size="small" />}
                      label="Otra dirección"
                    />
                  </RadioGroup>

                  {invoiceTransferData.destino_tipo === DESTINATION_TYPE.CLIENTE ? (
                    firstText(
                      invoiceTransferData.destino_direccion,
                      invoiceTransferData.destino_comuna
                    ) ? (
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                        <Typography variant="body2" fontWeight={900}>
                          {getCustomerDisplayName(customer) || customer?.rut || 'Cliente'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {invoiceTransferData.destino_direccion || 'Dirección no informada'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[invoiceTransferData.destino_comuna, invoiceTransferData.destino_ciudad]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                        {invoiceTransferData.destino_fuente && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            Dirección obtenida de: {invoiceTransferData.destino_fuente}
                          </Typography>
                        )}
                      </Paper>
                    ) : (
                      <Alert severity="warning">
                        El cliente no tiene una dirección utilizable. Selecciona <strong>Otra dirección</strong>
                        para informar el destino efectivo.
                      </Alert>
                    )
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                        gap: 1.25,
                      }}
                    >
                      <TextField
                        size="small"
                        required
                        label="Dirección de destino"
                        value={invoiceTransferData.destino_direccion || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField('destino_direccion', event.target.value)
                        }
                      />
                      <TextField
                        size="small"
                        required
                        label="Comuna"
                        value={invoiceTransferData.destino_comuna || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField('destino_comuna', event.target.value)
                        }
                      />
                      <TextField
                        size="small"
                        label="Ciudad"
                        value={invoiceTransferData.destino_ciudad || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField('destino_ciudad', event.target.value)
                        }
                      />
                    </Box>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={900}>
                    Transporte
                  </Typography>

                  <RadioGroup
                    row
                    value={invoiceTransferData.transportista_tipo || ''}
                    onChange={(event) =>
                      handleInvoiceTransporterTypeChange(event.target.value)
                    }
                    sx={{ mb: 1 }}
                  >
                    {invoiceDeliveryMode === INVOICE_DELIVERY_MODE.RETIRO_CLIENTE && (
                      <FormControlLabel
                        value={TRANSPORTER_TYPE.COMPRADOR}
                        control={<Radio size="small" />}
                        label="Comprador"
                      />
                    )}
                    {invoiceDeliveryMode === INVOICE_DELIVERY_MODE.DESPACHO && (
                      <FormControlLabel
                        value={TRANSPORTER_TYPE.EMISOR}
                        control={<Radio size="small" />}
                        label="Emisor / vendedor"
                      />
                    )}
                    <FormControlLabel
                      value={TRANSPORTER_TYPE.TERCERO}
                      control={<Radio size="small" />}
                      label="Tercero"
                    />
                  </RadioGroup>

                  {invoiceTransferData.transportista_tipo === TRANSPORTER_TYPE.TERCERO ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
                        gap: 1.25,
                      }}
                    >
                      <TextField
                        size="small"
                        required
                        label="RUT transportista"
                        value={invoiceTransferData.transportista_rut || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField(
                            'transportista_rut',
                            formatRutChile(event.target.value)
                          )
                        }
                      />
                      <TextField
                        size="small"
                        label="Razón social / nombre transportista"
                        value={invoiceTransferData.transportista_nombre || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField('transportista_nombre', event.target.value)
                        }
                      />
                    </Box>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'action.hover' }}>
                      <Typography variant="body2" fontWeight={800}>
                        {invoiceTransferData.transportista_nombre ||
                          (invoiceTransferData.transportista_tipo === TRANSPORTER_TYPE.COMPRADOR
                            ? 'Comprador'
                            : 'Emisor / vendedor')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        RUT {formatRutChile(invoiceTransferData.transportista_rut) || 'pendiente'}
                      </Typography>
                    </Paper>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                    Conductor
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
                      gap: 1.25,
                    }}
                  >
                    <TextField
                      size="small"
                      required
                      label="RUT conductor"
                      value={invoiceTransferData.chofer_rut || ''}
                      onChange={(event) =>
                        updateInvoiceTransferField(
                          'chofer_rut',
                          formatRutChile(event.target.value)
                        )
                      }
                    />
                    <TextField
                      size="small"
                      required
                      label="Nombre conductor"
                      value={invoiceTransferData.chofer_nombre || ''}
                      onChange={(event) =>
                        updateInvoiceTransferField('chofer_nombre', event.target.value)
                      }
                    />
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={900}>
                    Vehículo
                  </Typography>

                  <RadioGroup
                    row
                    value={
                      parseBoolish(invoiceTransferData.patente_conocida, true)
                        ? 'CONOCIDA'
                        : 'DESCONOCIDA'
                    }
                    onChange={(event) =>
                      updateInvoiceTransferField(
                        'patente_conocida',
                        event.target.value === 'CONOCIDA'
                      )
                    }
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel
                      value="CONOCIDA"
                      control={<Radio size="small" />}
                      label="Patente conocida"
                    />
                    <FormControlLabel
                      value="DESCONOCIDA"
                      control={<Radio size="small" />}
                      label="No conocida al momento de emisión"
                    />
                  </RadioGroup>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: parseBoolish(invoiceTransferData.patente_conocida, true)
                          ? '1fr 1fr'
                          : '1fr',
                      },
                      gap: 1.25,
                    }}
                  >
                    {parseBoolish(invoiceTransferData.patente_conocida, true) && (
                      <TextField
                        size="small"
                        required
                        label="Patente vehículo"
                        value={invoiceTransferData.patente || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField(
                            'patente',
                            String(event.target.value || '').toUpperCase()
                          )
                        }
                        placeholder="ABCD12"
                      />
                    )}
                    <TextField
                      size="small"
                      label="Patente carro / remolque"
                      value={invoiceTransferData.patente_carro || ''}
                      onChange={(event) =>
                        updateInvoiceTransferField(
                          'patente_carro',
                          String(event.target.value || '').toUpperCase()
                        )
                      }
                      helperText="Opcional"
                    />
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight={900}>
                      Salida
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        updateInvoiceTransferField('fecha_salida', getLocalDateInputValue());
                        updateInvoiceTransferField('hora_salida', getLocalTimeInputValue());
                      }}
                    >
                      Usar fecha y hora actual
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 1.25,
                    }}
                  >
                    <TextField
                      size="small"
                      required
                      type="date"
                      label="Fecha de salida"
                      value={invoiceTransferData.fecha_salida || ''}
                      onChange={(event) =>
                        updateInvoiceTransferField('fecha_salida', event.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size="small"
                      required
                      type="time"
                      label="Hora de salida"
                      value={invoiceTransferData.hora_salida || ''}
                      onChange={(event) =>
                        updateInvoiceTransferField('hora_salida', event.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight={900}>
                    ¿El traslado durará más de un día?
                  </Typography>

                  <RadioGroup
                    row
                    value={
                      parseBoolish(invoiceTransferData.traslado_prolongado, false)
                        ? 'SI'
                        : 'NO'
                    }
                    onChange={(event) =>
                      updateInvoiceTransferField(
                        'traslado_prolongado',
                        event.target.value === 'SI'
                      )
                    }
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel
                      value="NO"
                      control={<Radio size="small" />}
                      label="No"
                    />
                    <FormControlLabel
                      value="SI"
                      control={<Radio size="small" />}
                      label="Sí"
                    />
                  </RadioGroup>

                  {parseBoolish(invoiceTransferData.traslado_prolongado, false) && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
                        gap: 1.25,
                      }}
                    >
                      <TextField
                        size="small"
                        required
                        type="date"
                        label="Fecha estimada de llegada"
                        value={invoiceTransferData.fecha_llegada_estimada || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField(
                            'fecha_llegada_estimada',
                            event.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        size="small"
                        required
                        label="Motivo del traslado prolongado"
                        value={invoiceTransferData.motivo_traslado_prolongado || ''}
                        onChange={(event) =>
                          updateInvoiceTransferField(
                            'motivo_traslado_prolongado',
                            event.target.value
                          )
                        }
                        placeholder="Ej.: distancia, ruta interregional, condiciones de carga..."
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeInvoiceTransferDialog} color="inherit">
            Cerrar
          </Button>
          <Button
            variant="contained"
            onClick={confirmInvoiceTransferDialog}
            disabled={!invoiceDeliveryMode}
          >
            Guardar datos de entrega
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentModal
        open={paymentModalOpen}
        paymentStep={paymentStep}
        onNextStep={setPaymentStep}

        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        selectedPaymentMethodConfig={selectedPaymentMethodConfig}
        selectedPaymentIsCash={selectedPaymentIsCash}
        selectedPaymentRequiresVoucher={selectedPaymentRequiresVoucher}

        voucherNumber={voucherNumber}
        setVoucherNumber={setVoucherNumber}

        cashGivenState={cashGivenState}
        setCashGiven={setCashGiven}
        change={change}

        baseTotalGross={baseTotalGross}
        totalPayable={totalPayable}
        totalRulesAdjustment={totalRulesAdjustment}
        fiscalTotalPayable={totalPayable}
        cashRoundingAmount={selectedPaymentIsCash ? cashRoundingAmount : 0}
        rulesEvaluation={posRulesEvaluation}

        canContinueDebit={canContinueNonCash}
        canContinueCash={canContinueCash}
        confirmPaymentDebit={confirmPaymentDebit}
        confirmPaymentCash={confirmPaymentCash}
        confirmMixedPayment={confirmMixedPayment}

        paymentMethodsConfig={availablePaymentMethods}

        hasPreviousPayment={importedOrderPaid}
        previousPayment={importSource?.pago_origen || importSource?.pagoOrigen}
        previousPaymentLabel={importedPaymentLabel}
        previousPaymentSource={importSource}
        confirmPreviousPayment={async () => {
          await handleSubmit(null, {
            usarPagoOrigen: true,
            cerrarModalPago: true,
          });
        }}
        onClose={() => setPaymentModalOpen(false)}
      />

      <CajaModal
        open={cajaModalOpen}
        onClose={handleCloseCajaModal}
      />

      <ModalMovimientosCaja
        open={movCajaModalOpen}
        onClose={() => setMovCajaModalOpen(false)}
        onSaved={async (movimientoGuardado) => {
          setMovCajaModalOpen(false);

          try {
            printCashMovementVoucher(movimientoGuardado, cajaData);
          } catch (error) {
            console.error('No se pudo imprimir voucher de caja:', error);

            setSnackbar({
              open: true,
              severity: 'warning',
              message:
                error?.message ||
                'Movimiento registrado, pero no se pudo abrir la impresión del voucher.',
            });
          }

          await fetchCajaEstado();

          window.dispatchEvent(new Event('cajaActualizada'));
        }}
      />

      <ModalConsultorPrecios
        open={consultorPreciosOpen}
        onClose={() => setConsultorPreciosOpen(false)}
      />

      <Dialog
        open={variablePriceModalOpen}
        onClose={handleCloseVariablePriceModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Ingresar valor bruto</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" fontWeight={800} sx={{ mb: 0.5 }}>
              {variablePriceProduct?.nombre ||
                variablePriceProduct?.name ||
                variablePriceProduct?.sku ||
                'Producto'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {variablePriceProduct?.sku ? `SKU ${variablePriceProduct.sku} · ` : ''}
              Producto con precio variable. Ingrese el monto bruto final de la línea.
            </Typography>

            <TextField
              inputRef={variablePriceInputRef}
              fullWidth
              label="Valor bruto (IVA incluido)"
              value={variablePriceValue}
              onChange={handleVariablePriceValueChange}
              onKeyDown={handleVariablePriceKeyDown}
              autoComplete="off"
              inputProps={{
                inputMode: 'numeric',
                min: 1,
              }}
              helperText="Se agregará con cantidad 1. No use la cantidad para representar el monto."
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseVariablePriceModal}>Cancelar</Button>
          <Button variant="contained" onClick={confirmVariablePrice}>
            Agregar producto
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editQtyModalOpen}
        onClose={handleCloseEditQtyModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Editar cantidad</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {itemToEditQty?.item?.name ||
                itemToEditQty?.item?.nombre ||
                itemToEditQty?.name ||
                'Producto'}
            </Typography>

            <TextField
              inputRef={editQtyInputRef}
              fullWidth
              label="Nueva cantidad"
              value={editQtyValue}
              onChange={handleEditQtyValueChange}
              onKeyDown={handleEditQtyKeyDown}
              autoComplete="off"
              inputProps={{
                inputMode: 'decimal',
                min: 1,
              }}
              helperText={
                itemToEditQty?.item?.stock != null
                  ? `Stock disponible: ${itemToEditQty.item.stock}`
                  : 'Ingresa una cantidad válida'
              }
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEditQtyModal}>Cancelar</Button>

          <Button variant="contained" onClick={handleRequestEditQtyFromModal}>
            {editQtyRequiresAuthorization
              ? 'Solicitar autorización'
              : 'Guardar cantidad'}
          </Button>
        </DialogActions>
      </Dialog>

      <PinConfirmModal
        open={reprintPinOpen}
        onClose={() => setReprintPinOpen(false)}
        onVerified={handleReprintAfterPin}
        title="Autorizar reimpresión"
        message="Ingresa PIN para reimprimir la última venta."
      />

      <PinConfirmModal
        open={nuevaVentaPinOpen}
        onClose={handleCerrarNuevaVentaPin}
        onVerified={handleNuevaVentaAutorizada}
        title="Autorizar nueva venta"
        message="La venta actual será descartada. Ingresa PIN para continuar."
      />

      <PinConfirmModal
        open={editQtyPinOpen}
        onClose={handleCloseEditQtyPin}
        onVerified={handleEditQtyAfterPin}
        title="Autorizar cambio de cantidad"
        message="Ingresa PIN para modificar la cantidad del producto."
      />

      <PinConfirmModal
        open={lineActionPinOpen}
        onClose={handleCloseLineActionPin}
        onVerified={applyLineActionAfterPin}
        onConfirm={applyLineActionAfterPin}
        title={pendingLineAction?.title || 'Autorizar acción'}
        message={pendingLineAction?.message || 'Ingresa PIN para continuar.'}
      />

      <PinConfirmModal
        open={Boolean(pendingRuleAuthorization)}
        onClose={handleCloseRuleAuthorization}
        onVerified={handleRuleAuthorizationVerified}
        title="Autorizar regla POS"
        message={
          pendingRuleAuthorization
            ? `La regla "${pendingRuleAuthorization.nombre || pendingRuleAuthorization.codigo}" requiere autorización. Ajuste estimado: ${formatter.format(
                pendingRuleAuthorization.monto_estimado || 0
              )}`
            : 'Esta regla requiere autorización.'
        }
      />

      <PinConfirmModal
        open={ventaEnEsperaDeletePinOpen}
        onClose={handleCancelarEliminarVentaEnEspera}
        onVerified={handleEliminarVentaEnEsperaAutorizada}
        title="Eliminar venta en espera"
        message="Ingresa PIN para eliminar esta venta en espera."
      />

      <PinConfirmModal
        open={lineActionPinOpen}
        onClose={handleCloseLineActionPin}
        onVerified={applyLineActionAfterPin}
        onConfirm={applyLineActionAfterPin}
        title={pendingLineAction?.title || 'Autorizar acción'}
        message={pendingLineAction?.message || 'Ingresa PIN para continuar.'}
      />

      <ImportDocToPOSModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        disabled={!isCajaAbierta || !isOnline}
        onImport={importDocToPOS}
      />

      <POS_VentasPendientes
        open={ventasPendientesOpen}
        ventas={ventasEnEspera}
        onClose={() => setVentasPendientesOpen(false)}
        onRetomar={handleRetomarVentaEnEspera}
        onEliminar={handleSolicitarEliminarVentaEnEspera}
        onLimpiarTodo={handleLimpiarVentasEnEspera}
      />

      <POS_VentasContingencia
        open={ventasContingenciaOpen}
        ventas={ventasContingencia}
        loading={ventasContingenciaLoading}
        syncing={syncingOffline}
        syncingId={offlineSaleSyncingId}
        isOnline={isOnline}
        onClose={closeVentasContingencia}
        onRefresh={handleActualizarContingencia}
        onReintentar={handleSyncOfflineSale}
        onReintentarTodas={handleSyncAllOfflineSales}
        onReimprimirContingencia={handlePrintContingencyRecord}
        onImprimirDte={handlePrintSyncedOfflineDte}
        onEliminar={handleDeleteOfflineSale}
        onLimpiarSincronizadas={handleClearSyncedOfflineSales}
        onReintentarErrores={handleRetryErroredOfflineSales}
      />

      {postSaleChangeNotice && Number(postSaleChangeNotice.amount || 0) > 0 && (
        <Box
          role="status"
          aria-live="assertive"
          sx={{
            position: 'fixed',
            top: { xs: 72, sm: 84 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1950,
            width: { xs: 'calc(100% - 24px)', sm: 'auto' },
            minWidth: { sm: 430 },
            maxWidth: 620,
            pointerEvents: 'none',
          }}
        >
          <Paper
            elevation={10}
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: { xs: 1.75, sm: 2.25 },
              borderRadius: 3,
              textAlign: 'center',
              // Mismo verde semántico del botón Pagar (MUI success).
              // Al usar tokens del theme, ambos se mantienen alineados incluso
              // si la paleta de IntegraNube cambia más adelante.
              bgcolor: 'success.main',
              color: '#FFFFFF',
              border: '3px solid',
              borderColor: 'success.dark',
              boxShadow: '0 14px 34px rgba(46, 125, 50, 0.34)',
              // El contenedor exterior no bloquea el POS, pero la tarjeta sí
              // acepta interacción para que el cajero pueda cerrar el aviso.
              pointerEvents: 'auto',
            }}
          >
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                fontWeight: 900,
                letterSpacing: 1.4,
                lineHeight: 1.1,
              }}
            >
              VUELTO A ENTREGAR
            </Typography>

            <Typography
              sx={{
                mt: 0.4,
                fontSize: { xs: '2.35rem', sm: '3.15rem' },
                lineHeight: 1,
                fontWeight: 1000,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatter.format(Number(postSaleChangeNotice.amount || 0))}
            </Typography>

            {postSaleChangeNotice.folio && (
              <Typography sx={{ mt: 0.75, fontWeight: 700, opacity: 0.9 }}>
                Venta {postSaleChangeNotice.folio}
              </Typography>
            )}

            <Button
              variant="contained"
              onClick={() => setPostSaleChangeNotice(null)}
              sx={{
                mt: 1.5,
                minWidth: 150,
                bgcolor: '#FFFFFF',
                color: 'success.dark',
                fontWeight: 900,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              Cerrar
            </Button>
          </Paper>
        </Box>
      )}

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.45)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 2000,
            pointerEvents: 'none',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              px: 3,
              py: 2,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CircularProgress size={22} />
            <Typography fontWeight={800}>Procesando...</Typography>
          </Paper>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 3000 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{ pointerEvents: 'none' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{
            width: '100%',
            whiteSpace: 'pre-line',
            pointerEvents: 'none',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POS;
